import type { BarcodeFormat, Brand, Category, CmsSettings, Item, LabelMark, PendingLabel } from '@erp/shared';
import { barcodeFit } from '@erp/shared';
import JsBarcode from 'jsbarcode';
import { Check, EyeOff, FileDown, FolderTree, Minus, Plus, Printer, RefreshCw, Search, Tag, Trash2 } from 'lucide-react';
import { type ReactNode, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Rnd } from 'react-rnd';
import { toast } from 'sonner';

import { Card } from '../../components/shadcn/index.js';
import { Button, Combobox, type ComboboxOption, Input } from '../../components/ui/index.js';
import { useLayoutDirection } from '../../hooks/useDirection.js';
import { usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';
import { useAuthStore } from '../../lib/authStore.js';
import { formatNumber } from '../../lib/intl.js';
import { moduleEnabled } from '../../lib/modules.js';
import { useEnabledModules } from '../../lib/modulesStore.js';
import { formatMoney, getCurrencyDecimals } from '../../lib/money.js';
import { normalizeAmountTyping } from '../../lib/numerals.js';
import { type BulkPriceLine, listPriceLists, type PriceList, resolvePricesBulk } from '../../lib/pricing.js';
import { assetUrl } from '../../lib/publicApi.js';
import { flattenCategoryOptions } from '../items/itemHelpers.js';

import { LabelQueue, postLabelMarks, undoOf } from './LabelQueue.js';

// A clean label font stack (Latin + Arabic fallback), used in the preview, the
// print popup, and the PDF canvas so all three look the same.
const FONT = "'Segoe UI', system-ui, -apple-system, 'Helvetica Neue', Arial, 'Noto Sans Arabic', 'Cairo', sans-serif";
// Fixed print ink/paper colors — a label is always black on white regardless of the
// app theme, so brand tokens don't apply here (used by the PDF canvas).
/* eslint-disable no-restricted-syntax */
const INK = '#0b0b0b';
const PAPER = '#ffffff';
const CUT = '#c9ccd4';
// The rest of the fixed print palette: the default accent (a DS red), the muted grey
// of the SKU / extra line, and the hairline of the price-tag divider.
const ACCENT_DEFAULT = '#c1272d';
const MUTED = '#52525b';
const RULE = '#b8bcc6';
// Bars are PURE black, not the label's near-black INK: contrast is what a scanner
// reads, so the one element that has to be machine-readable gets the maximum of it.
// (It is also jsbarcode's own default, which every label printed before this file
// grew a colour option — so existing labels keep coming out identical.)
const BARCODE_INK = '#000000';
/* eslint-enable no-restricted-syntax */
const FMT: Record<Exclude<BarcodeFormat, 'qr'>, string> = { code128: 'CODE128', ean13: 'EAN13', ean8: 'EAN8', upc_a: 'UPC' };
const SVGNS = 'http://www.w3.org/2000/svg';

// ── Watermark + vertical-strip geometry. Both the CSS-var builder and the PDF
//    canvas derive from these, so the three renderers can't drift apart. ──
const WM_TEXT_FACTOR = 0.42;   // text font-size = the watermark box × this, so one knob drives both sources
const WM_PAD_MM = 1;           // corner inset of a non-centred watermark
const VBC_NUM_FACTOR = 1.35;   // the digit band is a bit wider than the digits themselves
const TAG_PAD_MM = 1;          // .lp-tag padding (all four sides — the strip can sit on either)
const VBC_GAP_MM = 0.8;        // gap between the strip and the tag's main column
const VBC_MIN_THICK_MM = 5;    // shorter bars than this and a hand scanner needs a perfect angle
// The bars keep this much of the strip no matter how big the digits get. Deliberately
// BELOW the warn threshold: a floor at 5mm would make the warning unreachable.
const VBC_MIN_BARS_MM = 3;
const VBC_QUIET_MODULES = 12;  // quiet zone in modules, at each END of the strip (the scan axis)
// Bar length expressed in MODULES: both generators use it, so the strip image has one
// aspect ratio and the stretched result is identical in the preview and the PDF.
const VBC_BAR_MODULES = 23;

// ── Spacing / geometry knobs. Every one of them takes AUTO_MM, which means "keep the
//    template's own built-in value" — that is what makes a design saved before these
//    options existed print exactly what it always printed. ──
/** "Not set — use the template's default." A negative millimetre can't be a real size. */
const AUTO_MM = -1;
// The template's own defaults, ONE copy each, resolved by geometry() below.
const PAD_X_DEFAULT = 1.5;     // .lp-label's horizontal padding (centered)
const PAD_Y_DEFAULT = 1;       // .lp-label's vertical padding (centered)
const GAP_DEFAULT = 0.6;       // .lp-label's flex gap (centered)
const TAG_GAP_DEFAULT = 0.4;   // .lp-main's flex gap (price tag)
const NAME_LINES_DEFAULT = 2;  // lines the product name may wrap to (centered)
const TAG_NAME_LINES = 1;      // …and on a price tag, where the name is a single header
const DIV_MM_DEFAULT = 0.25;   // .lp-div's rule thickness
const CELL_GAP_DEFAULT = 2;    // the gap between labels on a sheet
const TAG_BC_MM_DEFAULT = 5;   // .lp-tag .lp-bc's max-height
const TAG_MID_GAP_MM = 1.5;    // .lp-mid's gap — between the logo and the price
/**
 * .lp-name's clamp height per line, in ems. line-height is 1.08, so this is that plus
 * a little slack for descenders — the same 2.3em the centered template has always used
 * at two lines, now expressed per line so the count can be a knob.
 */
const NAME_LINE_FACTOR = 1.15;
/**
 * .lp-name's tracking, in em — ONE constant for the CSS *and* the canvas, for the same
 * reason HC_TRACKING_EM exists: the canvas now WRAPS the name, and a wrap point is
 * decided by measureText(). Measuring untracked put the break at a different word from
 * the browser's, so the preview and the PDF split the same name differently.
 */
const NAME_TRACKING_EM = -0.01;

/**
 * Options that change a barcode's pixels — every one of them is part of the cache key.
 * `xAlign` is in here because a barcode is letterboxed inside its box (it keeps its
 * aspect), and WHERE the spare width goes is preserveAspectRatio's business — a CSS
 * property can't move it. Without it a left-aligned label put the bars flush left in
 * the PDF and dead centre in the preview.
 */
interface BcOpt { displayValue?: boolean; lineColor?: string; background?: string; stretch?: boolean; quietModules?: number; xAlign?: 'left' | 'center' | 'right'; matchSvgAspect?: boolean }
const PAR_X: Record<'left' | 'center' | 'right', string> = { left: 'xMin', center: 'xMid', right: 'xMax' };
// A colour picker fires on every drag frame, so colour is now a cache dimension that
// can explode over a few hundred labels — drop the whole map rather than leak.
const CACHE_MAX = 1500;

/**
 * The bar box + quiet zone, shared by the SVG and the canvas generator so the two images
 * can't drift apart. The quiet zone lives INSIDE the image (JsBarcode's margin) so it
 * scales — and survives rotation — with the bars; but for the rotated strip it must sit
 * on the SCAN axis only, because a top/bottom margin is stretched into the bars' LENGTH
 * and eats about half of it. JsBarcode's fixOptions does `marginTop = marginTop || margin`,
 * so `margin` itself has to be 0 for the explicit left/right pair to stick.
 * `plainHeight` keeps the ordinary horizontal barcode pixel-for-pixel what it was.
 *
 * `flat` matters for the same reason: EAN/UPC otherwise extend their guard bars by
 * `fontSize/2 + textMargin` PIXELS below the others (room for digits the strip doesn't
 * print), which both makes the image taller than `height` — the stretch then shrinks the
 * ordinary bars to ~84% of the strip — and, being an absolute nudge on two images with
 * different module widths, leaves the two renderers a per-cent apart. Flat = pure bars.
 */
/**
 * `matchSvgAspect` re-expresses the plain barcode as MULTIPLES OF THE MODULE WIDTH, which
 * is the only way a raster of it can be the same image as the SVG rather than merely a
 * similar one. The two are otherwise tuned apart (svg 2/46/13, canvas 3/110/20) with an
 * absolute `margin: 10` on both — and a fixed margin on two different image widths is a
 * different quiet zone: 10/266 of the SVG against 10/389 of the bitmap. Letterboxed into
 * one box that put the PDF's bars 2.6% wider than the preview's with a correspondingly
 * thinner quiet zone, which is the half of the image a scanner actually needs.
 *
 * The multipliers ARE the SVG's own numbers over its module width of 2, so asking for
 * this yields the SVG at 1.5x and nothing else. Only the custom template does; the flow
 * templates keep the exact bitmap they have always had.
 */
const BC_SVG_UNIT = { height: 23, margin: 5, fontSize: 6.5, textMargin: 1 };
function barGeometry(o: BcOpt, width: number, plainHeight: number): { height: number; margin: number; marginLeft?: number; marginRight?: number; flat?: boolean; fontSize?: number; textMargin?: number } {
  if (o.quietModules === undefined) {
    return o.matchSvgAspect
      ? {
        height: BC_SVG_UNIT.height * width,
        margin: BC_SVG_UNIT.margin * width,
        fontSize: BC_SVG_UNIT.fontSize * width,
        textMargin: BC_SVG_UNIT.textMargin * width,
      }
      : { height: plainHeight, margin: 10 };
  }
  const q = o.quietModules * width;
  return { height: VBC_BAR_MODULES * width, margin: 0, marginLeft: q, marginRight: q, flat: true };
}

const svgCache = new Map<string, string>();
/** Scannable 1D barcode → responsive standalone <svg> string (for preview + print). */
function barcodeSvg(value: string, format: BarcodeFormat, o: BcOpt = {}): string {
  const v = value.trim();
  if (!v) return '';
  const dv = o.displayValue !== false;
  const lc = o.lineColor ?? BARCODE_INK;
  const bg = o.background ?? PAPER;
  const key = `s:${v}::${format}::${dv ? 1 : 0}::${lc}::${bg}::${o.stretch ? 1 : 0}::${o.quietModules ?? 'd'}::${o.xAlign ?? 'c'}`;
  const hit = svgCache.get(key);
  if (hit !== undefined) return hit;
  const render = (fmt: string): string => {
    const doc = document.implementation.createHTMLDocument('bc');
    const svg = doc.createElementNS(SVGNS, 'svg');
    const width = 2;
    JsBarcode(svg, v, { format: fmt, displayValue: dv, fontSize: 13, width, lineColor: lc, background: bg, ...barGeometry(o, width, 46) });
    const w = parseFloat(svg.getAttribute('width') ?? '0');
    const h = parseFloat(svg.getAttribute('height') ?? '0');
    if (w > 0 && h > 0) {
      svg.setAttribute('viewBox', `0 0 ${w} ${h}`);
      // Non-uniform scaling of a 1D barcode is legitimate (x = module width, y = bar
      // length, independent axes) and is what lets the rotated strip fill its box.
      svg.setAttribute('preserveAspectRatio', o.stretch ? 'none' : `${PAR_X[o.xAlign ?? 'center']}YMid meet`);
      svg.setAttribute('width', '100%');
      svg.setAttribute('height', '100%');
    }
    return new XMLSerializer().serializeToString(svg);
  };
  const primary = format === 'qr' ? 'CODE128' : FMT[format];
  let out = '';
  try { out = render(primary); } catch { try { out = render('CODE128'); } catch { out = ''; } }
  if (svgCache.size > CACHE_MAX) svgCache.clear();
  svgCache.set(key, out);
  return out;
}

const canvasCache = new Map<string, HTMLCanvasElement | null>();
/** Scannable 1D barcode → high-res <canvas> (for the PDF; crisp + font/Arabic-proof). */
function barcodeCanvas(value: string, format: BarcodeFormat, o: BcOpt = {}): HTMLCanvasElement | null {
  const v = value.trim();
  if (!v) return null;
  const dv = o.displayValue !== false;
  const lc = o.lineColor ?? BARCODE_INK;
  const bg = o.background ?? PAPER;
  // No `stretch` here — drawImage() imposes the aspect, so it can't change these pixels.
  const key = `c:${v}::${format}::${dv ? 1 : 0}::${lc}::${bg}::${o.quietModules ?? 'd'}::${o.matchSvgAspect ? 'm' : 'p'}`;
  if (canvasCache.has(key)) return canvasCache.get(key) ?? null;
  const draw = (fmt: string): HTMLCanvasElement => {
    const cv = document.createElement('canvas');
    const width = 3;
    JsBarcode(cv, v, { format: fmt, displayValue: dv, fontSize: 20, width, lineColor: lc, background: bg, ...barGeometry(o, width, 110) });
    return cv;
  };
  const primary = format === 'qr' ? 'CODE128' : FMT[format];
  let out: HTMLCanvasElement | null = null;
  try { out = draw(primary); } catch { try { out = draw('CODE128'); } catch { out = null; } }
  if (canvasCache.size > CACHE_MAX) canvasCache.clear();
  canvasCache.set(key, out);
  return out;
}

const aspectCache = new Map<string, number | null>();
/**
 * The shape the HTML letterboxes a barcode into, read off the SVG's own viewBox.
 *
 * The two makers are tuned independently — the SVG at module width 2 / bar height 46 /
 * margin 10, the canvas at 3 / 110 / 10 — and an ABSOLUTE margin on two different module
 * widths cannot scale, so one barcode comes out with an aspect of 3.284 as an SVG and
 * 2.559 as a bitmap. That is invisible while a renderer STRETCHES the image, and wrong
 * the moment it letterboxes: the custom template's 46mm box drew 31.5mm of bars on
 * screen and 24.6mm in the exported PDF, a 22% narrower barcode on the paper than in
 * the preview the design was approved in.
 *
 * Measuring the letterbox from the SVG instead means the bitmap is simply stretched into
 * the rect the browser would have used — which is the non-uniform scale a 1D barcode is
 * indifferent to (x = module width, y = bar length, independent axes) — and the two
 * renderers land on the same millimetres without touching either generator.
 *
 * ONLY the custom template uses this. The flow templates letterbox too, but their canvas
 * derives the barcode's height from its own bottom-up stacking rather than from
 * flexbox's, and that separate disagreement currently offsets this one; correcting the
 * aspect there alone makes them diverge MORE, so it is left to be fixed together.
 */
function barcodeAspect(value: string, format: BarcodeFormat, o: BcOpt = {}): number | null {
  const v = value.trim();
  if (!v) return null;
  // Only what changes the GEOMETRY — colours and xAlign move nothing in the viewBox.
  const key = `${v}::${format}::${o.displayValue !== false ? 1 : 0}::${o.quietModules ?? 'd'}`;
  const hit = aspectCache.get(key);
  if (hit !== undefined) return hit;
  const m = /viewBox="0 0 ([\d.]+) ([\d.]+)"/.exec(barcodeSvg(v, format, o));
  const w = parseFloat(m?.[1] ?? '0');
  const h = parseFloat(m?.[2] ?? '0');
  const ar = w > 0 && h > 0 ? w / h : null;
  if (aspectCache.size > CACHE_MAX) aspectCache.clear();
  aspectCache.set(key, ar);
  return ar;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] ?? c));
}

/**
 * Red/orange/yellow bars are invisible to the ~660nm light laser and CCD scanners
 * read, and anything light fails the print-contrast ratio — both scan as "no code".
 */
function lightOrRed(hex: string): boolean {
  const m = /^#?([\da-f]{6})$/i.exec(hex);
  if (!m) return false;
  const v = parseInt(m[1]!, 16);
  const r = (v >> 16) & 255; const g = (v >> 8) & 255; const b = v & 255;
  const lum = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  return lum > 0.35 || (r > 120 && r > g * 1.6 && r > b * 1.6);
}

type Template = 'centered' | 'tag' | 'custom';
const FONTS: Record<string, string> = {
  default: FONT,
  sans: "'Helvetica Neue', Arial, 'Noto Sans Arabic', 'Cairo', sans-serif",
  serif: "'Georgia', 'Times New Roman', 'Noto Naskh Arabic', serif",
  mono: "ui-monospace, 'Cascadia Code', 'Courier New', monospace",
  rounded: "'Segoe UI Rounded', 'Nunito', 'Cairo', system-ui, sans-serif",
};
// `hidden` is a FINISHED string, exactly like `price` already is: the async
// price-list data is resolved and formatted once in the `specs` memo, so all
// three renderers stay pure and synchronous and just print what they're given.
interface LabelSpec { name: string; code: string; price: string; hidden: string; barcode: string; format: BarcodeFormat }
type WmSource = 'logo' | 'text';
type WmPos = 'center' | 'tl' | 'tr' | 'bl' | 'br';
type VbcMode = 'barcode' | 'number';
// PHYSICAL, not logical: the PDF canvas has no writing direction, so a logical
// start/end could never agree with the HTML in an RTL admin. The UI translates.
type VbcSide = 'left' | 'right';
/**
 * Where the label's text sits. PHYSICAL for the same reason as VbcSide — and these
 * three values are also exactly CanvasTextAlign's, so the PDF can use them verbatim.
 */
type Align = 'left' | 'center' | 'right';
/**
 * The barcode symbology every label is drawn in. 'item' = whatever each product is
 * set to (the default, and what this page always did); anything else FORCES that
 * symbology for this print run without touching the catalogue. A value the forced
 * symbology can't carry still prints — barcodeSvg/barcodeCanvas fall back to Code 128
 * so a sticker is never blank — which is exactly why the panel counts and names them.
 */
type BcFormatOpt = BarcodeFormat | 'item';
const BC_FORMATS: readonly BarcodeFormat[] = ['code128', 'ean13', 'ean8', 'upc_a'];
/** text-align → the flex properties that move a shrink-to-fit child the same way. */
const FLEX_ALIGN: Record<Align, string> = { left: 'flex-start', center: 'center', right: 'flex-end' };
/**
 * Per-element alignment. 'auto' defers to the label's own `align`, and is the default
 * everywhere — so the single Text-alignment control still moves everything until an
 * element is deliberately given a mind of its own.
 */
type ElAlign = Align | 'auto';
const EL_ALIGNS: readonly ElAlign[] = ['auto', 'left', 'center', 'right'];

// ── The CUSTOM template: every element is a box you place yourself. ──
/** The elements a custom label can place. The vertical strip stays price-tag-only. */
type ElId = 'logo' | 'name' | 'barcode' | 'price' | 'code' | 'extra' | 'hidden';
/** Paint order — later sits on top, and it is the DOM order the canvas mirrors. */
const EL_IDS: readonly ElId[] = ['logo', 'name', 'barcode', 'code', 'price', 'extra', 'hidden'];
/** A box in millimetres, measured from the label's inner edge (just inside the border). */
interface ElBox { x: number; y: number; w: number; h: number }
type CustomLayout = Record<ElId, ElBox>;
const EL_MIN_MM = 2;          // a box smaller than this can't be grabbed again
const DESIGN_SNAP_MM = 0.5;   // drag/nudge step; Shift drops it to DESIGN_FINE_MM
const DESIGN_FINE_MM = 0.1;
/**
 * How near an edge has to come before it snaps to a guide — in SCREEN pixels, converted
 * to millimetres against the live zoom, so the pull feels identical at 1× and at 14×.
 * A fixed millimetre tolerance would be unusable at both ends: invisible when zoomed out,
 * and a magnet you cannot escape when zoomed in.
 */
const GUIDE_SNAP_PX = 7;
/** A line the designer is currently aligned to, in millimetres down/across the label. */
interface Guide { axis: 'x' | 'y'; at: number }

/**
 * Pull a box onto the things somebody laying out a label actually aims at: the label's
 * own two edges and its centre line, and every other box's edges and centre — on both
 * axes, for the moving box's leading edge, centre and trailing edge alike.
 *
 * This is what makes placement feel deliberate rather than approximate. A 0.5mm grid
 * alone cannot do it: "line this up with the barcode above" is a different question from
 * "put this on a round number", and the answer is only a grid position by coincidence.
 * The nearest candidate within tolerance wins per axis, and the axes are independent, so
 * a box can be centred horizontally while still free vertically.
 */
function alignBox(b: ElBox, others: ElBox[], W: number, H: number, tolMm: number): { box: ElBox; guides: Guide[] } {
  const xs = [0, W / 2, W, ...others.flatMap((o) => [o.x, o.x + o.w / 2, o.x + o.w])];
  const ys = [0, H / 2, H, ...others.flatMap((o) => [o.y, o.y + o.h / 2, o.y + o.h])];
  const best = (anchors: number[], targets: number[]): { delta: number; at: number } | null => {
    let hit: { delta: number; at: number } | null = null;
    for (const a of anchors) {
      for (const t of targets) {
        const delta = t - a;
        if (Math.abs(delta) <= tolMm && (hit === null || Math.abs(delta) < Math.abs(hit.delta))) hit = { delta, at: t };
      }
    }
    return hit;
  };
  const gx = best([b.x, b.x + b.w / 2, b.x + b.w], xs);
  const gy = best([b.y, b.y + b.h / 2, b.y + b.h], ys);
  const guides: Guide[] = [];
  if (gx) guides.push({ axis: 'x', at: gx.at });
  if (gy) guides.push({ axis: 'y', at: gy.at });
  return { box: { ...b, x: b.x + (gx?.delta ?? 0), y: b.y + (gy?.delta ?? 0) }, guides };
}

/**
 * A sensible starting layout for ANY label size — name, barcode, SKU, price stacked
 * down the label, with the logo and the two small lines tucked into corners. Expressed
 * as fractions of the label so a 100×50 opens looking as deliberate as a 30×20, then
 * frozen into millimetres, because a designer thinks "8mm from the top", not "27%".
 */
export function defaultLayout(W: number, H: number): CustomLayout {
  const p = Math.min(1.5, W * 0.04);
  const iw = W - 2 * p;
  const r = (v: number): number => Math.round(v * 10) / 10;
  const box = (x: number, y: number, w: number, h: number): ElBox => ({ x: r(x), y: r(y), w: r(w), h: r(h) });
  return {
    // The name gets 22% rather than a tighter 18% for a reason: at the default 2.7mm
    // font two lines need 5.83mm, and a box that holds only one would silently drop
    // half of every long product name the moment the template is chosen.
    name: box(p, H * 0.03, iw, H * 0.22),
    barcode: box(p, H * 0.27, iw, H * 0.32),
    code: box(p, H * 0.6, iw, H * 0.1),
    price: box(p, H * 0.71, iw, H * 0.18),
    // These three start out of the way of the stack above; the logo overlaps the name
    // deliberately — it is off until a store logo exists, and moving it is the point.
    logo: box(p, H * 0.03, iw * 0.22, H * 0.16),
    extra: box(p, H * 0.9, iw * 0.6, H * 0.09),
    hidden: box(p + iw * 0.62, H * 0.9, iw * 0.38, H * 0.09),
  };
}

/**
 * Keep a box inside the label. Applied at RENDER time, in all three renderers, rather
 * than only when dragging: shrinking the label afterwards must not push an element off
 * the paper (or, in the canvas, start drawing left of its own right edge).
 */
function clampBox(b: ElBox, W: number, H: number): ElBox {
  const w = Math.max(EL_MIN_MM, Math.min(b.w, W));
  const h = Math.max(EL_MIN_MM, Math.min(b.h, H));
  return { w, h, x: Math.max(0, Math.min(b.x, W - w)), y: Math.max(0, Math.min(b.y, H - h)) };
}

// ── Hidden staff price code: ONE short string carrying several price-list prices
//    at once ("xx1000xx2" = 1000 on the Offices list, 2 on Stationery). The
//    customer sees noise; staff read every tier off the sticker. Off by default:
//    a pre-hidden-code preset has no hcOn key ⇒ nothing about it changes. ──
type HcPos = 'top' | 'bottom' | 'corner';
// PHYSICAL like WmPos, and for the same reason (the canvas has no direction).
type HcCorner = 'tl' | 'tr' | 'bl' | 'br';
const HC_TPL_MAX = 120;          // input cap; width-fitting handles what still doesn't fit
const HC_MISSING_DEFAULT = '-';  // "this list has no price for this item"
const HC_UNKNOWN = '?';          // "the template names a list that no longer exists"
const HC_INSET_MM = 0.8;         // corner inset — ONE constant for the CSS *and* the canvas
/**
 * .lp-hidden's tracking, in em — ONE constant for the CSS *and* the canvas, because
 * the canvas has to MEASURE what the HTML lays out. Without it the canvas measured the
 * code ~6% narrow, so `fitText`'s ellipsis and CSS `text-overflow` cut the same design
 * at different characters and the PDF carried digits the print pop-up had dropped.
 */
const HC_TRACKING_EM = 0.06;
/**
 * Resolved (list,item,unit) lines held in the ref cache. Sized so a legitimate working
 * set always fits without eviction: one full "Add all" (BULK_MAX = 4000 products) priced
 * on the ~8 lists a 120-character template can name. Over the cap the OLDEST lines go,
 * one at a time — clearing the whole map made every later row change refetch every list.
 */
const HC_CACHE_MAX = 32000;
const HC_BULK_CHUNK = 500;       // BulkPriceRequestSchema caps `lines` at 500
/**
 * The whole grammar, in one regex, one pass. Anything else — a stray `{`, a typo
 * like `{lst:x}`, the owner's own markers `xx` / `/` / Arabic letters — passes
 * through verbatim, so what they typed is exactly what prints. (No escape syntax
 * for a literal brace: it survives because only a COMPLETE recognised token is
 * ever consumed.)
 */
const HC_TOKEN_RE = /\{(list|cost|price|sku|barcode)(?::([^}]*))?\}/g;

/**
 * A price as it goes INTO the hidden code: bare digits, no grouping, no currency
 * code, the currency's OWN decimals. "1000" not "1,000.00"; "2" not "$2.00".
 * `base` is the tenant base currency — what a resolved line with no currency of
 * its own is denominated in, so its decimals come from there rather than a flat 2
 * that would print IQD as "8000.00".
 */
function hcNumber(amount: string | null | undefined, ccy: string | null | undefined, base: string): string | null {
  if (amount == null || amount === '') return null;
  const v = Number(amount);
  if (!Number.isFinite(v)) return null;
  const dp = getCurrencyDecimals(ccy ?? base) ?? 2;
  // Locale pinned to en-US, NOT i18n.language: an Arabic locale resolves to the
  // `arab` numbering system, so the same saved design would print xx٨٠٠٠xx٢ for one
  // admin and xx8000xx2 for another — and the cipher below only maps ASCII digits.
  // No grouping either: a comma is itself a plausible separator, and reading the
  // code back off the sticker is the whole point. minimumFractionDigits:0 is what
  // turns 2.0000 into "2" instead of "2.00".
  return new Intl.NumberFormat('en-US', { useGrouping: false, minimumFractionDigits: 0, maximumFractionDigits: dp }).format(v);
}

/**
 * Optional letter substitution so a customer can't read the code. Ten characters,
 * one per digit: key[0] replaces '0' … key[9] replaces '9' — the classic shop trick
 * ("MONEYTALKS" ⇒ 1000 prints as "OMMM"). Deterministic and reversible BY EYE,
 * which is the point: this deters a shopper, it is not cryptography.
 *
 * ONE pass over the code points, never a loop of replaceAll(): with a digit-
 * permutation key (a legitimate choice) mapping 1→5 and then 5→3 would silently
 * turn every original 1 into a 3.
 */
function hcCipher(s: string, key: string[] | null): string {
  if (!key) return s;
  return Array.from(s).map((ch) => {
    // Arabic-Indic and extended Arabic-Indic digits normalise first, so a SKU typed
    // as ٢٠٠ can't come out half-ciphered.
    const c = ch.codePointAt(0)!;
    const d = c >= 0x30 && c <= 0x39 ? c - 0x30
      : c >= 0x0660 && c <= 0x0669 ? c - 0x0660
        : c >= 0x06f0 && c <= 0x06f9 ? c - 0x06f0 : -1;
    return d < 0 ? ch : key[d]!;   // letters, '.', '-' and the owner's marks pass through
  }).join('');
}

/** Everything one printed label (one barcode source) contributes to its code. */
interface HcCtx {
  itemId: string; unitId: string | null;
  cost: string | null; costCcy: string | null;
  price: string | null; priceCcy: string | null;
  sku: string; barcode: string;
}
/** The shared half: the tenant's lists, the fetched prices and the markers. */
interface HcEnv {
  listIdFor: (ref: string) => string | null;
  prices: Map<string, BulkPriceLine>;
  missing: string; baseFallback: boolean; key: string[] | null; baseCurrency: string;
  /**
   * Lists whose basis carries a percent adjustment. The server applies that percent to
   * a BASE-unit line but not to a unit line, so the raw-price fallback below is only
   * honest for an unadjusted list — see the comment at its call site.
   */
  adjusted: Set<string>;
  /**
   * The cipher was ASKED for and can't be applied (the key isn't 10 usable characters).
   * Every token then prints the missing marker instead of its value: printing the cost
   * in plain digits is the exact thing the owner turned scrambling on to prevent, and a
   * trailing space in the key is invisible in the input — so this fails closed.
   */
  suppress: boolean;
}
/** (list, item, unit) — the unit dimension is not optional: a carton label must
 *  show the CARTON's list price, and the server prices per itemUnitId. */
const hcPriceKey = (listId: string, itemId: string, unitId: string | null): string => `${listId}|${itemId}|${unitId ?? ''}`;
/** Every {list:REF} a template names, in order (the caller de-dupes/resolves). */
function hcListRefs(tpl: string): string[] {
  const out: string[] = [];
  for (const m of tpl.matchAll(HC_TOKEN_RE)) {
    const ref = (m[2] ?? '').trim();
    if (m[1] === 'list' && ref) out.push(ref);
  }
  return out;
}
/** Template + one label + the resolved data → the string that gets printed. */
function buildHiddenCode(tpl: string, ctx: HcCtx, env: HcEnv): string {
  if (!tpl.trim()) return '';
  return tpl.replace(HC_TOKEN_RE, (whole: string, kind: string, ref: string | undefined): string => {
    let value: string | null = null;
    if (kind === 'list') {
      const id = env.listIdFor((ref ?? '').trim());
      // A template ERROR, not a data gap — it must not look like a missing price.
      if (!id) return HC_UNKNOWN;
      const line = env.prices.get(hcPriceKey(id, ctx.itemId, ctx.unitId));
      // source 'base' = the list had no answer and the server handed back the item's
      // own retail price (which is what a DEACTIVATED list returns for the whole
      // catalogue). Printing that under the Offices marker would tell staff the office
      // price is the shelf price — so it counts as missing unless explicitly asked for.
      // 'list-item' and 'list-percent' are both the list's own answer; a price of
      // exactly 0 is a deliberate entry, so only null/non-finite counts as absent.
      if (line && line.source !== 'none' && (line.source !== 'base' || env.baseFallback)) {
        value = hcNumber(line.unitPrice, line.currencyCode, env.baseCurrency);
      } else if (env.baseFallback && line?.source === 'none' && ctx.unitId && !env.adjusted.has(id)) {
        // The SERVER's own fallback (source 'base') is gated on `!unitId` in
        // resolveBulk — both the base and the percent branch — so a pack/carton line
        // comes back 'none' where the very same product's primary barcode comes back
        // 'base'. Without this the toggle silently did nothing on unit labels: the
        // carton sticker printed the marker while the base sticker printed a price.
        // ctx.price IS this unit's own price (sourcesOf: `u.salePrice ?? item.salePrice`).
        // Skipped for a list with a PERCENT adjustment: the server applies the percent to
        // the base-unit line, so falling back to the unit's RAW price would print an
        // unadjusted number under the same marker that shows an adjusted one on the very
        // same product's base sticker — staff would quote cartons at the wrong price.
        // Applying the percent here instead is not an option: this app never computes a
        // price client-side.
        value = hcNumber(ctx.price, ctx.priceCcy, env.baseCurrency);
      }
    } else if (kind === 'cost') value = hcNumber(ctx.cost, ctx.costCcy, env.baseCurrency);
    else if (kind === 'price') value = hcNumber(ctx.price, ctx.priceCcy, env.baseCurrency);
    else if (kind === 'sku') value = ctx.sku || null;
    else if (kind === 'barcode') value = ctx.barcode || null;
    else return whole;
    // Only a token's VALUE is scrambled: the owner's own letters and marks print as
    // typed — which is also what makes a plain-text prefix work without its own field.
    // `suppress` drops the value entirely (see HcEnv): a code the cipher couldn't
    // reach must read as "nothing here", never as the number itself.
    return value == null || env.suppress ? env.missing : hcCipher(value, env.key);
  });
}

/** Font / colour / direction / tracking for the hidden code — identical in both templates. */
function hcFont(ctx: CanvasRenderingContext2D, o: SheetOpts, S: number): void {
  const fs = Math.round(S * o.hcMm);
  ctx.font = `${fs}px ui-monospace, "Cascadia Code", "Courier New", monospace`;
  ctx.fillStyle = o.hcColor;
  // = .lp-hidden's `direction: ltr`, and deliberately NOT baseDirection(): that
  // helper exists to match the `unicode-bidi: plaintext` rows (watermark, name,
  // extra, price), while the hidden code wants FORCED ltr in all three renderers
  // so an Arabic marker can't reorder it in one of them. (A detached canvas
  // resolves the default 'inherit' to 'ltr' anyway, so nothing drawn afterwards
  // changes.)
  ctx.direction = 'ltr';
  // = .lp-hidden's letter-spacing, from the SAME constant the CSS uses. This is a
  // measurement, not decoration: fitText() below trims on measureText(), so an
  // untracked canvas fitted characters the HTML had already ellipsised away and the
  // PDF ended up carrying different digits from the preview and the print pop-up.
  // Guarded because the property only exists in canvas2d from Chrome 99 / Firefox 118
  // / Safari 17.4 — where it is missing, both stay on the old (narrow) measurement.
  if ('letterSpacing' in ctx) ctx.letterSpacing = `${(fs * HC_TRACKING_EM).toFixed(2)}px`;
}

/** CSS `text-overflow: ellipsis` for the canvas — the same hand-trim the name blocks do. */
function fitText(ctx: CanvasRenderingContext2D, s: string, maxW: number): string {
  if (maxW <= 0) return '';
  if (ctx.measureText(s).width <= maxW) return s;
  const cs = Array.from(s);
  while (cs.length > 1 && ctx.measureText(`${cs.join('')}…`).width > maxW) cs.pop();
  return `${cs.join('')}…`;
}
/**
 * CSS `letter-spacing` for the canvas. Guarded because the property only reached
 * canvas2d in Chrome 99 / Firefox 118 / Safari 17.4; where it is missing, text simply
 * measures and draws untracked, which is what this file did everywhere before.
 * ALWAYS put it back to 0 afterwards — canvas text state is sticky, so a stray
 * tracking leaks into every element drawn after it, in the PDF only.
 */
function setTracking(ctx: CanvasRenderingContext2D, px: number): void {
  if ('letterSpacing' in ctx) ctx.letterSpacing = `${px.toFixed(2)}px`;
}
/**
 * CSS `-webkit-line-clamp` for the canvas: greedy word wrap to at most `maxLines`,
 * with everything that did not fit folded into the last line so fitText() can ellipsise
 * it — which is exactly what the browser does. Before this the canvas drew ONE line
 * always, so a two-line name looked right in the preview and came out truncated in the
 * PDF; the whole point of the option is that all three renderers break it in the same
 * place, which is also why the caller sets the name's tracking before calling.
 */
function wrapLines(ctx: CanvasRenderingContext2D, text: string, maxW: number, maxLines: number): string[] {
  if (maxW <= 0 || maxLines < 1) return [];
  const words = text.split(/\s+/).filter(Boolean);
  if (words.length === 0) return [];
  const lines: string[] = [];
  let cur = '';
  let i = 0;
  while (i < words.length) {
    const next = cur ? `${cur} ${words[i]}` : words[i]!;
    // `!cur` takes the word unconditionally: a single word wider than the label has to
    // go somewhere, and an empty line would spin this loop forever.
    if (!cur || ctx.measureText(next).width <= maxW) { cur = next; i += 1; continue; }
    if (lines.length + 1 >= maxLines) break;  // `cur` is the last line — the rest folds into it
    lines.push(fitText(ctx, cur, maxW));
    cur = '';
  }
  const rest = words.slice(i).join(' ');
  lines.push(fitText(ctx, rest ? `${cur} ${rest}` : cur, maxW));
  return lines;
}
/**
 * The ONE way the hidden code reaches the canvas: .lp-hidden's font, colour, forced
 * ltr and tracking, then its ellipsis measured through that same tracking — and the
 * tracking put back afterwards, because canvas text styles are sticky context state
 * and every other element on the label draws untracked. The caller sets textAlign /
 * textBaseline (the anchor differs per position) and passes the resulting x/y.
 */
function drawHidden(ctx: CanvasRenderingContext2D, o: SheetOpts, S: number, text: string, x: number, y: number, maxW: number): void {
  hcFont(ctx, o, S);
  ctx.fillText(fitText(ctx, text, maxW), x, y);
  if ('letterSpacing' in ctx) ctx.letterSpacing = '0px';
}
interface SheetOpts {
  widthMm: number; heightMm: number; mode: 'sheet' | 'roll' | 'custom'; template: Template;
  showName: boolean; showBarcode: boolean; showCode: boolean; showPrice: boolean;
  /**
   * Stand the CUSTOM template's barcode on its end.
   *
   * Only the custom template: the flow templates lay their barcode out in a row of
   * stacked blocks, where a rotated one has no sensible height to occupy. Here the box
   * is drawn by the owner, so "fill it the tall way" is a well-posed question — and the
   * answer for a narrow label (a shelf edge, a pen, a cable) is usually yes.
   */
  bcVertical: boolean;
  border: boolean; divider: boolean; showVertical: boolean; logoUrl: string | null;
  nameMm: number; priceMm: number; codeMm: number; vbcMm: number; extraMm: number;
  accent: string; priceAccent: boolean; nameColor: string; priceColor: string; codeColor: string;
  font: string; logoMm: number; upper: boolean; extraText: string; pricePrefix: string; priceSuffix: string;
  // Watermark — a faded layer behind everything else on the label.
  wmOn: boolean; wmSource: WmSource; wmText: string; wmOpacity: number; wmScale: number;
  wmRotate: number; wmColor: string; wmPos: WmPos; wmBehindBarcode: boolean;
  // Separate from logoUrl on purpose: the watermark must still work when the INLINE
  // logo toggle is off, and logoUrl is gated by it.
  wmLogoUrl: string | null;
  // The price-tag side strip.
  vbcMode: VbcMode; vbcSide: VbcSide; vbcWidthMm: number; vbcDigits: boolean; vbcRotate: number; vbcColor: string;
  // The hidden code's PLACEMENT and styling only. hcOn / the template / the cipher
  // are deliberately absent: they shape the STRING, and every renderer gates on
  // `s.hidden !== ''` alone — one code path, so "off", "empty template" and
  // "nothing resolved" can't print an empty element and there is no second flag
  // to keep in sync with the data.
  hcPos: HcPos; hcCorner: HcCorner; hcMm: number; hcColor: string;
  // ── Layout / spacing. Every millimetre here accepts AUTO_MM ("keep the template's
  //    own value"), which is what every pre-existing saved design resolves to. ──
  /** Where the name / price / code / extra line sit — the default for all of them. */
  align: Align;
  /** …and each one's own override, 'auto' meaning "whatever `align` says". */
  nameAlign: ElAlign; priceAlign: ElAlign; codeAlign: ElAlign; extraAlign: ElAlign;
  /** Where every element sits on a CUSTOM label. Ignored by the other two templates. */
  custom: CustomLayout;
  /** Lines the product name may wrap to; 0 = the template's own (2 centered, 1 tag). */
  nameLines: number;
  /** The scannable barcode's height, AUTO_MM = fill whatever the layout leaves. */
  bcMm: number;
  /** …and its width, as a percentage of the content width. */
  bcWidthPct: number;
  /** How much of the label's height the side strip's bars run down, as a percentage. */
  vbcLenPct: number;
  /** The gap between every element on the label. */
  gapMm: number;
  /** The label's own inner padding — the two axes are separate knobs. */
  padXMm: number; padYMm: number;
  /** The gap BETWEEN labels on a sheet (print + PDF tiling both read these). */
  cellGapXMm: number; cellGapYMm: number;
  /** The price tag's rule: how thick, and whether it runs the label's full width. */
  dividerMm: number; dividerFull: boolean;
}

// ── The ONE place the watermark box and the strip's bands are computed: the CSS-var
//    builder turns them into mm, labelCanvas multiplies them by S. Never both. ──
function wmMetrics(o: SheetOpts): { sizeMm: number; fsMm: number } {
  const base = Math.min(o.widthMm, o.heightMm);
  const sizeMm = Math.max(1, (base * o.wmScale) / 100);
  return { sizeMm, fsMm: sizeMm * WM_TEXT_FACTOR };
}
/**
 * Every measurement whose default depends on the TEMPLATE, resolved in one place so the
 * CSS-var builder and the PDF canvas can never disagree about what "Auto" meant. The
 * padding is also clamped here: a padding bigger than half the label would otherwise
 * invert the content box, and the canvas would start drawing to the left of its own
 * right edge while CSS silently floored it at zero.
 */
/**
 * An element's effective alignment. ONE resolver for the CSS vars and the canvas, so
 * 'auto' can't mean the global value in one renderer and 'center' in the other.
 */
function alignOf(o: SheetOpts, el: ElAlign): Align {
  return el === 'auto' ? o.align : el;
}

/**
 * How many lines of the name actually FIT a custom box, capped by the owner's line
 * count. CSS clamps by whole line BOXES while a canvas clamps by ink, so a box one and
 * a half lines tall came out as a full second line in the PDF and a second line sliced
 * through the middle in the preview. Deciding the count here — and handing it to the
 * HTML as --name-lines and to the canvas as wrapLines' limit — is what makes the two
 * cut at the same place.
 */
function fitLines(o: SheetOpts, boxHmm: number, want: number): number {
  const lh = o.nameMm * 1.08;                    // = .lp-name's line-height
  return Math.max(1, Math.min(want, Math.floor((boxHmm + 0.01) / lh)));
}

function geometry(o: SheetOpts): { padX: number; padY: number; gap: number; nameLines: number } {
  const tag = o.template === 'tag';
  const clamp = (v: number, hi: number): number => Math.max(0, Math.min(v, Math.max(0, hi)));
  return {
    padX: clamp(o.padXMm >= 0 ? o.padXMm : tag ? TAG_PAD_MM : PAD_X_DEFAULT, (o.widthMm - 4) / 2),
    padY: clamp(o.padYMm >= 0 ? o.padYMm : tag ? TAG_PAD_MM : PAD_Y_DEFAULT, (o.heightMm - 4) / 2),
    gap: o.gapMm >= 0 ? o.gapMm : tag ? TAG_GAP_DEFAULT : GAP_DEFAULT,
    nameLines: o.nameLines > 0 ? o.nameLines : tag ? TAG_NAME_LINES : NAME_LINES_DEFAULT,
  };
}

function vbcMetrics(o: SheetOpts): { numMm: number; thickMm: number; lenMm: number; offMm: number; availMm: number } {
  // The split is TOTAL-PRESERVING (numMm + thickMm === vbcWidthMm): the strip's two
  // children are fixed-basis flex items with nothing to shrink, and the PDF derives its
  // offsets from the same pair — so a digit band that outgrew the strip used to push the
  // bars straight over the price column in both renderers. The digits yield, not the bars.
  const want = o.vbcDigits ? o.vbcMm * VBC_NUM_FACTOR : 0;
  const numMm = Math.min(want, Math.max(0, o.vbcWidthMm - Math.min(o.vbcWidthMm, VBC_MIN_BARS_MM)));
  // The label's CONTENT height. Computed here rather than as a calc() because
  // border-box + the 0.2mm border makes the CSS version 0.4mm too long — and
  // overflow:hidden would eat that surplus out of the quiet zone.
  const availMm = Math.max(5, o.heightMm - 2 * geometry(o).padY - (o.border ? 0.4 : 0));
  // How far the code RUNS down the label (the scan axis). Shortening it is a real
  // choice on a tall tag — but it is the SCAN axis, so the length that is left is
  // still measured in whole millimetres of ink and the warning below still applies.
  const lenMm = Math.max(5, (availMm * o.vbcLenPct) / 100);
  return {
    numMm,
    // How long each BAR is — what the scanner's aiming line has to cross. Every
    // millimetre of it is ink: the strip's quiet zone is on the scan axis only
    // (barGeometry), so the stretched image is bars edge to edge.
    thickMm: o.vbcWidthMm - numMm,
    lenMm,
    // A shortened strip is CENTRED on the label, in both renderers: the HTML offsets
    // the (absolutely positioned, rotated) svg by this, the canvas its y origin.
    offMm: Math.max(0, (availMm - lenMm) / 2),
    // The strip's BOX, which is full height however short the bars are — .lp-vbc and
    // .lp-vbc-num are align-items: stretch. The canvas needs it to clip and to centre
    // the digit band on: clipping the digits to the BARS' length instead sliced the
    // first and last character off in the PDF the moment the bars were shortened.
    availMm,
  };
}

/**
 * The watermark layer, emitted first inside .lp-label and taken out of flow by CSS.
 * A real <img> / text node, never a background-image: "print backgrounds" is off by
 * default in every browser, so a background watermark previews fine and prints blank.
 */
function watermarkHtml(o: SheetOpts): string {
  if (!o.wmOn) return '';
  const pos = o.wmPos === 'center' ? '' : ` lp-wm-${o.wmPos}`;
  if (o.wmSource === 'logo') {
    if (!o.wmLogoUrl) return '';
    return `<div class="lp-wm${pos}" aria-hidden="true"><img class="lp-wm-img" src="${escapeHtml(o.wmLogoUrl)}" alt=""></div>`;
  }
  const tx = o.wmText.trim();
  if (!tx) return '';
  return `<div class="lp-wm${pos}" aria-hidden="true"><span class="lp-wm-tx">${escapeHtml(tx)}</span></div>`;
}

/**
 * Exported for the no-backend harness that drives the preview, the print pop-up and
 * the PDF canvas side by side. Three renderers of one document only stay in step if
 * they can be measured against each other without a server or a product list.
 */
export function labelHtml(s: LabelSpec, o: SheetOpts): string {
  const logo = o.logoUrl ? `<img class="lp-logo" src="${escapeHtml(o.logoUrl)}" alt="">` : '';
  // JsBarcode paints an opaque background rect by default; '' makes it transparent so
  // the watermark can show through — only when the user explicitly accepts the risk.
  const bcBg = o.wmOn && o.wmBehindBarcode ? '' : PAPER;
  const bcSvg = o.showBarcode ? barcodeSvg(s.barcode || s.code, s.format, { background: bcBg, xAlign: o.align }) : '';
  // An explicit height takes the barcode out of "fill what's left" and pins it, in
  // BOTH templates — the class is what flips `flex` as well as the height, because a
  // flex-grow item ignores a height it is told to grow past.
  const bcCls = `lp-bc${o.bcMm >= 0 ? ' lp-bc-fix' : ''}`;
  const vnum = (s.barcode || s.code || '').trim();
  const wm = watermarkHtml(o);
  const nameEl = o.showName && s.name ? `<div class="lp-name${o.upper ? ' lp-upper' : ''}">${escapeHtml(s.name)}</div>` : '';
  const priceTxt = s.price ? `${o.pricePrefix}${s.price}${o.priceSuffix}` : '';
  const priceEl = o.showPrice && priceTxt ? `<div class="lp-price${o.priceAccent ? ' lp-accent' : ''}">${escapeHtml(priceTxt)}</div>` : '';
  const codeEl = o.showCode && s.code ? `<div class="lp-code">${escapeHtml(s.code)}</div>` : '';
  const extraEl = o.extraText.trim() ? `<div class="lp-extra">${escapeHtml(o.extraText.trim())}</div>` : '';
  // Built once, placed three ways. An empty string means "off / nothing resolved",
  // and then no element is emitted at all — see SheetOpts.
  const hcEl = s.hidden
    ? `<div class="lp-hidden${o.hcPos === 'corner' ? ` lp-hidden-corner lp-hidden-${o.hcCorner}` : ''}">${escapeHtml(s.hidden)}</div>`
    : '';

  // ── Custom: every element is a box the owner placed. No flow layout at all, which
  //    is also why this is the EASIEST template for the PDF to match — a rectangle is
  //    a rectangle in both renderers, with none of flexbox's behaviour to emulate. ──
  if (o.template === 'custom') {
    const box = (id: ElId, inner: string): string => {
      const b = clampBox(o.custom[id], o.widthMm, o.heightMm);
      // Re-declaring --name-lines on the name's own box overrides the sheet-wide one
      // for that subtree, so .lp-name's clamp AND its max-height both come down to
      // what this box can hold — no half-line, and the same count the canvas uses.
      const lines = id === 'name' ? `;--name-lines:${fitLines(o, b.h, geometry(o).nameLines)}` : '';
      // A standing barcode is laid out against the box's axes SWAPPED, because it is
      // rotated a quarter turn afterwards — its length runs down the box's height.
      const bcv = id === 'barcode' && o.bcVertical ? `;--bcv-len:${b.h}mm;--bcv-thick:${b.w}mm` : '';
      return `<div class="lp-abs" style="left:${b.x}mm;top:${b.y}mm;width:${b.w}mm;height:${b.h}mm${lines}${bcv}">${inner}</div>`;
    };
    // The hidden code gets a box like everything else here, so its top/bottom/corner
    // setting has nothing to say — emitted plain, never with the corner classes.
    const hcPlain = s.hidden ? `<div class="lp-hidden">${escapeHtml(s.hidden)}</div>` : '';
    // Standing the barcode up is a rotation of the IMAGE inside its box, not a
    // different barcode: the box the owner drew keeps its own footprint on the label,
    // and the bars simply run the other way inside it. The swap of width/height before
    // the rotation is what makes it fill that box rather than sit in a square.
    const bcInner = bcSvg
      ? (o.bcVertical ? `<div class="lp-bc lp-bc-vert">${bcSvg}</div>` : `<div class="lp-bc">${bcSvg}</div>`)
      : '';
    const byId: Record<ElId, string> = {
      logo, name: nameEl, barcode: bcInner,
      code: codeEl, price: priceEl, extra: extraEl, hidden: hcPlain,
    };
    const parts = EL_IDS.filter((id) => byId[id]).map((id) => box(id, byId[id]));
    return `<div class="lp-label lp-custom${o.border ? ' lp-bordered' : ''}">${wm}${parts.join('')}</div>`;
  }

  // ── Price-tag layout (logo-left · big price · name on top · SKU at the bottom ·
  //    a real scannable barcode standing up the side). ──
  if (o.template === 'tag') {
    const main: string[] = [];
    if (hcEl && o.hcPos === 'top') main.push(hcEl);
    if (nameEl) main.push(nameEl);
    if (o.divider) main.push(`<div class="lp-div${o.dividerFull ? ' lp-div-full' : ''}"></div>`);
    const mid: string[] = [];
    if (logo) mid.push(logo);
    if (priceEl) mid.push(priceEl);
    main.push(`<div class="lp-mid">${mid.join('')}</div>`);
    if (bcSvg) main.push(`<div class="${bcCls}">${bcSvg}</div>`);
    if (codeEl) main.push(codeEl);
    if (extraEl) main.push(extraEl);
    if (hcEl && o.hcPos === 'bottom') main.push(hcEl);
    let strip = '';
    if (o.showVertical && vnum) {
      const sideCls = ` lp-vbc-${o.vbcSide}`;
      if (o.vbcMode === 'number') {
        // The old look — the number as vertical text. Kept as an escape hatch only.
        strip = `<div class="lp-vbc lp-vbc-num-only${sideCls}">${escapeHtml(vnum)}</div>`;
      } else {
        // displayValue:false and the digits drawn as their own band — otherwise
        // JsBarcode's text would be scaled with the stretched svg and --fs-vbc
        // would stop being an honest millimetre size.
        const svg = barcodeSvg(vnum, s.format, { displayValue: false, lineColor: o.vbcColor, background: bcBg, stretch: true, quietModules: VBC_QUIET_MODULES });
        const flip = o.vbcRotate === 270 ? ' lp-vbc-r270' : '';
        const num = o.vbcDigits ? `<div class="lp-vbc-num">${escapeHtml(vnum)}</div>` : '';
        // Always [digits, bars] in the DOM; row-reverse flips it for 270°.
        strip = `<div class="lp-vbc lp-vbc-bc${sideCls}${flip}">${num}<div class="lp-vbc-bars">${svg}</div></div>`;
      }
    }
    // A full-width rule runs out through the label's padding to the paper's edge — but
    // NOT across the side strip, whose bars it would cut straight through and make
    // unscannable. So the label advertises which edge the strip took, and the rule
    // stops there. (No strip ⇒ neither class ⇒ it spans both edges.)
    const stripCls = strip ? ` lp-vbc-at-${o.vbcSide}` : '';
    // The corner box goes INSIDE .lp-main, never the whole label, so an absolutely
    // positioned code can never land on the vertical barcode strip.
    return `<div class="lp-label lp-tag${stripCls}${o.border ? ' lp-bordered' : ''}">${wm}<div class="lp-main">${main.join('')}${hcEl && o.hcPos === 'corner' ? hcEl : ''}</div>${strip}</div>`;
  }

  // ── Centered stack (the original). ──
  const parts: string[] = [];
  if (hcEl && o.hcPos === 'top') parts.push(hcEl);
  if (logo) parts.push(logo);
  if (nameEl) parts.push(nameEl);
  if (bcSvg) parts.push(`<div class="${bcCls}">${bcSvg}</div>`);
  if (codeEl) parts.push(codeEl);
  if (priceEl) parts.push(priceEl);
  if (extraEl) parts.push(extraEl);
  if (hcEl && o.hcPos === 'bottom') parts.push(hcEl);
  // Out of flow ⇒ a direct child of .lp-label, right beside the watermark layer.
  const hcCornerEl = hcEl && o.hcPos === 'corner' ? hcEl : '';
  return `<div class="lp-label${o.border ? ' lp-bordered' : ''}">${wm}${hcCornerEl}${parts.join('')}</div>`;
}
export function buildSheetHtml(specs: LabelSpec[], o: SheetOpts): string {
  const labels = specs.map((s) => labelHtml(s, o)).join('');
  const { sizeMm, fsMm } = wmMetrics(o);
  const { numMm, thickMm, lenMm, offMm } = vbcMetrics(o);
  const g = geometry(o);
  // Every knob rides as a CSS var so the preview and the print popup share one rule set.
  const vars = `--lw:${o.widthMm}mm;--lh:${o.heightMm}mm;--fs-name:${o.nameMm}mm;--fs-price:${o.priceMm}mm;--fs-code:${o.codeMm}mm;--fs-vbc:${o.vbcMm}mm;--fs-extra:${o.extraMm}mm;--accent:${o.accent};--name-c:${o.nameColor};--price-c:${o.priceColor};--code-c:${o.codeColor};--font:${o.font};--logo-h:${o.logoMm}mm`
    + `;--wm-size:${sizeMm.toFixed(2)}mm;--wm-fs:${fsMm.toFixed(2)}mm;--wm-op:${o.wmOpacity};--wm-rot:${o.wmRotate}deg;--wm-c:${o.wmColor}`
    + `;--vbc-w:${o.vbcWidthMm}mm;--vbc-num:${numMm.toFixed(2)}mm;--vbc-thick:${thickMm.toFixed(2)}mm;--vbc-len:${lenMm.toFixed(2)}mm;--vbc-off:${offMm.toFixed(2)}mm;--vbc-c:${o.vbcColor}`
    // Inert while the hidden code is off: no .lp-hidden element is ever emitted then.
    + `;--fs-hc:${o.hcMm}mm;--hc-c:${o.hcColor}`
    // Layout + spacing. The mm values are ALREADY resolved (Auto → the template's own
    // default) and clamped by geometry(), so the fallbacks in the rules below only ever
    // apply to markup built without this builder.
    + `;--pad-x:${g.padX.toFixed(2)}mm;--pad-y:${g.padY.toFixed(2)}mm;--gap:${g.gap.toFixed(2)}mm;--name-lines:${g.nameLines}`
    + `;--align:${o.align};--align-items:${FLEX_ALIGN[o.align]}`
    // Per element, already resolved through alignOf() — the CSS never sees 'auto'.
    // --ai-price is the flex twin: on a price tag the price lives inside .lp-mid's ROW,
    // where text-align has nothing to move and justify-content does the work.
    + `;--al-name:${alignOf(o, o.nameAlign)};--al-price:${alignOf(o, o.priceAlign)};--al-code:${alignOf(o, o.codeAlign)};--al-extra:${alignOf(o, o.extraAlign)}`
    + `;--ai-price:${FLEX_ALIGN[alignOf(o, o.priceAlign)]}`
    // The barcode: a percentage width, and a height only when it is pinned (the
    // .lp-bc-fix class is what reads it — an auto barcode still fills what's left).
    + `;--bc-w:${o.bcWidthPct}%${o.bcMm >= 0 ? `;--bc-h:${o.bcMm}mm` : ''}`
    // A narrowed barcode is moved by auto margins, because .lp-main stretches its
    // children and so has no align-items to place it with.
    + `;--bc-ml:${o.align === 'left' ? '0' : 'auto'};--bc-mr:${o.align === 'right' ? '0' : 'auto'}`
    + `;--div-h:${o.dividerMm}mm;--cell-gx:${o.cellGapXMm}mm;--cell-gy:${o.cellGapYMm}mm`;
  return o.mode === 'roll' ? `<div class="lp-roll" style="${vars}">${labels}</div>` : `<div class="lp-sheet" style="${vars}">${labels}</div>`;
}

// Strong-directional ranges (Hebrew/Arabic/Syriac/Thaana/NKo… vs Latin/Greek/Cyrillic/
// Armenian) — enough to pick a base direction the way the bidi algorithm's P2/P3 does.
const STRONG_RTL = /[\u0591-\u07FF\uFB1D-\uFDFF\uFE70-\uFEFF]/;
const STRONG_LTR = /[A-Za-z\u00C0-\u02AF\u0370-\u058F\u1E00-\u1FFF]/;
/**
 * First-strong-character base direction = exactly what `unicode-bidi: plaintext` gives
 * .lp-wm-tx, .lp-name, .lp-extra and .lp-price. A detached canvas always resolves to
 * 'ltr', so without this a mixed watermark ("خصم 20%") — or an Arabic product name
 * ("دفتر ناكي تيل B5") — would print its tokens in the opposite order in the PDF.
 */
function baseDirection(s: string): CanvasDirection {
  for (const ch of s) {
    if (STRONG_RTL.test(ch)) return 'rtl';
    if (STRONG_LTR.test(ch)) return 'ltr';
  }
  return 'ltr';
}

/**
 * The PDF twin of watermarkHtml — keep the two in step. Drawn after the paper fill and
 * the cut border, which is exactly where a positioned z-index:0 child paints in HTML.
 */
function drawWatermark(ctx: CanvasRenderingContext2D, o: SheetOpts, W: number, H: number, S: number, logoImg: HTMLImageElement | null): void {
  if (!o.wmOn) return;
  const { sizeMm, fsMm } = wmMetrics(o);
  const pad = S * WM_PAD_MM;
  const rot = (o.wmRotate * Math.PI) / 180;

  // The un-rotated box is placed first and then spun about its own centre — a CSS
  // transform never affects layout either, so the two land in the same place.
  let boxW = 0; let boxH = 0;
  const isText = o.wmSource === 'text';
  const text = o.wmText.trim();
  if (isText) {
    if (!text) return;
    const fs = Math.round(S * fsMm);
    ctx.save();
    ctx.font = `900 ${fs}px ${o.font}`;
    boxW = ctx.measureText(text).width;
    boxH = fs;
    ctx.restore();
  } else {
    if (!o.wmLogoUrl || !logoImg) return;
    boxW = S * sizeMm; boxH = S * sizeMm; // square box; the logo is contained inside it
  }

  // Anchor = the centre of that box (flex align/justify + the WM_PAD_MM margin).
  const cx = o.wmPos === 'tl' || o.wmPos === 'bl' ? pad + boxW / 2
    : o.wmPos === 'tr' || o.wmPos === 'br' ? W - pad - boxW / 2
      : W / 2;
  const cy = o.wmPos === 'tl' || o.wmPos === 'tr' ? pad + boxH / 2
    : o.wmPos === 'bl' || o.wmPos === 'br' ? H - pad - boxH / 2
      : H / 2;

  // save/restore is not optional: globalAlpha, the clip and the transform are all
  // sticky context state, and everything drawn after this would inherit them —
  // in the exported PDF only, never in the preview.
  ctx.save();
  ctx.beginPath(); ctx.rect(0, 0, W, H); ctx.clip(); // = .lp-label's overflow: hidden
  ctx.globalAlpha = o.wmOpacity;
  ctx.translate(cx, cy);
  if (rot) ctx.rotate(rot);
  if (isText) {
    ctx.font = `900 ${Math.round(S * fsMm)}px ${o.font}`;
    ctx.fillStyle = o.wmColor;
    // Match .lp-wm-tx's unicode-bidi: plaintext — a canvas is 'ltr' by default, which
    // would reorder a mixed Arabic/Latin watermark in the PDF only.
    ctx.direction = baseDirection(text);
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText(text, 0, 0);
  } else if (logoImg) {
    const a = logoImg.width / Math.max(1, logoImg.height); // object-fit: contain
    let dw = boxW; let dh = boxH;
    if (a >= 1) dh = boxW / a; else dw = boxH * a;
    ctx.drawImage(logoImg, -dw / 2, -dh / 2, dw, dh);
  }
  ctx.restore();
}

/**
 * Draw one label to a high-res canvas (crisp barcode + Arabic/any-font text that
 * jsPDF's built-in fonts can't render — so text goes in as an image too).
 *
 * THE THIRD RENDERER. It lives at module scope beside labelHtml/buildSheetHtml on
 * purpose: it closes over nothing but its arguments, so the one renderer that is easy
 * to forget sits next to the two it has to agree with — and can be driven directly.
 */
export function labelCanvas(s: LabelSpec, opts: SheetOpts, logoImg: HTMLImageElement | null): HTMLCanvasElement {
  const S = 12; // px per mm
  const W = Math.round(opts.widthMm * S);
  const H = Math.round(opts.heightMm * S);
  const cv = document.createElement('canvas');
  cv.width = W; cv.height = H;
  const ctx = cv.getContext('2d')!;
  const FF = opts.font;
  const nameTxt = opts.upper && s.name ? s.name.toUpperCase() : s.name;
  const priceTxt = s.price ? `${opts.pricePrefix}${s.price}${opts.priceSuffix}` : '';
  const priceCol = opts.priceAccent ? opts.accent : opts.priceColor;
  const extra = opts.extraText.trim();
  // The SAME resolver the CSS vars come from, so "Auto" can't mean two things.
  const geo = geometry(opts);
  const AL = opts.align;
  /**
   * The space after an element. Each call passes the literal this file has always
   * used at that spot, so an Auto design draws pixel-for-pixel what it drew before;
   * an explicit gap replaces every one of them at once, which is what the option
   * means ("the padding between everyone").
   */
  const gp = (dflt: number): number => S * (opts.gapMm >= 0 ? opts.gapMm : dflt);
  // Every content box in here starts INSIDE the cut border, exactly like a border-box
  // padding edge in CSS — that is the edge --pad-x / --pad-y are measured from too.
  const bo = opts.border ? S * 0.2 : 0;
  const padX = S * geo.padX + bo;
  const padY = S * geo.padY + bo;
  ctx.fillStyle = PAPER; ctx.fillRect(0, 0, W, H);
  if (opts.border) { const lw = Math.max(1, S * 0.2); ctx.strokeStyle = CUT; ctx.lineWidth = lw; ctx.strokeRect(lw / 2, lw / 2, W - lw, H - lw); }
  // Behind everything except the paper and the cut border — the same paint order as
  // the HTML, and drawn once here so both templates get it.
  drawWatermark(ctx, opts, W, H, S, logoImg);
  const bcBg = opts.wmOn && opts.wmBehindBarcode ? '' : PAPER;

  // ── Custom layout (mirrors the .lp-custom HTML). Each element is drawn into the box
  //    the owner placed, clipped to it exactly like the box's overflow: hidden, and in
  //    the same EL_IDS order the DOM uses — so when two boxes overlap, the one on top
  //    is the same one in both renderers. ──
  if (opts.template === 'custom') {
    /** The box in px, measured from just inside the border — .lp-custom has no padding. */
    const boxOf = (id: ElId): { x: number; y: number; w: number; h: number } => {
      const b = clampBox(opts.custom[id], opts.widthMm, opts.heightMm);
      return { x: bo + b.x * S, y: bo + b.y * S, w: b.w * S, h: b.h * S };
    };
    /** = the box's overflow: hidden, around one element's drawing. */
    const inBox = (id: ElId, draw: (b: { x: number; y: number; w: number; h: number }) => void): void => {
      const b = boxOf(id);
      ctx.save();
      ctx.beginPath(); ctx.rect(b.x, b.y, b.w, b.h); ctx.clip();
      draw(b);
      ctx.restore();
    };
    /** The x a `ctx.textAlign` of this alignment measures from, inside the box. */
    const anchor = (b: { x: number; w: number }, al: Align): number =>
      (al === 'left' ? b.x : al === 'right' ? b.x + b.w : b.x + b.w / 2);
    /**
     * object-fit: contain — the same letterbox .lp-abs img/svg gets.
     *
     * `alx` is where the SPARE WIDTH goes, and it exists because the two renderers
     * had no way to agree without it: the HTML hands the barcode's alignment to
     * preserveAspectRatio (xMin/xMid/xMax), which no CSS property can override, so a
     * left-aligned label put the bars flush left in the preview and the print pop-up
     * while this centred them in the PDF. An <img> is different — object-position
     * defaults to centre and nothing sets it — so the logo keeps the default.
     */
    const contain = (
      b: { x: number; y: number; w: number; h: number },
      ar: number,
      alx: Align = 'center',
    ): { x: number; y: number; w: number; h: number } => {
      let w = b.w; let h = w / ar;
      if (h > b.h) { h = b.h; w = h * ar; }
      const x = alx === 'left' ? b.x : alx === 'right' ? b.x + b.w - w : b.x + (b.w - w) / 2;
      return { x, y: b.y + (b.h - h) / 2, w, h };
    };
    /** One line of text, vertically centred in its box like the flex row it mirrors.
     *  `dir` = the row's `unicode-bidi: plaintext`; omitted, the ambient ltr stands
     *  (the SKU — .lp-code declares no bidi either). inBox's restore puts it back. */
    const line = (id: ElId, txt: string, al: Align, font: string, colour: string, dir?: CanvasDirection): void => inBox(id, (b) => {
      ctx.font = font; ctx.fillStyle = colour; ctx.textAlign = al; ctx.textBaseline = 'middle';
      if (dir) ctx.direction = dir;
      ctx.fillText(fitText(ctx, txt, b.w), anchor(b, al), b.y + b.h / 2);
    });

    for (const id of EL_IDS) {
      if (id === 'logo' && opts.logoUrl && logoImg) {
        inBox('logo', (b) => {
          const d = contain(b, logoImg.width / Math.max(1, logoImg.height));
          ctx.drawImage(logoImg, d.x, d.y, d.w, d.h);
        });
      } else if (id === 'name' && opts.showName && nameTxt) {
        inBox('name', (b) => {
          ctx.font = `700 ${Math.round(S * opts.nameMm)}px ${FF}`; ctx.fillStyle = opts.nameColor;
          const al = alignOf(opts, opts.nameAlign);
          ctx.textAlign = al; ctx.textBaseline = 'top';
          // = .lp-name's unicode-bidi: plaintext; inBox's restore puts ltr back.
          ctx.direction = baseDirection(nameTxt);
          setTracking(ctx, Math.round(S * opts.nameMm) * NAME_TRACKING_EM);
          const lh = S * opts.nameMm * 1.08;
          const lines = wrapLines(ctx, nameTxt, b.w, fitLines(opts, b.h / S, geo.nameLines));
          // The block is centred in the box, which is what align-items: center does to
          // a multi-line child — not "first line at the top".
          const top = b.y + (b.h - lines.length * lh) / 2;
          lines.forEach((ln, i) => ctx.fillText(ln, anchor(b, al), top + i * lh));
          setTracking(ctx, 0);
        });
      } else if (id === 'barcode' && opts.showBarcode) {
        // matchSvgAspect because this template LETTERBOXES the barcode inside a box the
        // owner drew, so the image's own proportions reach the paper instead of being
        // scaled away — bars, quiet zone and digits all have to be the SVG's.
        const bc = barcodeCanvas(s.barcode || s.code, s.format, { background: bcBg, matchSvgAspect: true });
        // opts.align, NOT a per-element alignment: labelHtml passes exactly this to
        // barcodeSvg's xAlign, and there is no barcodeAlign control for it to read.
        const bcAr = barcodeAspect(s.barcode || s.code, s.format) ?? (bc ? bc.width / bc.height : 1);
        if (bc) {
          inBox('barcode', (b) => {
            if (!opts.bcVertical) {
              const d = contain(b, bcAr, opts.align);
              ctx.drawImage(bc, d.x, d.y, d.w, d.h);
              return;
            }
            // The same quarter turn the HTML does, expressed the canvas way: letterbox
            // into the box with its AXES SWAPPED, then rotate that result about the
            // box's centre. Letterboxing after the rotation instead would fit the image
            // to the wrong pair of sides and leave the bars short.
            const swapped = { x: 0, y: 0, w: b.h, h: b.w };
            const d = contain(swapped, bcAr, opts.align);
            ctx.save();
            ctx.translate(b.x + b.w / 2, b.y + b.h / 2);
            ctx.rotate(-Math.PI / 2);
            ctx.drawImage(bc, d.x - swapped.w / 2, d.y - swapped.h / 2, d.w, d.h);
            ctx.restore();
          });
        }
      } else if (id === 'code' && opts.showCode && s.code) {
        line('code', s.code, alignOf(opts, opts.codeAlign), `${Math.round(S * opts.codeMm)}px "Courier New", monospace`, opts.codeColor);
      } else if (id === 'price' && opts.showPrice && priceTxt) {
        line('price', priceTxt, alignOf(opts, opts.priceAlign), `900 ${Math.round(S * opts.priceMm)}px ${FF}`, priceCol, baseDirection(priceTxt));
      } else if (id === 'extra' && extra) {
        line('extra', extra, alignOf(opts, opts.extraAlign), `${Math.round(S * opts.extraMm)}px ${FF}`, opts.codeColor, baseDirection(extra));
      } else if (id === 'hidden' && s.hidden) {
        // Through drawHidden, so the code keeps its forced ltr + tracking here too.
        inBox('hidden', (b) => {
          // The LABEL's alignment, not codeAlign: .lp-hidden declares no text-align of
          // its own, so in HTML it inherits --align from .lp-label — and the flow
          // templates draw it from `AL` for that same reason. Reading codeAlign here
          // sent the hidden code one way in the PDF and the other way in the preview
          // the moment the SKU was aligned away from the label.
          const al = opts.align;
          ctx.textAlign = al; ctx.textBaseline = 'middle';
          drawHidden(ctx, opts, S, s.hidden, anchor(b, al), b.y + b.h / 2, b.w);
        });
      }
    }
    return cv;
  }

  // ── Price-tag layout (mirrors the .lp-tag HTML). ──
  if (opts.template === 'tag') {
    const vnum = (s.barcode || s.code || '').trim();
    let L = padX;
    let R = W - padX;
    let stripSide: VbcSide | null = null;
    if (opts.showVertical && vnum) {
      const { numMm, thickMm, lenMm, offMm, availMm } = vbcMetrics(opts);
      stripSide = opts.vbcSide;
      const stripW = S * opts.vbcWidthMm;
      const numW = S * numMm; const thick = S * thickMm; const len = S * lenMm;
      const x0 = opts.vbcSide === 'left' ? padX : W - padX - stripW;
      // The strip's BOX is full height (align-items: stretch) whatever the bars do;
      // only the BARS are shortened and centred by offMm, exactly like --vbc-off.
      const yBox = padY; const boxH = S * availMm;
      const y0 = yBox + S * offMm;
      const r90 = opts.vbcRotate !== 270;
      // = .lp-vbc's overflow: hidden. Text that outgrows its band clips at the strip
      // here too, instead of the PDF alone printing digits over the price column.
      ctx.save();
      ctx.beginPath(); ctx.rect(x0, yBox, stripW, boxH); ctx.clip();
      if (opts.vbcMode === 'number') {
        const fs = Math.round(S * opts.vbcMm);
        ctx.save();
        ctx.font = `700 ${fs}px ${FF}`; ctx.fillStyle = opts.accent; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.translate(x0 + stripW / 2, yBox + boxH / 2); ctx.rotate(Math.PI / 2); ctx.fillText(vnum, 0, 0);
        ctx.restore();
      } else {
        // At 90° the barcode's baseline edge lands on the strip's left, so the digits
        // belong there; at 270° it lands on the right. = flex-direction: row-reverse.
        const barsX = r90 ? x0 + numW : x0;
        const numX = r90 ? x0 : x0 + thick;
        const bc = barcodeCanvas(vnum, s.format, { displayValue: false, lineColor: opts.vbcColor, background: bcBg, quietModules: VBC_QUIET_MODULES });
        if (bc) {
          // Explicit dw/dh = the same non-uniform scale as preserveAspectRatio="none".
          // Both images are pure bars in the same module-based aspect (barGeometry), so
          // the module width and the bar length come out identical to the preview.
          ctx.save();
          ctx.translate(barsX, y0);
          if (r90) { ctx.rotate(Math.PI / 2); ctx.drawImage(bc, 0, -thick, len, thick); } else { ctx.rotate(-Math.PI / 2); ctx.drawImage(bc, -len, 0, len, thick); }
          ctx.restore();
        }
        if (opts.vbcDigits) {
          ctx.save();
          // Clip to the DIGIT BAND, not the whole strip: vbcMetrics gives the digits
          // whatever the bars don't need, so an oversized digit size can exceed the
          // band. The HTML clips at .lp-vbc-num, and without the same rect here the
          // digits would print across the bars in the PDF only — and unscannably.
          // The BOX, not the bars: .lp-vbc-num stretches to the strip's full height and
          // centres its text there, so clipping to a shortened bar length sliced the
          // first and last digit off in the PDF alone.
          ctx.beginPath(); ctx.rect(numX, yBox, numW, boxH); ctx.clip();
          ctx.font = `700 ${Math.round(S * opts.vbcMm)}px ${FF}`; ctx.fillStyle = opts.vbcColor; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
          ctx.translate(numX + numW / 2, yBox + boxH / 2); ctx.rotate(r90 ? Math.PI / 2 : -Math.PI / 2); ctx.fillText(vnum, 0, 0);
          ctx.restore();
        }
      }
      ctx.restore(); // drop the strip clip before the main column is drawn
      if (opts.vbcSide === 'left') L = padX + stripW + S * VBC_GAP_MM; else R = W - padX - stripW - S * VBC_GAP_MM;
    }
    const mw = R - L; let tTop = padY; let tBot = H - padY;
    // The anchor every flow element in the main column is drawn from — L / centre / R
    // for the three alignments, matching .lp-main's inherited text-align.
    const ax = AL === 'left' ? L : AL === 'right' ? R : L + mw / 2;
    /**
     * The anchor for ONE element, through its own alignment. Every fillText sets
     * ctx.textAlign from the same call, because the two must agree: an anchor on the
     * right edge with textAlign 'left' would draw the text off the label.
     */
    const at_ = (el: ElAlign): Align => alignOf(opts, el);
    const axOf = (el: ElAlign): number => { const a = at_(el); return a === 'left' ? L : a === 'right' ? R : L + mw / 2; };
    ctx.textAlign = AL;
    // Hidden code, TOP: drawn before the name so it owns its space, exactly like
    // the first child of .lp-main. The literal is that flex column's own gap.
    if (s.hidden && opts.hcPos === 'top') {
      ctx.textBaseline = 'top';
      ctx.textAlign = AL;
      drawHidden(ctx, opts, S, s.hidden, ax, tTop, mw);
      tTop += S * opts.hcMm + gp(TAG_GAP_DEFAULT);
    }
    if (opts.showName && nameTxt) {
      ctx.textBaseline = 'top'; ctx.font = `700 ${Math.round(S * opts.nameMm)}px ${FF}`; ctx.fillStyle = opts.nameColor;
      // Tracked while it wraps AND while it draws, so the PDF breaks the name at the
      // same word the browser does — then put back, because canvas text state sticks.
      ctx.textAlign = at_(opts.nameAlign);
      // = .lp-name's unicode-bidi: plaintext — and put back, like the tracking,
      // because direction is sticky context state too.
      ctx.direction = baseDirection(nameTxt);
      setTracking(ctx, Math.round(S * opts.nameMm) * NAME_TRACKING_EM);
      const lh = S * opts.nameMm * 1.08;   // = .lp-name's line-height
      const lines = wrapLines(ctx, nameTxt, mw, geo.nameLines);
      const nx = axOf(opts.nameAlign);
      lines.forEach((ln, i) => ctx.fillText(ln, nx, tTop + i * lh));
      setTracking(ctx, 0);
      ctx.direction = 'ltr';
      tTop += lines.length * lh + gp(0.5);
    }
    // The rule runs the full label width when asked — but never across the side
    // strip, whose bars it would cut through. Same rule as .lp-div-full's margins.
    if (opts.divider) {
      const dL = opts.dividerFull && stripSide !== 'left' ? bo : L;
      const dR = opts.dividerFull && stripSide !== 'right' ? W - bo : R;
      ctx.strokeStyle = RULE; ctx.lineWidth = Math.max(1, S * opts.dividerMm);
      ctx.beginPath(); ctx.moveTo(dL, tTop + S * 0.2); ctx.lineTo(dR, tTop + S * 0.2); ctx.stroke();
      // = .lp-div's own box (0.2mm margin · the rule · 0.2mm margin) and then the
      // column's gap. The 0.15 is the slack the single 0.8mm constant used to carry,
      // kept so an Auto design lands on exactly the millimetre it always did.
      tTop += S * (0.15 + opts.dividerMm) + gp(0.4);
    }
    // Hidden code, BOTTOM. The tag draws bottom-up, so drawing FIRST puts it
    // lowest — where the last child of .lp-main sits. tBot has already moved by
    // the time the barcode sizes itself, so nothing can overlap it.
    if (s.hidden && opts.hcPos === 'bottom') {
      ctx.textBaseline = 'bottom';
      ctx.textAlign = AL;
      drawHidden(ctx, opts, S, s.hidden, ax, tBot, mw);
      tBot -= S * opts.hcMm + gp(TAG_GAP_DEFAULT);
    }
    if (extra) { ctx.textBaseline = 'bottom'; ctx.font = `${Math.round(S * opts.extraMm)}px ${FF}`; ctx.fillStyle = opts.codeColor; ctx.textAlign = at_(opts.extraAlign); ctx.direction = baseDirection(extra); ctx.fillText(fitText(ctx, extra, mw), axOf(opts.extraAlign), tBot); ctx.direction = 'ltr'; tBot -= S * opts.extraMm + gp(0.5); }
    if (opts.showCode && s.code) { ctx.textBaseline = 'bottom'; ctx.font = `${Math.round(S * opts.codeMm)}px "Courier New", monospace`; ctx.fillStyle = opts.codeColor; ctx.textAlign = at_(opts.codeAlign); ctx.fillText(s.code, axOf(opts.codeAlign), tBot); tBot -= S * opts.codeMm + gp(0.6); }
    if (opts.showBarcode) {
      const bc = barcodeCanvas(s.barcode || s.code, s.format, { background: bcBg });
      if (bc) {
        const room = tBot - tTop;
        // Auto keeps the old "at most 5mm, and never more than 40% of what's left";
        // a pinned height is the height the owner asked for, capped only by the room.
        const bh = opts.bcMm >= 0 ? Math.min(S * opts.bcMm, room) : Math.min(S * TAG_BC_MM_DEFAULT, room * 0.4);
        const a = bc.width / bc.height;
        let dw = (mw * opts.bcWidthPct) / 100; let dh = dw / a;
        if (dh > bh) { dh = bh; dw = dh * a; }
        if (dh > 0) {
          ctx.drawImage(bc, AL === 'left' ? L : AL === 'right' ? R - dw : L + (mw - dw) / 2, tBot - dh, dw, dh);
          tBot -= dh + gp(0.4);
        }
      }
    }
    // .lp-mid — [logo][gap][price] laid out as ONE row and justified together, which
    // is what the flexbox does; the canvas used to pin the logo left and centre the
    // price in what was left, so the two renderers drifted apart the moment a label
    // had a logo. Measured first, placed second.
    const midH = tBot - tTop;
    let logoW = 0; let logoH = 0;
    if (opts.logoUrl && logoImg) {
      const a = logoImg.width / Math.max(1, logoImg.height);
      logoH = midH * 0.85; logoW = logoH * a;
      if (logoW > mw * 0.34) { logoW = mw * 0.34; logoH = logoW / a; }
    }
    let priceW = 0;
    if (opts.showPrice && priceTxt) { ctx.font = `900 ${Math.round(S * opts.priceMm)}px ${FF}`; priceW = ctx.measureText(priceTxt).width; }
    const midGap = logoW > 0 && priceW > 0 ? S * TAG_MID_GAP_MM : 0;
    const rowW = logoW + midGap + priceW;
      const pAl = at_(opts.priceAlign);
    const rowX = pAl === 'left' ? L : pAl === 'right' ? R - rowW : L + (mw - rowW) / 2;
    if (logoW > 0 && logoImg) ctx.drawImage(logoImg, rowX, tTop + (midH - logoH) / 2, logoW, logoH);
    if (priceW > 0) {
      ctx.textAlign = 'left'; ctx.textBaseline = 'middle'; ctx.fillStyle = priceCol;
      // = .lp-price's unicode-bidi: plaintext. The anchor stays the row's physical
      // left edge; only the bidi order inside the measured width changes.
      ctx.direction = baseDirection(priceTxt);
      ctx.fillText(priceTxt, rowX + logoW + midGap, tTop + midH / 2);
      ctx.direction = 'ltr';
    }
    // Hidden code, CORNER — out of flow, so it is drawn last and paints on top.
    // Its box is L..R × padY..(H-padY): exactly .lp-main's padding box, which is
    // why it can never reach the vertical barcode strip (L/R are already strip-
    // and-gap adjusted above). Nothing may follow it — textAlign is left changed.
    if (s.hidden && opts.hcPos === 'corner') {
      const ins = S * HC_INSET_MM; const cTop = padY; const cBot = H - padY;
      ctx.textBaseline = opts.hcCorner === 'tl' || opts.hcCorner === 'tr' ? 'top' : 'bottom';
      ctx.textAlign = opts.hcCorner === 'tl' || opts.hcCorner === 'bl' ? 'left' : 'right';
      drawHidden(
        ctx, opts, S, s.hidden,
        ctx.textAlign === 'left' ? L + ins : R - ins,
        ctx.textBaseline === 'top' ? cTop + ins : cBot - ins,
        mw - 2 * ins,
      );
    }
    return cv;
  }

  // The content box, and the x every flow element is drawn from. Both axes now come
  // from the padding option (the vertical one used to be hard-coded to the horizontal
  // 1.5mm, half a millimetre away from the 1mm the CSS has always used).
  const cw = W - padX * 2;
  const ax = AL === 'left' ? padX : AL === 'right' ? W - padX : W / 2;
    /**
     * The anchor for ONE element, through its own alignment. Every fillText sets
     * ctx.textAlign from the same call, because the two must agree: an anchor on the
     * right edge with textAlign 'left' would draw the text off the label.
     */
  const at_ = (el: ElAlign): Align => alignOf(opts, el);
  const axOf = (el: ElAlign): number => { const a = at_(el); return a === 'left' ? padX : a === 'right' ? W - padX : W / 2; };
  let top = padY; let bottom = H - padY;
  ctx.textAlign = AL; ctx.fillStyle = INK;
  // Hidden code, TOP: the first child of .lp-label, above the logo. The literal is
  // that flex column's own gap.
  if (s.hidden && opts.hcPos === 'top') {
    ctx.textBaseline = 'top';
    ctx.textAlign = AL;
    drawHidden(ctx, opts, S, s.hidden, ax, top, cw);
    top += S * opts.hcMm + gp(GAP_DEFAULT);
  }
  // Store logo strip at the top (mirrors .lp-logo): fit inside the configured
  // height × 65% wide, preserving aspect ratio.
  if (opts.logoUrl && logoImg) {
    const aspect = logoImg.width / Math.max(1, logoImg.height);
    let dh = S * opts.logoMm;
    let dw = dh * aspect;
    if (dw > W * 0.65) { dw = W * 0.65; dh = dw / aspect; }
    ctx.drawImage(logoImg, AL === 'left' ? padX : AL === 'right' ? W - padX - dw : (W - dw) / 2, top + (S * opts.logoMm - dh) / 2, dw, dh);
    top += S * opts.logoMm + gp(GAP_DEFAULT);
  }
  if (opts.showName && nameTxt) {
    ctx.textBaseline = 'top'; ctx.font = `700 ${Math.round(S * opts.nameMm)}px ${FF}`; ctx.fillStyle = opts.nameColor;
    // Tracked while it wraps AND while it draws, so the PDF breaks the name at the
    // same word the browser does — then put back, because canvas text state sticks.
    ctx.textAlign = at_(opts.nameAlign);
    // = .lp-name's unicode-bidi: plaintext — and put back, like the tracking,
    // because direction is sticky context state too.
    ctx.direction = baseDirection(nameTxt);
    setTracking(ctx, Math.round(S * opts.nameMm) * NAME_TRACKING_EM);
    const lh = S * opts.nameMm * 1.08;   // = .lp-name's line-height
    const lines = wrapLines(ctx, nameTxt, cw, geo.nameLines);
    const nx = axOf(opts.nameAlign);
    lines.forEach((ln, i) => ctx.fillText(ln, nx, top + i * lh));
    setTracking(ctx, 0);
    ctx.direction = 'ltr';
    top += lines.length * lh + gp(0.7);
  }
  ctx.textBaseline = 'bottom';
  // Hidden code, BOTTOM — drawn first for the same bottom-up reason as the tag.
  // `bottom` shrinking feeds straight into the barcode's `bh = bottom - top` and
  // its `bh > S * 3` bail-out, so a cramped label loses barcode height, never ink.
  if (s.hidden && opts.hcPos === 'bottom') {
    ctx.textAlign = AL;
    drawHidden(ctx, opts, S, s.hidden, ax, bottom, cw);
    bottom -= S * opts.hcMm + gp(GAP_DEFAULT);
  }
  if (extra) { ctx.font = `${Math.round(S * opts.extraMm)}px ${FF}`; ctx.fillStyle = opts.codeColor; ctx.textAlign = at_(opts.extraAlign); ctx.direction = baseDirection(extra); ctx.fillText(fitText(ctx, extra, cw), axOf(opts.extraAlign), bottom); ctx.direction = 'ltr'; bottom -= S * opts.extraMm + gp(0.5); }
  if (opts.showPrice && priceTxt) { ctx.font = `900 ${Math.round(S * opts.priceMm)}px ${FF}`; ctx.fillStyle = priceCol; ctx.textAlign = at_(opts.priceAlign); ctx.direction = baseDirection(priceTxt); ctx.fillText(priceTxt, axOf(opts.priceAlign), bottom); ctx.direction = 'ltr'; bottom -= S * opts.priceMm + gp(0.6); }
  if (opts.showCode && s.code) { ctx.font = `${Math.round(S * opts.codeMm)}px "Courier New", monospace`; ctx.fillStyle = opts.codeColor; ctx.textAlign = at_(opts.codeAlign); ctx.fillText(s.code, axOf(opts.codeAlign), bottom); bottom -= S * opts.codeMm + gp(0.7); }
  if (opts.showBarcode) {
    const room = bottom - top;
    // Auto fills whatever the stack left over — and refuses under 3mm, because a
    // barcode that short is a smear. A pinned height is the owner's call, so the
    // only cap on it is the room that is actually there.
    const bh = opts.bcMm >= 0 ? Math.min(S * opts.bcMm, room) : room;
    if (bh > (opts.bcMm >= 0 ? 0 : S * 3)) {
      const bc = barcodeCanvas(s.barcode || s.code, s.format, { background: bcBg });
      if (bc) {
        const aspect = bc.width / bc.height;
        let dw = (cw * opts.bcWidthPct) / 100; let dh = dw / aspect;
        if (dh > bh) { dh = bh; dw = dh * aspect; }
        ctx.drawImage(bc, AL === 'left' ? padX : AL === 'right' ? W - padX - dw : (W - dw) / 2, top + (bh - dh) / 2, dw, dh);
      }
    }
  }
  // Hidden code, CORNER — last, so it paints on top like an out-of-flow child.
  // The `bo` term is what makes this agree with CSS: an absolutely positioned
  // child is offset from its containing block's PADDING box, i.e. just inside
  // .lp-bordered's 0.2mm border, so both renderers measure from the same edge.
  // Nothing may follow it — textAlign is left changed.
  if (s.hidden && opts.hcPos === 'corner') {
    const ins = S * HC_INSET_MM;
    ctx.textBaseline = opts.hcCorner === 'tl' || opts.hcCorner === 'tr' ? 'top' : 'bottom';
    ctx.textAlign = opts.hcCorner === 'tl' || opts.hcCorner === 'bl' ? 'left' : 'right';
    drawHidden(
      ctx, opts, S, s.hidden,
      ctx.textAlign === 'left' ? bo + ins : W - bo - ins,
      ctx.textBaseline === 'top' ? bo + ins : H - bo - ins,
      W - 2 * (bo + ins),
    );
  }
  return cv;
}

export const SHEET_CSS = `
/* Row gap then column gap: the space BETWEEN labels on the sheet, which the PDF's own
   tiling maths reads from the same two options (see onExportPdf). */
.lp-sheet { display: grid; grid-template-columns: repeat(auto-fill, var(--lw)); grid-auto-rows: var(--lh); gap: var(--cell-gy, ${CELL_GAP_DEFAULT}mm) var(--cell-gx, ${CELL_GAP_DEFAULT}mm); justify-content: start; align-content: start; }
.lp-roll { display: block; }
.lp-roll .lp-label { break-after: page; margin: 0 auto; }
/* --align moves the text, --align-items the shrink-to-fit children that text-align
   can't reach (a centered-template price is only as wide as its digits). Both come
   from ONE option; geometry() has already turned every "Auto" into a real value. */
.lp-label { position: relative; width: var(--lw); height: var(--lh); box-sizing: border-box; padding: var(--pad-y, ${PAD_Y_DEFAULT}mm) var(--pad-x, ${PAD_X_DEFAULT}mm); overflow: hidden; break-inside: avoid; page-break-inside: avoid; display: flex; flex-direction: column; align-items: var(--align-items, center); justify-content: center; gap: var(--gap, ${GAP_DEFAULT}mm); text-align: var(--align, center); font-family: var(--font, ${FONT}); color: #0b0b0b; background: #fff; }
/* position:relative above is what keeps the absolute watermark inside the label —
   without it the layer escapes to the app shell / the print page. */
.lp-label > :not(.lp-wm) { position: relative; z-index: 1; }
.lp-bordered { border: 0.2mm solid #c9ccd4; border-radius: 1mm; }
/* Watermark: out of flow (so neither template's flex layout changes), painted above
   the label's background + border but below every content child. */
.lp-wm { position: absolute; inset: 0; z-index: 0; overflow: hidden; pointer-events: none; direction: ltr; display: flex; align-items: center; justify-content: center; opacity: var(--wm-op, .12); -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.lp-wm-tl { align-items: flex-start; justify-content: flex-start; }
.lp-wm-tr { align-items: flex-start; justify-content: flex-end; }
.lp-wm-bl { align-items: flex-end; justify-content: flex-start; }
.lp-wm-br { align-items: flex-end; justify-content: flex-end; }
/* The inset is a margin on the inner box, not padding on .lp-wm: overflow clips at the
   padding box, so padding would also clip an over-100% bleed 1mm inside the edge.
   max-width/height are neutralised because Tailwind's preflight caps every <img> at
   100% in the app, but the print popup and the PDF canvas have no such reset — the box
   has to be the one the shared metrics computed in all three. */
.lp-wm-img { flex: none; box-sizing: border-box; width: var(--wm-size); height: var(--wm-size); max-width: none; max-height: none; object-fit: contain; margin: ${WM_PAD_MM}mm; transform: rotate(var(--wm-rot, 0deg)); transform-origin: center; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.lp-wm-tx { font-family: var(--font, ${FONT}); font-size: var(--wm-fs); font-weight: 900; line-height: 1; white-space: nowrap; color: var(--wm-c, #0b0b0b); unicode-bidi: plaintext; margin: ${WM_PAD_MM}mm; transform: rotate(var(--wm-rot, 0deg)); transform-origin: center; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.lp-logo { height: var(--logo-h, 4.2mm); max-width: 65%; object-fit: contain; flex: none; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
/* The line count and the clamp height are ONE knob: --name-lines is a bare number, so
   the max-height multiplies out to exactly the 2.3em the centered template always had
   at two lines. The tag's old hard-coded single line is now that template's Auto. */
/* TEXT DOES NOT SHRINK — the barcode does. Every row here is a flex item, and the
   default flex-shrink: 1 let the column squeeze them when the barcode's intrinsic
   height made the stack overflow: a two-line name came out with its second line sliced
   through the middle in the preview and the print pop-up, while the canvas (which draws
   text first and gives the barcode what is left) printed both lines. .lp-bc is
   "flex: 1 1 auto; min-height: 0" precisely so it can absorb that pressure instead. */
.lp-name, .lp-code, .lp-price, .lp-extra, .lp-hidden, .lp-div { flex: none; }
/* text-align only moves text inside a box that is WIDER than the text, so the rows
   that used to be shrink-to-fit (centred by the column's align-items) are now full
   width. Visually identical while everything is centred — and it is what lets one row
   sit left while its neighbour sits right. */
.lp-name { text-align: var(--al-name, var(--align, center)); }
.lp-code { text-align: var(--al-code, var(--align, center)); width: 100%; }
.lp-extra { text-align: var(--al-extra, var(--align, center)); }
.lp-price { text-align: var(--al-price, var(--align, center)); width: 100%; }
/* …except the tag's price, which is a flex item in the logo+price ROW. There its own
   width would break the pair, so --ai-price justifies the row instead. */
.lp-tag .lp-mid .lp-price { width: auto; }
/* unicode-bidi: plaintext on the name/extra/price rows = base direction from the row's
   own first strong character, NOT from whatever the surroundings say — the designer
   canvas is dir=ltr, .lp-tag forces direction:ltr and the print popup follows the
   admin's language, so an Arabic name ("دفتر ناكي تيل B5…") read its words back to
   front in all of them. Same pattern as .lp-wm-tx; the canvas agrees through
   baseDirection(). The SKU and the hidden code stay forced-ltr on purpose. */
.lp-name { font-size: var(--fs-name, 2.7mm); font-weight: 700; line-height: 1.08; letter-spacing: ${NAME_TRACKING_EM}em; color: var(--name-c, #0b0b0b); max-height: calc(var(--fs-name, 2.7mm) * var(--name-lines, ${NAME_LINES_DEFAULT}) * ${NAME_LINE_FACTOR}); overflow: hidden; width: 100%; display: -webkit-box; -webkit-line-clamp: var(--name-lines, ${NAME_LINES_DEFAULT}); -webkit-box-orient: vertical; unicode-bidi: plaintext; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.lp-upper { text-transform: uppercase; }
.lp-bc { width: var(--bc-w, 100%); flex: 1 1 auto; min-height: 0; display: flex; align-items: center; justify-content: center; }
/* A pinned height kills flex-GROW (an item told to grow ignores the height it was
   given) but keeps flex-SHRINK: the height is a cap, not a promise. It is the only
   shrinkable row left, so when the stack asks for more than the label has, the barcode
   yields and the text survives — which is exactly the rule the canvas applies with
   "Math.min(S * bcMm, room)". Pin it rigid instead and the label just overflows. */
.lp-bc-fix { flex: 0 1 var(--bc-h, ${TAG_BC_MM_DEFAULT}mm); max-height: var(--bc-h, ${TAG_BC_MM_DEFAULT}mm); }
.lp-bc svg { width: 100%; height: 100%; max-height: 100%; display: block; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.lp-code { font-size: var(--fs-code, 2.3mm); font-family: ui-monospace, "Cascadia Code", "Courier New", monospace; color: var(--code-c, #52525b); letter-spacing: .04em; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.lp-extra { font-size: var(--fs-extra, 2mm); color: var(--code-c, #52525b); line-height: 1.1; width: 100%; overflow: hidden; unicode-bidi: plaintext; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.lp-price { font-size: var(--fs-price, 5mm); font-weight: 900; letter-spacing: -0.01em; color: var(--price-c, #0b0b0b); unicode-bidi: plaintext; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.lp-accent { color: var(--accent, #c1272d); -webkit-print-color-adjust: exact; print-color-adjust: exact; }
/* Price-tag template. Symmetric padding + direction:ltr because the side strip can now
   sit on either edge, and flex order has to mean the same thing in an RTL admin as it
   does in the PDF canvas (which has no writing direction at all). */
/* The row layout keeps align-items: stretch (that is what makes the main column and the
   strip full height), so --align reaches the tag purely through text-align, inherited
   from .lp-label. Padding is inherited from .lp-label too — one option, both templates. */
.lp-tag { flex-direction: row; align-items: stretch; gap: ${VBC_GAP_MM}mm; direction: ltr; }
.lp-tag .lp-main { flex: 1 1 auto; min-width: 0; display: flex; flex-direction: column; justify-content: space-between; gap: var(--gap, ${TAG_GAP_DEFAULT}mm); }
.lp-tag .lp-div { border-top: var(--div-h, ${DIV_MM_DEFAULT}mm) solid #b8bcc6; align-self: stretch; margin: 0.2mm 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
/* Full width = out through the label's own padding to the paper's edge. NOT across the
   side strip: the rule would cross the bars and no scanner would read them again, so
   the label tells us which edge the strip took and the rule stops at the column there. */
.lp-tag .lp-div-full { margin-left: calc(-1 * var(--pad-x, ${TAG_PAD_MM}mm)); margin-right: calc(-1 * var(--pad-x, ${TAG_PAD_MM}mm)); }
.lp-vbc-at-left .lp-div-full { margin-left: 0; }
.lp-vbc-at-right .lp-div-full { margin-right: 0; }
.lp-tag .lp-mid { flex: 1 1 auto; min-height: 0; display: flex; align-items: center; justify-content: var(--ai-price, var(--align-items, center)); gap: ${TAG_MID_GAP_MM}mm; }
.lp-tag .lp-mid .lp-logo { height: auto; max-height: 92%; max-width: 34%; flex: none; }
.lp-tag .lp-price { flex: 0 1 auto; line-height: 1; }
/* .lp-main stretches its children, so a narrowed barcode has no align-items to place
   it — auto margins do that job instead. Physical left/right, not logical: .lp-tag is
   direction: ltr precisely so the two renderers can't disagree in an Arabic admin. */
.lp-tag .lp-bc { flex: 0 0 auto; max-height: ${TAG_BC_MM_DEFAULT}mm; margin-left: var(--bc-ml, auto); margin-right: var(--bc-mr, auto); }
.lp-tag .lp-bc-fix { flex: 0 1 var(--bc-h, ${TAG_BC_MM_DEFAULT}mm); max-height: var(--bc-h, ${TAG_BC_MM_DEFAULT}mm); }
/* The side strip. box-sizing is spelled out because the print popup has no CSS reset,
   so these fixed widths would otherwise grow by their padding there but not in-app.
   overflow:hidden is the last line of defence (the metrics already keep the two bands
   inside the strip): oversized digits must clip, never print over the price column. */
.lp-tag .lp-vbc { order: 2; flex: 0 0 var(--vbc-w, 9mm); width: var(--vbc-w, 9mm); box-sizing: border-box; display: flex; direction: ltr; align-items: stretch; overflow: hidden; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.lp-tag .lp-main { order: 1; }
.lp-tag .lp-vbc-left { order: 0; }
.lp-tag .lp-vbc-r270 { flex-direction: row-reverse; }
.lp-vbc-num { flex: 0 0 var(--vbc-num, 3.2mm); width: var(--vbc-num, 3.2mm); box-sizing: border-box; overflow: hidden; writing-mode: vertical-rl; text-orientation: mixed; white-space: nowrap; font-size: var(--fs-vbc, 2.4mm); font-weight: 700; letter-spacing: .04em; line-height: 1; color: var(--vbc-c, #0b0b0b); display: flex; align-items: center; justify-content: center; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.lp-vbc-r270 .lp-vbc-num { transform: rotate(180deg); }
.lp-vbc-bars { flex: 0 0 var(--vbc-thick, 5.8mm); width: var(--vbc-thick, 5.8mm); box-sizing: border-box; position: relative; overflow: hidden; }
/* The svg gets the SWAPPED box (length × thickness) and is spun into place:
   rotate(90deg) translate(0,-100%) maps a local (x,y) to (T-y, x), so the barcode
   exactly fills the T × L wrapper and reads top→bottom — no leftover offset. */
/* --vbc-off centres a shortened strip: after the rotation the visual box runs from the
   svg's own top edge down by --vbc-len, so offsetting the box offsets the bars. */
.lp-vbc-bars > svg { position: absolute; top: var(--vbc-off, 0); left: 0; width: var(--vbc-len); height: var(--vbc-thick, 5.8mm); transform-origin: 0 0; transform: rotate(90deg) translate(0, -100%); display: block; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.lp-vbc-r270 > .lp-vbc-bars > svg { transform: rotate(-90deg) translate(-100%, 0); }
/* Number-only mode: the pre-barcode look, kept for anyone who preferred it. Selector
   qualified with .lp-tag so its centering beats the strip rule's align-items: stretch. */
.lp-tag .lp-vbc-num-only { writing-mode: vertical-rl; text-orientation: mixed; white-space: nowrap; font-size: var(--fs-vbc, 2.4mm); font-weight: 700; letter-spacing: .06em; color: var(--accent, #c1272d); display: flex; align-items: center; justify-content: center; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
/* ── CUSTOM template: absolutely positioned boxes, placed by the owner. ──
   padding:0 because an absolute child is offset from the PADDING box — with padding
   on, "3mm from the left" would silently mean 3mm from the padding edge and the canvas
   (which measures from just inside the border) would disagree by exactly that padding.
   The selector matches ".lp-label > :not(.lp-wm)" for specificity and comes later, for
   the same reason .lp-hidden-corner does: otherwise that rule forces position:relative
   back on and every box collapses into a stack. */
.lp-custom { display: block; padding: 0; }
.lp-label > .lp-abs { position: absolute; z-index: 1; overflow: hidden; display: flex; align-items: center; }
.lp-abs > * { width: 100%; max-width: 100%; }
/* Inside a box the BOX is the size — bcMm / bcWidthPct belong to the flow templates,
   where there is a leftover space to negotiate over. Here there isn't. */
.lp-abs .lp-bc { width: 100%; height: 100%; flex: 1 1 auto; max-height: 100%; }
/* A barcode standing on its end.
   The inner box is sized to the OUTER box with its axes swapped, then spun a quarter
   turn about its own centre — so after the rotation it lands back on exactly the
   rectangle the owner drew. Rotating without the swap would spin a wide image inside a
   tall box and leave the bars a fraction of the height available.
   The bars themselves are unchanged: same symbol, same quiet zone, read bottom-to-top. */
.lp-abs .lp-bc-vert { position: relative; width: 100%; height: 100%; }
.lp-abs .lp-bc-vert > svg { position: absolute; top: 50%; left: 50%;
  width: var(--bcv-len); height: var(--bcv-thick);
  transform: translate(-50%, -50%) rotate(-90deg); transform-origin: 50% 50%; display: block;
  -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.lp-abs .lp-logo { width: 100%; height: 100%; max-width: none; object-fit: contain; }
.lp-abs .lp-name { max-height: 100%; }
/* Everything except the name is ONE line in a box, because that is what the canvas
   draws — it fits the string and ellipsises the rest. Left to wrap, the preview put
   "8,000 IQD" on two lines and clipped the second while the PDF showed "8,00…". */
.lp-abs .lp-price, .lp-abs .lp-code, .lp-abs .lp-extra, .lp-abs .lp-hidden { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
/* The hidden staff code. Monospace + letter-spacing because it is read character by
   character — and that tracking comes from HC_TRACKING_EM, the same constant hcFont()
   hands the canvas, so all three renderers measure (and so truncate) one string.
   direction:ltr + unicode-bidi:isolate are NOT cosmetic: the print popup
   writes <html dir="rtl"> for an Arabic admin, so an Arabic marker in the template
   would otherwise reorder the whole code in HTML while the canvas (base direction
   ltr) printed it the other way round. */
.lp-hidden { font-size: var(--fs-hc, 1.8mm); font-family: ui-monospace, "Cascadia Code", "Courier New", monospace; color: var(--hc-c, #52525b); letter-spacing: ${HC_TRACKING_EM}em; line-height: 1; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 100%; direction: ltr; unicode-bidi: isolate; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
/* Out of flow. \`.lp-label > :not(.lp-wm)\` above forces position:relative on every
   direct child at specificity (0,2,0) — a bare \`.lp-hidden-corner\` would LOSE and
   silently drop back into normal flow in the preview and the print popup while the
   canvas still drew it in the corner. This selector matches that specificity and
   comes later in the sheet. */
.lp-label > .lp-hidden-corner, .lp-tag .lp-main > .lp-hidden-corner { position: absolute; z-index: 1; max-width: calc(100% - ${2 * HC_INSET_MM}mm); }
.lp-hidden-tl { top: ${HC_INSET_MM}mm; left: ${HC_INSET_MM}mm; }
.lp-hidden-tr { top: ${HC_INSET_MM}mm; right: ${HC_INSET_MM}mm; }
.lp-hidden-bl { bottom: ${HC_INSET_MM}mm; left: ${HC_INSET_MM}mm; }
.lp-hidden-br { bottom: ${HC_INSET_MM}mm; right: ${HC_INSET_MM}mm; }
`;

const SIZE_PRESETS = [
  { id: '40x30', w: 40, h: 30 }, { id: '50x30', w: 50, h: 30 }, { id: '50x25', w: 50, h: 25 },
  { id: '40x25', w: 40, h: 25 }, { id: '38x25', w: 38, h: 25 }, { id: '30x20', w: 30, h: 20 },
  { id: '60x40', w: 60, h: 40 }, { id: '100x50', w: 100, h: 50 },
];
const PREVIEW_CAP = 48;
/** CSS reference pixels per millimetre — what the designer converts pointer moves by. */
const PX_PER_MM = 96 / 25.4;
const BULK_MAX = 4000;
// Hard cap on labels in a single client-side PDF — each label is a full canvas +
// base64 PNG held in memory until save(), so an unbounded run can freeze/OOM the tab.
const PDF_MAX = 800;

// ---- A barcode source = one printable barcode of a product (primary / a unit / a variant). ----
interface BarcodeSource {
  key: string; sub: string; value: string; format: BarcodeFormat; price: string;
  // The RAW inputs the hidden-code builder needs. `price` above is already
  // formatted, grouped, localised and possibly currency-suffixed, so it is
  // unusable as a number — never build a code from it.
  /** item_units.id — the itemUnitId a bulk price resolves against (null = base unit). */
  unitId: string | null;
  rawPrice: string | null;
  rawCurrency: string | null;
  /**
   * This source's OWN cost, plumbed per-unit exactly like rawPrice: a carton has its
   * own purchase price, and printing the BASE unit's cost beside the CARTON's sale
   * price is a wrong number with nothing on the sticker to reveal it. Only the item
   * carries a purchase CURRENCY (item_units has no such column), so that stays shared.
   */
  rawCost: string | null;
  rawCostCurrency: string | null;
}
function sourcesOf(item: Item, locale: string, withCurrency = true): BarcodeSource[] {
  // withCurrency=false prints the bare number ("8,000" not "8,000 IQD") —
  // shelf stickers often don't need the code, and it frees label width.
  const money = (p: string | null | undefined, ccy: string | null | undefined): string => {
    if (!p || !ccy) return '';
    if (withCurrency) return formatMoney(p, { locale, currency: ccy });
    const dp = getCurrencyDecimals(ccy) ?? 2;
    // Latin digits pinned (like the hidden code above): this price is ink on a
    // sticker that shoppers and cashiers re-key, printed beside the barcode, and
    // the same saved design must come out identically for every admin.
    return formatNumber(Number(p), { locale, numberingSystem: 'latn' }, { minimumFractionDigits: 0, maximumFractionDigits: dp });
  };
  const basePrice = money(item.salePrice, item.saleCurrencyCode);
  const ccy = item.saleCurrencyCode;
  // The item's own cost — the fallback for every source, and the answer for all of
  // them except a packaging unit, which has a purchase price of its own.
  const cost = item.purchasePrice;
  const costCcy = item.purchaseCurrencyCode;
  const out: BarcodeSource[] = [];
  if (item.barcode) out.push({ key: 'primary', sub: '', value: item.barcode, format: item.barcodeFormat, price: basePrice, unitId: null, rawPrice: item.salePrice, rawCurrency: ccy, rawCost: cost, rawCostCurrency: costCcy });
  // The `?? item.salePrice` mirrors the `|| basePrice` above: a unit/variant with no
  // price of its own sells at the item's price, in the item's currency. `?? cost` is
  // the same rule for the buy side — a unit with no purchase price of its own costs
  // what the item costs.
  for (const u of item.units ?? []) if (u.barcode) out.push({ key: `unit:${u.id}`, sub: u.name, value: u.barcode, format: item.barcodeFormat, price: money(u.salePrice, ccy) || basePrice, unitId: u.id, rawPrice: u.salePrice ?? item.salePrice, rawCurrency: ccy, rawCost: u.purchasePrice ?? cost, rawCostCurrency: costCcy });
  // Variants are NOT a pricing dimension server-side (a bulk line is itemId +
  // itemUnitId only), so a variant label resolves the ITEM's list price — and
  // ItemVariantSchema has no purchasePrice at all, so the item's cost is its cost.
  for (const v of item.variants ?? []) if (v.barcode) out.push({ key: `variant:${v.id}`, sub: v.name, value: v.barcode, format: item.barcodeFormat, price: money(v.salePrice, ccy) || basePrice, unitId: null, rawPrice: v.salePrice ?? item.salePrice, rawCurrency: ccy, rawCost: cost, rawCostCurrency: costCcy });
  if (out.length === 0) out.push({ key: 'code', sub: '', value: item.code, format: item.barcodeFormat, price: basePrice, unitId: null, rawPrice: item.salePrice, rawCurrency: ccy, rawCost: cost, rawCostCurrency: costCcy });
  return out;
}

/** The ONE place a (product, barcode source) pair becomes a hidden-code context. */
function hcCtxOf(item: Item, src: BarcodeSource, sku: string): HcCtx {
  return {
    itemId: item.id, unitId: src.unitId,
    // From the SOURCE, never the item: on a carton label every other number here is
    // the carton's, and a base-unit cost printed beside a carton price reads as
    // headroom that isn't there.
    cost: src.rawCost, costCcy: src.rawCostCurrency,
    price: src.rawPrice, priceCcy: src.rawCurrency,
    sku, barcode: src.value,
  };
}

interface Row { item: Item; qty: number; selected: string[]; priceOverride?: string; codeOverride?: string; hiddenOverride?: string }

/** The largest text size on a label, in mm — big enough for a shelf price sign. */
const TEXT_MAX_MM = 40;

export function LabelsPage(): JSX.Element {
  const { t, i18n } = useTranslation('items');
  const layoutDir = useLayoutDirection();
  const { t: tc } = useTranslation();
  const canWrite = usePermission('items.write');
  const tenant = useAuthStore((s) => s.tenant);
  // /api/v1/pricing is NOT module-gated server-side (it only wants items.read, which
  // anyone on this page has), so this client gate is what makes the feature respect a
  // tenant who switched price lists off.
  const pricingOn = moduleEnabled(useEnabledModules(tenant?.id ?? null), 'pricing');
  // Decimals for a resolved line that carries no currency of its own.
  const baseCurrency = tenant?.baseCurrencyCode ?? 'USD';

  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [results, setResults] = useState<Item[]>([]);
  const [rows, setRows] = useState<Row[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [catId, setCatId] = useState<string | undefined>();
  const [brandId, setBrandId] = useState<string | undefined>();
  const [bulkBusy, setBulkBusy] = useState(false);
  // Shelf-label bookkeeping (LabelQueue): which barcodes of a queued product still
  // need a label and why, and a counter the queue re-reads on after a print.
  const [pendingByItem, setPendingByItem] = useState<Map<string, PendingLabel[]>>(() => new Map());
  const [queueVersion, setQueueVersion] = useState(0);
  const [pdfBusy, setPdfBusy] = useState(false);

  const [widthMm, setWidthMm] = useState(50);
  const [heightMm, setHeightMm] = useState(30);
  const [mode, setMode] = useState<'sheet' | 'roll' | 'custom'>('sheet');
  // Custom page size (mode 'custom') — tile labels on any sheet, not just A4.
  const [sheetW, setSheetW] = useState(100);
  const [sheetH, setSheetH] = useState(150);
  const [sheetMargin, setSheetMargin] = useState(5);
  const [showName, setShowName] = useState(true);
  const [showBarcode, setShowBarcode] = useState(true);
  /** Custom template only: stand the barcode on its end. */
  const [bcVertical, setBcVertical] = useState(false);
  const [showCode, setShowCode] = useState(true);
  const [showPrice, setShowPrice] = useState(true);
  const [border, setBorder] = useState(true);
  const [showLogo, setShowLogo] = useState(true);
  // Print the currency code with the price ("8,000 IQD") or just the number.
  const [showCurrency, setShowCurrency] = useState(false);
  // Text sizes in mm (drive preview + print via CSS vars, and the PDF canvas fonts).
  const [nameMm, setNameMm] = useState(2.7);
  // The price is the headline of a shelf sticker — big and heavy by default.
  const [priceMm, setPriceMm] = useState(5);
  const [codeMm, setCodeMm] = useState(2.3);
  // Layout template + its extras (the "tag" layout mirrors a shelf price-tag:
  // logo-left, big price, name on top, SKU at the bottom, barcode up the side).
  const [template, setTemplate] = useState<Template>('centered');
  const [divider, setDivider] = useState(true);
  // The side strip on/off. Kept as the same option (and preset key) it always was.
  const [showVertical, setShowVertical] = useState(true);
  const [priceAccent, setPriceAccent] = useState(false);
  // Accent colour (the number-only strip + optional price). Defaults to a DS red.
  const [accent, setAccent] = useState(ACCENT_DEFAULT);
  // Size of the strip's human-readable digits (was the size of the vertical number).
  const [vbcMm, setVbcMm] = useState(2.4);
  // ── Watermark: the store logo (or any text) faded in behind the label. Off by
  //    default so every saved design keeps printing exactly what it printed before. ──
  const [wmOn, setWmOn] = useState(false);
  const [wmSource, setWmSource] = useState<WmSource>('logo');
  const [wmText, setWmText] = useState('');
  const [wmOpacity, setWmOpacity] = useState(0.12);
  const [wmScale, setWmScale] = useState(70); // % of the label's short side
  const [wmRotate, setWmRotate] = useState(0);
  const [wmColor, setWmColor] = useState(INK);
  const [wmPos, setWmPos] = useState<WmPos>('center');
  // Off ⇒ both barcodes keep an opaque backing, so a dithered wash can never land in
  // the bars or the quiet zone (1-bit thermal printers have no greyscale).
  const [wmBehindBarcode, setWmBehindBarcode] = useState(false);
  // ── The hidden staff price code. Design state (saved in presets) — all of it
  //    inert until hcOn, which is off by default. ──
  const [hcOn, setHcOn] = useState(false);
  const [hcTemplate, setHcTemplate] = useState('');   // e.g. 'xx{list:المكاتب}xx{list:قرطاسية}'
  const [hcCipherOn, setHcCipherOn] = useState(false);
  const [hcKey, setHcKey] = useState('');             // 10 characters: digit d → key[d]
  const [hcMissing, setHcMissing] = useState(HC_MISSING_DEFAULT);
  const [hcBaseFallback, setHcBaseFallback] = useState(false);
  const [hcPos, setHcPos] = useState<HcPos>('bottom');
  const [hcCorner, setHcCorner] = useState<HcCorner>('br');
  const [hcMm, setHcMm] = useState(1.8);              // smaller than the SKU: it should read as noise
  const [hcColor, setHcColor] = useState(MUTED);
  // Runtime state — DATA, not design, so presets never touch it.
  const [hcLists, setHcLists] = useState<PriceList[] | null>(null);
  const [hcPrices, setHcPrices] = useState<Map<string, BulkPriceLine>>(() => new Map());
  // Lists answering with a percent adjustment (from each resolve-bulk basis).
  const [hcAdjusted, setHcAdjusted] = useState<Set<string>>(() => new Set());
  const [hcBusy, setHcBusy] = useState(false);
  const [hcFailed, setHcFailed] = useState<string[]>([]);   // list ids that wouldn't resolve
  const [hcNonce, setHcNonce] = useState(0);                // the "Refresh prices" button
  // The accumulating store lives in a ref (adding one product costs one request
  // line, not a full refetch); `hcPrices` is the published snapshot.
  const hcCacheRef = useRef(new Map<string, BulkPriceLine>());
  const hcInputRef = useRef<HTMLInputElement>(null);
  // The failing-list set the user has already been warned about at print time, so a
  // second click can mean "print it with the markers" instead of a dead end.
  const hcFailAckRef = useRef('');
  // ── The price-tag side strip: a real rotated barcode by default. ──
  const [vbcMode, setVbcMode] = useState<VbcMode>('barcode');
  const [vbcSide, setVbcSide] = useState<VbcSide>('left');
  const [vbcWidthMm, setVbcWidthMm] = useState(9);
  const [vbcDigits, setVbcDigits] = useState(true);
  const [vbcRotate, setVbcRotate] = useState(90);
  const [vbcColor, setVbcColor] = useState(BARCODE_INK);
  // Per-element colours, font, logo size, casing + free text — full styling control.
  const [nameColor, setNameColor] = useState(INK);
  const [priceColor, setPriceColor] = useState(INK);
  const [codeColor, setCodeColor] = useState(MUTED);
  const [fontKey, setFontKey] = useState<keyof typeof FONTS>('default');
  const [logoMm, setLogoMm] = useState(4.2);
  const [upper, setUpper] = useState(false);
  const [extraText, setExtraText] = useState('');
  const [extraMm, setExtraMm] = useState(2);
  const [pricePrefix, setPricePrefix] = useState('');
  const [priceSuffix, setPriceSuffix] = useState('');
  // ── Layout / spacing. Every one of these defaults to "Auto" (AUTO_MM, or 0 lines),
  //    which resolves to exactly what the template has always drawn — so a design
  //    saved before today prints the same label it printed yesterday. ──
  const [align, setAlign] = useState<Align>('center');
  // Per element, 'auto' = follow `align`. Every older preset lands here, so the single
  // Text-alignment control keeps moving everything until one is deliberately changed.
  const [nameAlign, setNameAlign] = useState<ElAlign>('auto');
  const [priceAlign, setPriceAlign] = useState<ElAlign>('auto');
  const [codeAlign, setCodeAlign] = useState<ElAlign>('auto');
  const [extraAlign, setExtraAlign] = useState<ElAlign>('auto');
  // The custom template's layout, plus which box the designer has selected.
  const [custom, setCustom] = useState<CustomLayout>(() => defaultLayout(50, 30));
  /**
   * The label size the layout in `custom` was BUILT for, or null once the owner has
   * placed something themselves.
   *
   * defaultLayout is written in fractions of the label precisely so it suits any size,
   * but it is frozen into millimetres the moment it is stored — so a layout seeded at
   * 50×30 and then shown on a 100×50 label is not "the default", it is every element
   * huddled in the top-left quarter with the rest of the label blank. Re-seeding while
   * the layout is still untouched is what makes picking Custom open on something
   * deliberate at ANY size. Once a box has been moved the layout is the owner's, and
   * resizing the label only clamps it.
   */
  const customFor = useRef<string | null>('50x30');
  const [selEl, setSelEl] = useState<ElId>('name');
  const [nameLines, setNameLines] = useState(0);
  const [bcMm, setBcMm] = useState(AUTO_MM);
  // 'item' = each product's own symbology, which is what this page always printed.
  const [bcFormat, setBcFormat] = useState<BcFormatOpt>('item');
  const [bcWidthPct, setBcWidthPct] = useState(100);
  const [vbcLenPct, setVbcLenPct] = useState(100);
  const [gapMm, setGapMm] = useState(AUTO_MM);
  const [padXMm, setPadXMm] = useState(AUTO_MM);
  const [padYMm, setPadYMm] = useState(AUTO_MM);
  const [cellGapXMm, setCellGapXMm] = useState(CELL_GAP_DEFAULT);
  const [cellGapYMm, setCellGapYMm] = useState(CELL_GAP_DEFAULT);
  const [dividerMm, setDividerMm] = useState(DIV_MM_DEFAULT);
  const [dividerFull, setDividerFull] = useState(false);
  // Preview-only zoom (never affects the printed size).
  const [zoom, setZoom] = useState(1);
  // The store logo (public storefront media URL — absolute, so the print popup
  // and the PDF canvas can both load it). Null ⇒ tenant has no logo; the toggle hides.
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [logoImg, setLogoImg] = useState<HTMLImageElement | null>(null);
  // The URL can exist and still never load (deleted attachment, unpublished storefront).
  // Tracked so the PDF drops the watermark like the preview does, instead of refusing to
  // export forever on a "still loading" that will never become true.
  const [logoFailed, setLogoFailed] = useState(false);
  // Saved label designs (localStorage) — configure once, reuse forever.
  const [presetName, setPresetName] = useState('');
  const [presets, setPresets] = useState<Record<string, Record<string, unknown>>>(() => {
    try { return JSON.parse(localStorage.getItem('label.presets.v1') || '{}') as Record<string, Record<string, unknown>>; } catch { return {}; }
  });

  useEffect(() => {
    let on = true;
    void api().get<CmsSettings>('/cms/settings')
      .then((r) => {
        const url = r.data.logoAttachmentId && r.data.slug
          ? assetUrl(`/api/v1/storefront/${r.data.slug}/media/${r.data.logoAttachmentId}`)
          : null;
        if (!on || !url) return;
        setLogoUrl(url);
        // assetUrl() can point at another origin, and drawing a cross-origin image
        // taints the canvas → toDataURL() throws and the PDF dies. Ask for CORS, and
        // fall back to a plain load (preview + print still work) if it isn't granted.
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => { if (on) setLogoImg(img); };
        img.onerror = () => {
          const plain = new Image();
          plain.onload = () => { if (on) setLogoImg(plain); };
          // Second failure = the URL itself is dead, not a CORS refusal.
          plain.onerror = () => { if (on) setLogoFailed(true); };
          plain.src = url;
        };
        img.src = url;
      })
      .catch(() => undefined);
    return () => { on = false; };
  }, []);

  const dataLocale = i18n.language.split('-')[0] ?? 'en';

  useEffect(() => { const id = setTimeout(() => setDebounced(search.trim()), 300); return () => clearTimeout(id); }, [search]);
  useEffect(() => {
    if (!debounced) { setResults([]); return; }
    let on = true;
    void api().get<{ items: Item[] }>('/items', { params: { q: debounced, locale: dataLocale, limit: 25 } })
      .then((r) => { if (on) setResults(r.data.items); }).catch(() => { if (on) setResults([]); });
    return () => { on = false; };
  }, [debounced, dataLocale]);
  useEffect(() => {
    void api().get<{ items: Category[] }>('/categories', { params: { locale: dataLocale } }).then((r) => setCategories(r.data.items)).catch(() => setCategories([]));
    void api().get<{ items: Brand[] }>('/brands').then((r) => setBrands(r.data.items)).catch(() => setBrands([]));
  }, [dataLocale]);

  const addItems = (items: Item[]): number => {
    let added = 0;
    setRows((rs) => {
      const have = new Set(rs.map((r) => r.item.id));
      const more = items.filter((it) => !have.has(it.id)).map((it) => { added += 1; return { item: it, qty: 1, selected: [sourcesOf(it, i18n.language)[0]!.key] }; });
      return more.length ? [...rs, ...more] : rs;
    });
    return added;
  };
  const addOne = (item: Item): void => { addItems([item]); setSearch(''); setResults([]); };
  // From "Needs a label": select exactly the barcodes that need one — a repriced
  // carton queues the carton's label, not the single piece's. A product already in
  // the list gains those barcodes instead of being added twice.
  const addPending = (items: Item[], sources: Record<string, PendingLabel[]>): number => {
    const have = new Set(rows.map((r) => r.item.id));
    const added = items.filter((it) => !have.has(it.id)).length;
    setPendingByItem((m) => { const n = new Map(m); for (const it of items) n.set(it.id, sources[it.id] ?? []); return n; });
    setRows((rs) => {
      const out = [...rs];
      for (const it of items) {
        const srcs = sourcesOf(it, i18n.language);
        const keys = new Set(srcs.map((s) => s.key));
        const want = (sources[it.id] ?? []).map((l) => l.key).filter((k) => keys.has(k));
        const sel = want.length ? want : [srcs[0]!.key];
        const at = out.findIndex((r) => r.item.id === it.id);
        if (at >= 0) out[at] = { ...out[at]!, item: it, selected: [...new Set([...out[at]!.selected, ...sel])] };
        else out.push({ item: it, qty: 1, selected: sel });
      }
      return out;
    });
    return added;
  };
  const setQty = (id: string, qty: number): void => setRows((rs) => rs.map((r) => (r.item.id === id ? { ...r, qty: Math.max(1, Math.min(999, qty)) } : r)));
  const setPriceOverride = (id: string, priceOverride: string): void => setRows((rs) => rs.map((r) => (r.item.id === id ? { ...r, priceOverride } : r)));
  const setCodeOverride = (id: string, codeOverride: string): void => setRows((rs) => rs.map((r) => (r.item.id === id ? { ...r, codeOverride } : r)));
  const setHiddenOverride = (id: string, hiddenOverride: string): void => setRows((rs) => rs.map((r) => (r.item.id === id ? { ...r, hiddenOverride } : r)));

  // ---- Saved label designs (the whole styling config, not the product selection) ----
  const currentConfig = (): Record<string, unknown> => ({
    template, widthMm, heightMm, mode, sheetW, sheetH, sheetMargin, showName, showBarcode, bcVertical, showCode, showPrice, showLogo, showCurrency,
    showVertical, divider, priceAccent, border, upper, accent, nameColor, priceColor, codeColor, fontKey,
    logoMm, nameMm, priceMm, codeMm, vbcMm, extraMm, extraText, pricePrefix, priceSuffix,
    wmOn, wmSource, wmText, wmOpacity, wmScale, wmRotate, wmColor, wmPos, wmBehindBarcode,
    vbcMode, vbcSide, vbcWidthMm, vbcDigits, vbcRotate, vbcColor, vbcLenPct,
    // The hidden code is design, so all of it travels with a saved design. The
    // fetched prices and the list catalogue deliberately do not.
    hcOn, hcTemplate, hcCipherOn, hcKey, hcMissing, hcBaseFallback, hcPos, hcCorner, hcMm, hcColor,
    align, nameLines, bcMm, bcWidthPct, gapMm, padXMm, padYMm, cellGapXMm, cellGapYMm, dividerMm, dividerFull, bcFormat,
    nameAlign, priceAlign, codeAlign, extraAlign, custom,
  });
  const applyConfig = (c: Record<string, unknown>): void => {
    const b = (v: unknown, d: boolean): boolean => (typeof v === 'boolean' ? v : d);
    const n = (v: unknown, d: number): number => (typeof v === 'number' ? v : d);
    const s = (v: unknown, d: string): string => (typeof v === 'string' ? v : d);
    // A preset is hand-editable JSON in localStorage, and may have been written by an
    // older build — so enums are whitelisted and the new ranges are clamped, not cast.
    const e = <T extends string>(v: unknown, allowed: readonly T[], d: T): T =>
      (typeof v === 'string' && (allowed as readonly string[]).includes(v) ? (v as T) : d);
    const nc = (v: unknown, d: number, lo: number, hi: number): number =>
      Math.min(hi, Math.max(lo, typeof v === 'number' && Number.isFinite(v) ? v : d));
    // These three were cast straight from the stored string, which contradicted the
    // comment above and let a stale preset (say template:'grid' from a build that
    // never shipped) leave the page in a state no control can select or undo.
    setTemplate(e(c.template, ['centered', 'tag', 'custom'] as const, 'centered')); setWidthMm(n(c.widthMm, 50)); setHeightMm(n(c.heightMm, 30));
    setMode(e(c.mode, ['sheet', 'roll', 'custom'] as const, 'sheet'));
    setSheetW(n(c.sheetW, 100)); setSheetH(n(c.sheetH, 150)); setSheetMargin(n(c.sheetMargin, 5));
    setShowName(b(c.showName, true)); setShowBarcode(b(c.showBarcode, true)); setShowCode(b(c.showCode, true)); setShowPrice(b(c.showPrice, true));
    // Defaults FALSE, not true: a layout saved before this existed was drawn with a
    // horizontal barcode, and must reopen that way.
    setBcVertical(b(c.bcVertical, false));
    setShowLogo(b(c.showLogo, true)); setShowCurrency(b(c.showCurrency, false)); setShowVertical(b(c.showVertical, true)); setDivider(b(c.divider, true));
    setPriceAccent(b(c.priceAccent, false)); setBorder(b(c.border, true)); setUpper(b(c.upper, false));
    setAccent(s(c.accent, ACCENT_DEFAULT)); setNameColor(s(c.nameColor, INK)); setPriceColor(s(c.priceColor, INK)); setCodeColor(s(c.codeColor, MUTED));
    setFontKey(e(c.fontKey, Object.keys(FONTS), 'default')); setLogoMm(n(c.logoMm, 4.2));
    // vbcMm used to be clamped 1.5–8 by the generic size stepper; as a digit size
    // anything over 5mm is nonsense, so old presets get pulled back into range.
    setNameMm(n(c.nameMm, 2.7)); setPriceMm(n(c.priceMm, 5)); setCodeMm(n(c.codeMm, 2.3)); setVbcMm(nc(c.vbcMm, 2.4, 1.2, 5)); setExtraMm(n(c.extraMm, 2));
    setExtraText(s(c.extraText, '')); setPricePrefix(s(c.pricePrefix, '')); setPriceSuffix(s(c.priceSuffix, ''));
    // Absent in every pre-watermark preset ⇒ wmOn:false ⇒ those designs render exactly
    // as they always did. vbcMode defaults to 'barcode' on purpose: an old strip becomes
    // a real scannable one, which is the whole point — 'Number only' is the way back.
    setWmOn(b(c.wmOn, false)); setWmSource(e(c.wmSource, ['logo', 'text'] as const, 'logo')); setWmText(s(c.wmText, ''));
    setWmOpacity(nc(c.wmOpacity, 0.12, 0.03, 0.6)); setWmScale(nc(c.wmScale, 70, 10, 150)); setWmRotate(nc(c.wmRotate, 0, -90, 90));
    setWmColor(s(c.wmColor, INK)); setWmPos(e(c.wmPos, ['center', 'tl', 'tr', 'bl', 'br'] as const, 'center')); setWmBehindBarcode(b(c.wmBehindBarcode, false));
    setVbcMode(e(c.vbcMode, ['barcode', 'number'] as const, 'barcode')); setVbcSide(e(c.vbcSide, ['left', 'right'] as const, 'left'));
    setVbcWidthMm(nc(c.vbcWidthMm, 9, 5, 20)); setVbcDigits(b(c.vbcDigits, true));
    setVbcRotate(nc(c.vbcRotate, 90, 90, 270) === 270 ? 270 : 90); setVbcColor(s(c.vbcColor, BARCODE_INK));
    // Absent in every pre-hidden-code preset ⇒ hcOn:false ⇒ those designs print
    // exactly what they always printed: no element in any of the three renderers.
    setHcOn(b(c.hcOn, false));
    setHcTemplate(s(c.hcTemplate, '').slice(0, HC_TPL_MAX));
    setHcCipherOn(b(c.hcCipherOn, false)); setHcKey(s(c.hcKey, '').slice(0, 40));
    setHcMissing(s(c.hcMissing, HC_MISSING_DEFAULT).slice(0, 3));
    setHcBaseFallback(b(c.hcBaseFallback, false));
    setHcPos(e(c.hcPos, ['top', 'bottom', 'corner'] as const, 'bottom'));
    setHcCorner(e(c.hcCorner, ['tl', 'tr', 'bl', 'br'] as const, 'br'));
    setHcMm(nc(c.hcMm, 1.8, 1.2, 5)); setHcColor(s(c.hcColor, MUTED));
    // Absent in every pre-layout preset ⇒ centre / Auto / 100% ⇒ those designs resolve
    // to the very literals this file used before the options existed (see geometry()).
    setAlign(e(c.align, ['left', 'center', 'right'] as const, 'center'));
    setNameLines(Math.round(nc(c.nameLines, 0, 0, 3)));
    setBcMm(nc(c.bcMm, AUTO_MM, AUTO_MM, 60)); setBcWidthPct(nc(c.bcWidthPct, 100, 20, 100));
    setVbcLenPct(nc(c.vbcLenPct, 100, 20, 100));
    setGapMm(nc(c.gapMm, AUTO_MM, AUTO_MM, 8));
    setPadXMm(nc(c.padXMm, AUTO_MM, AUTO_MM, 15)); setPadYMm(nc(c.padYMm, AUTO_MM, AUTO_MM, 15));
    setCellGapXMm(nc(c.cellGapXMm, CELL_GAP_DEFAULT, 0, 20)); setCellGapYMm(nc(c.cellGapYMm, CELL_GAP_DEFAULT, 0, 20));
    setDividerMm(nc(c.dividerMm, DIV_MM_DEFAULT, 0.1, 2)); setDividerFull(b(c.dividerFull, false));
    // Absent in every older preset ⇒ 'item' ⇒ each product's own symbology, exactly
    // as that design printed before this option existed.
    setBcFormat(e(c.bcFormat, ['item', ...BC_FORMATS, 'qr'] as const, 'item'));
    setNameAlign(e(c.nameAlign, EL_ALIGNS, 'auto')); setPriceAlign(e(c.priceAlign, EL_ALIGNS, 'auto'));
    setCodeAlign(e(c.codeAlign, EL_ALIGNS, 'auto')); setExtraAlign(e(c.extraAlign, EL_ALIGNS, 'auto'));
    // A preset is hand-editable JSON, so the layout is rebuilt field by field: a missing
    // element, a string where a number should be or a NaN would otherwise reach the
    // renderers as `undefined.x` and blank the label with no way to recover from the UI.
    // A saved design carries millimetres somebody chose, so it is theirs from the moment
    // it loads — the size-change auto-fit must not overwrite it on the way in. But only
    // if it actually carries a layout: a preset saved before the custom template existed
    // has no `custom` key, and disarming the auto-fit for it would strand every later
    // label size on the 50×30 default with no way back except Reset.
    customFor.current = c.custom && typeof c.custom === 'object'
      ? null
      : `${n(c.widthMm, 50)}x${n(c.heightMm, 30)}`;
    setCustom(() => {
      const fallback = defaultLayout(n(c.widthMm, 50), n(c.heightMm, 30));
      const src = c.custom && typeof c.custom === 'object' ? c.custom as Record<string, unknown> : {};
      const out = {} as CustomLayout;
      for (const id of EL_IDS) {
        const raw = src[id] && typeof src[id] === 'object' ? src[id] as Record<string, unknown> : {};
        const d = fallback[id];
        out[id] = { x: n(raw.x, d.x), y: n(raw.y, d.y), w: n(raw.w, d.w), h: n(raw.h, d.h) };
      }
      return out;
    });
  };
  const persistPresets = (next: Record<string, Record<string, unknown>>): void => {
    setPresets(next);
    try { localStorage.setItem('label.presets.v1', JSON.stringify(next)); } catch { /* storage full / private mode */ }
  };
  const savePreset = (): void => {
    const name = presetName.trim();
    if (!name) return;
    persistPresets({ ...presets, [name]: currentConfig() });
    setPresetName('');
    toast.success(t('labels.presetSaved', { defaultValue: 'Saved design “{{name}}”', name }));
  };
  const deletePreset = (name: string): void => { const next = { ...presets }; delete next[name]; persistPresets(next); };
  const removeRow = (id: string): void => setRows((rs) => rs.filter((r) => r.item.id !== id));
  const toggleSource = (id: string, key: string): void => setRows((rs) => rs.map((r) => {
    if (r.item.id !== id) return r;
    const has = r.selected.includes(key);
    const next = has ? r.selected.filter((k) => k !== key) : [...r.selected, key];
    return { ...r, selected: next.length ? next : r.selected }; // keep at least one
  }));

  // Record every label in the print list as printed: the barcode and price each
  // one carries now. Undo puts back what "Needs a label" said before.
  const markRowsPrinted = async (): Promise<void> => {
    const printed: LabelMark[] = [];
    const was: Array<{ itemId: string; label: PendingLabel }> = [];
    for (const r of rows) {
      const srcs = sourcesOf(r.item, i18n.language);
      const chosen = srcs.filter((s) => r.selected.includes(s.key));
      for (const s of chosen.length ? chosen : [srcs[0]!]) {
        printed.push({ itemId: r.item.id, key: s.key, barcode: s.value, price: s.rawPrice, currencyCode: s.rawCurrency });
        const p = pendingByItem.get(r.item.id)?.find((l) => l.key === s.key);
        if (p) was.push({ itemId: r.item.id, label: p });
      }
    }
    if (!printed.length) return;
    try {
      await postLabelMarks(printed);
      const done = new Set(printed.map((m) => `${m.itemId}|${m.key}`));
      setPendingByItem((m) => { const n = new Map(m); for (const [id, ls] of n) n.set(id, ls.filter((l) => !done.has(`${id}|${l.key}`))); return n; });
      setQueueVersion((v) => v + 1);
      const undo = undoOf(was);
      toast.success(t('labels.queue.marked', { defaultValue: '{{n}} label(s) marked as printed', n: printed.length }), was.length ? {
        action: {
          label: t('labels.queue.undo', { defaultValue: 'Undo' }),
          onClick: () => {
            void postLabelMarks(undo.printed, undo.unprinted).then(() => {
              setPendingByItem((m) => { const n = new Map(m); for (const e of was) n.set(e.itemId, [...(n.get(e.itemId) ?? []).filter((l) => l.key !== e.label.key), e.label]); return n; });
              setQueueVersion((v) => v + 1);
            }).catch(() => toast.error(tc('errors:server')));
          },
        },
      } : undefined);
    } catch { toast.error(tc('errors:server')); }
  };
  // Asked, not assumed: the print window can still be cancelled, and a label the
  // shelf never got must stay on "Needs a label".
  const askMarkPrinted = (): void => {
    if (rows.length === 0) return;
    toast(t('labels.queue.printedAsk', { defaultValue: 'Did the labels print? Mark them printed so they leave “Needs a label”.' }), {
      duration: 60_000,
      action: { label: t('labels.queue.markPrinted', { defaultValue: 'Mark as printed' }), onClick: () => { void markRowsPrinted(); } },
    });
  };

  // Bulk add every product in a category / brand (paginated over the whole catalog).
  // A category now brings its SUBCATEGORIES with it — the server filters by the
  // whole subtree — so picking a department queues the department's labels.
  const addAll = async (params: { categoryId?: string; brandId?: string }, label: string): Promise<void> => {
    setBulkBusy(true);
    try {
      const collected: Item[] = [];
      let total = 0;
      let capped = false;
      for (let offset = 0; offset < BULK_MAX; offset += 500) {
        const r = await api().get<{ items: Item[]; total?: number }>('/items', { params: { ...params, locale: dataLocale, limit: 500, offset } });
        if (offset === 0) total = Number(r.data.total ?? r.data.items.length);
        collected.push(...r.data.items);
        if (r.data.items.length < 500) break;
        // A full final page as we reach the cap means the category/brand has more
        // than BULK_MAX items — we stop, but tell the user rather than silently truncate.
        if (offset + 500 >= BULK_MAX) capped = true;
      }
      const added = addItems(collected);
      if (capped && total > collected.length) {
        toast.warning(t('labels.addedCapped', { defaultValue: 'Added first {{n}} of {{total}} from {{label}} — narrow the selection or print in batches.', n: collected.length, total, label }));
      } else if (collected.length === 0) {
        // "Nothing there" vs "you already have them all" — one toast covered both,
        // which is why an empty category looked like a broken button.
        toast.info(t('labels.addedEmpty', { defaultValue: 'No products in {{label}}.', label }));
      } else if (added === 0) {
        toast.info(t('labels.addedNone', { defaultValue: 'No new products to add from {{label}}.', label }));
      } else {
        toast.success(t('labels.added', { defaultValue: 'Added {{n}} product(s) from {{label}}', n: added, label }));
      }
    } catch { toast.error(tc('errors:server')); } finally { setBulkBusy(false); }
  };

  // ── Hidden code: the ONE async input on this page. It lands in the `specs` memo
  //    and never crosses into labelHtml / buildSheetHtml / labelCanvas. ──
  const hcKeyChars = useMemo(() => {
    // Code points, so an Arabic letter counts as ONE character. Exactly 10 or the
    // cipher does not apply at all: a PARTIAL cipher is strictly worse than none —
    // it leaks the unmapped digits AND looks encrypted, so nobody notices.
    // Whitespace is rejected: an invisible key character can't be read off a sticker.
    const cs = Array.from(hcKey);
    return hcCipherOn && cs.length === 10 && !cs.some((c) => /\s/.test(c)) ? cs : null;
  }, [hcCipherOn, hcKey]);
  // "Scrambling was asked for and is NOT being applied" — a pasted key with a trailing
  // space looks perfect in the input, and the old behaviour was to print the raw cost
  // in plain digits on every sticker. Treated as a BROKEN state everywhere: the values
  // are dropped from the code (hcEnv.suppress) and Print / Export PDF refuse to run.
  // Gated on the template actually naming a VALUE token, not merely being non-empty: a
  // template of plain letters has no digits to scramble, so refusing to print it would
  // be a cipher error about a code that was never going to leak anything.
  // A fresh RegExp because HC_TOKEN_RE is /g — .test() on a shared global regex carries
  // lastIndex between calls and returns alternating answers for the same input.
  const hcKeyBroken = hcOn && hcCipherOn && !hcKeyChars && new RegExp(HC_TOKEN_RE.source).test(hcTemplate);
  const hcIndex = useMemo(() => {
    const byId = new Map<string, string>();
    const byName = new Map<string, string>();
    const dupes = new Set<string>();
    // ACTIVE lists only. /pricing/lists returns inactive ones too, and a deactivated
    // list answers resolve-bulk with the item's BASE price on every line — so it
    // can't be honoured as a list at all; the template's ref renders "?" instead.
    const active = (hcLists ?? []).filter((l) => l.isActive);
    for (const l of active) {
      byId.set(l.id.toLowerCase(), l.id);
      const n = l.name.trim().toLowerCase();
      if (byName.has(n)) dupes.add(n);
      byName.set(n, l.id);
    }
    // A token carries the NAME (so a saved design is self-documenting and hand-
    // editable), but the id is tried FIRST so a name that happens to look like a
    // uuid can never shadow a real list — and pasting an id is the escape hatch
    // that survives a rename.
    const listIdFor = (ref: string): string | null => {
      const k = ref.trim().toLowerCase();
      return byId.get(k) ?? byName.get(k) ?? null;
    };
    return { listIdFor, dupes, active };
  }, [hcLists]);
  const hcRefs = useMemo(() => (hcOn ? hcListRefs(hcTemplate) : []), [hcOn, hcTemplate]);
  // Refs that name no active list — a template error worth saying out loud, since
  // the code itself can only show a "?".
  const hcUnknownRefs = useMemo(
    () => (hcLists === null ? [] : [...new Set(hcRefs)].filter((r) => hcIndex.listIdFor(r) === null)),
    [hcRefs, hcIndex, hcLists],
  );
  const hcEnv = useMemo<HcEnv>(() => ({
    listIdFor: hcIndex.listIdFor, prices: hcPrices, missing: hcMissing,
    baseFallback: hcBaseFallback, key: hcKeyChars, baseCurrency, suppress: hcKeyBroken,
    adjusted: hcAdjusted,
  }), [hcIndex, hcPrices, hcMissing, hcBaseFallback, hcKeyChars, baseCurrency, hcKeyBroken, hcAdjusted]);
  // Derived from the RESOLVED ids, so a half-typed "xx{list:" moves nothing and no
  // debounce is needed: the dep only changes when a complete, valid token appears.
  const hcWantedKey = useMemo(
    () => [...new Set(hcRefs.map((r) => hcIndex.listIdFor(r)).filter((x): x is string => x !== null))].join(','),
    [hcRefs, hcIndex],
  );
  // Every distinct (item, unit) pair a label will actually print — sorted, so
  // re-ordering or re-typing a quantity can never trigger a refetch.
  const hcPairsKey = useMemo(() => {
    if (!hcOn) return '';
    const set = new Set<string>();
    for (const r of rows) {
      const srcs = sourcesOf(r.item, i18n.language, showCurrency);
      const chosen = srcs.filter((s) => r.selected.includes(s.key));
      for (const s of chosen.length ? chosen : [srcs[0]!]) set.add(`${r.item.id}|${s.unitId ?? ''}`);
    }
    return [...set].sort().join(',');
  }, [hcOn, rows, i18n.language, showCurrency]);

  useEffect(() => {
    if (!hcOn || !pricingOn || hcLists !== null) return;
    let on = true;
    // [] on failure, never a stuck null: "no lists" is an answer the UI can print.
    void listPriceLists().then((ls) => { if (on) setHcLists(ls); }).catch(() => { if (on) setHcLists([]); });
    return () => { on = false; };
  }, [hcOn, pricingOn, hcLists]);

  useEffect(() => {
    if (!hcOn || !pricingOn || !hcWantedKey || !hcPairsKey) {
      setHcFailed((f) => (f.length ? [] : f));
      return;
    }
    let active = true;
    setHcBusy(true);
    void (async () => {
      const cache = hcCacheRef.current;
      const pairs = hcPairsKey.split(',').map((p) => {
        const [itemId, unitId] = p.split('|');
        return { itemId: itemId!, unitId: unitId || null };
      });
      const failed: string[] = [];
      // Rebuilt from scratch each settle, so a list that stops being percent-based
      // stops being treated as one — a stale entry here silently blanks carton codes.
      const adjusted = new Set<string>();
      // Lists sequentially, chunks within a list in parallel: 3 lists × 4000 products
      // is 24 requests, and firing all 24 at once would starve the rest of the page.
      for (const listId of hcWantedKey.split(',')) {
        const todo = pairs.filter((p) => !cache.has(hcPriceKey(listId, p.itemId, p.unitId)));
        if (todo.length === 0) continue;           // adding one product costs one line
        const chunks: typeof todo[] = [];
        for (let i = 0; i < todo.length; i += HC_BULK_CHUNK) chunks.push(todo.slice(i, i + HC_BULK_CHUNK));
        try {
          const answers = await Promise.all(chunks.map((c) => resolvePricesBulk({
            priceListId: listId,
            // qty omitted ⇒ 1: a shelf sticker is the single-unit price, so quantity
            // breaks above 1 never apply.
            lines: c.map((p) => ({ itemId: p.itemId, itemUnitId: p.unitId })),
          })));
          if (!active) return;
          // A deactivated or deleted list still answers 200 — with the item's BASE
          // price on every line. A null basis.priceListId is the only honest signal
          // that this token can't be priced at all.
          if (answers[0]?.basis.priceListId !== listId) { failed.push(listId); continue; }
          // Remember whether this list prices by a PERCENT off the base: the server
          // applies that to base-unit lines only, which is what makes the raw-price
          // fallback dishonest for a carton (see buildHiddenCode).
          if (answers[0].basis.adjustmentType === 'percent') adjusted.add(listId);
          for (const a of answers) for (const l of a.items) cache.set(hcPriceKey(listId, l.itemId, l.itemUnitId), l);
        } catch {
          // 403 / offline / server down — record it and keep going; the page must
          // never break because a price list wouldn't load.
          if (!active) return;
          failed.push(listId);
        }
      }
      if (!active) return;
      // Bounded like svgCache/canvasCache, but EVICTED rather than emptied, and after
      // the refill so what gets published is never a half-filled map. Dropping the
      // whole cache meant a working set over the cap disabled it for good: the size
      // never came back under, so every later row change wiped it and refetched every
      // list from scratch (~42 requests) with Print locked behind hcLoading throughout.
      // ONE atomic publish, not per-list: a preview that filled in list by list would
      // let someone hit Print mid-way and get a roll of half-coded stickers. A NEW Map
      // every time on purpose — mutating the ref'd one never changes identity, so the
      // `specs` memo would never re-run and the preview would show markers forever.
      // PUBLISH FIRST, THEN EVICT. Trimming before the copy would drop rows we had just
      // fetched for THIS render, and those labels would silently print the missing
      // marker instead of a price — the published snapshot must always be complete.
      setHcPrices(new Map(cache));
      // Identity-stable: a Set rebuilt every settle would re-run every memo downstream.
      setHcAdjusted((prev) => (prev.size === adjusted.size && [...adjusted].every((x) => prev.has(x)) ? prev : adjusted));
      // Bounded like svgCache/canvasCache, but EVICTED rather than emptied. Dropping the
      // whole cache meant a working set over the cap disabled it for good: the size
      // never came back under, so every later row change wiped it and refetched every
      // list from scratch (~42 requests) with Print locked behind hcLoading throughout.
      // A Map iterates in insertion order, so the first key out is the oldest fetched.
      while (cache.size > HC_CACHE_MAX) {
        const oldest = cache.keys().next().value;
        if (oldest === undefined) break;
        cache.delete(oldest);
      }
      // Identity kept when the failing set is unchanged: `hcFailed` is what the
      // warning toast below fires on, so a new array on every settled fetch would
      // re-toast the same broken list on every keystroke that adds a product.
      setHcFailed((prev) => (prev.length === failed.length && prev.every((id, i) => id === failed[i]) ? prev : failed));
      setHcBusy(false);
    })();
    return () => { active = false; setHcBusy(false); };
  }, [hcOn, pricingOn, hcWantedKey, hcPairsKey, hcNonce]);

  // Only while a fetch is genuinely in flight. "Pricing off", a failed list and an
  // unknown ref are all FINAL answers (the markers print), so they never block.
  const hcLoading = hcOn && pricingOn && hcRefs.length > 0 && (hcLists === null || hcBusy);
  // The lists that wouldn't resolve, by name. ONE derivation, used by the toast below
  // and by the panel's own warning line, so the two can never name different lists.
  const hcFailedNames = hcFailed.map((id) => hcIndex.active.find((l) => l.id === id)?.name ?? id);
  // A failed list is a final answer, so it can't block the page — but every slot it
  // feeds prints the "no price" marker, and the only warning lived inside a <details>
  // that is collapsed by default. That is how an 800-sticker roll of "xx-xx-" gets out
  // with nothing on screen having said so: put it where the user is actually looking.
  useEffect(() => {
    // Prices resolved: forget the acknowledgement too, so a LATER failure of the same
    // list spends a click of its own rather than riding an old "print it anyway".
    if (hcFailed.length === 0) { hcFailAckRef.current = ''; return; }
    toast.error(`${t('labels.hiddenError', { defaultValue: 'Price-list prices could not be loaded — the code will print without them.' })} (${hcFailedNames.join(', ')})`);
    // The FAILURE is the trigger; hcFailedNames is derived from it (and hcFailed keeps
    // its identity while the set is unchanged, so this fires once per real failure).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hcFailed]);

  // A4 sheet mode can't exceed the printable width (210 − 2×6mm margin) or the label
  // runs off the page; roll mode has its own page size, so it allows the full 210.
  // The label can't exceed the printable area of its page: A4 (sheet), the label
  // itself (roll), or the custom sheet minus its margins (custom).
  const printW = mode === 'custom' ? Math.max(10, (sheetW || 100) - 2 * sheetMargin) : mode === 'sheet' ? 198 : 210;
  const printH = mode === 'custom' ? Math.max(10, (sheetH || 150) - 2 * sheetMargin) : 297;
  const wMm = Math.max(10, Math.min(printW, widthMm || 10));
  const hMm = Math.max(10, Math.min(printH, heightMm || 10));
  // Re-seed an UNTOUCHED custom layout whenever the label size changes, so choosing
  // Custom always opens on a layout built for the label in front of you. Deliberately
  // not gated on `template === 'custom'`: seeding on the size change instead of on the
  // template switch means the layout is already right when Custom is picked, and there
  // is no frame where the preview shows the 50×30 arrangement on a 100×50 label.
  useEffect(() => {
    if (customFor.current === null) return;      // the owner's own layout — never touch it
    const key = `${wMm}x${hMm}`;
    if (customFor.current === key) return;
    customFor.current = key;
    setCustom(defaultLayout(wMm, hMm));
  }, [wMm, hMm]);
  // The strip can never eat the whole tag: leave at least 5mm for the main column.
  const vbcW = Math.max(5, Math.min(vbcWidthMm, wMm - 5));
  const opts: SheetOpts = {
    widthMm: wMm, heightMm: hMm, mode, template, showName, showBarcode, bcVertical, showCode, showPrice, border, divider, showVertical,
    // A dead logo URL is dropped everywhere at once: a broken <img> in the preview and the
    // print popup, nothing at all in the PDF, was the three renderers disagreeing again.
    logoUrl: showLogo && !logoFailed ? logoUrl : null, nameMm, priceMm, codeMm, vbcMm, extraMm, accent, priceAccent, nameColor, priceColor, codeColor,
    font: FONTS[fontKey] ?? FONT, logoMm, upper, extraText, pricePrefix, priceSuffix,
    wmOn, wmSource, wmText, wmOpacity, wmScale, wmRotate, wmColor, wmPos, wmBehindBarcode,
    wmLogoUrl: logoFailed ? null : logoUrl, // un-gated by showLogo: the watermark works with the inline logo off
    vbcMode, vbcSide, vbcWidthMm: vbcW, vbcDigits, vbcRotate, vbcColor, vbcLenPct,
    hcPos, hcCorner, hcMm, hcColor,
    align, nameLines, bcMm, bcWidthPct, gapMm, padXMm, padYMm, cellGapXMm, cellGapYMm, dividerMm, dividerFull,
    nameAlign, priceAlign, codeAlign, extraAlign, custom,
  };

  const specs = useMemo(() => {
    const out: LabelSpec[] = [];
    for (const r of rows) {
      const srcs = sourcesOf(r.item, i18n.language, showCurrency);
      const chosen = srcs.filter((s) => r.selected.includes(s.key));
      const use = chosen.length ? chosen : [srcs[0]!];
      // A manually typed price wins over the item's own price, verbatim.
      const override = r.priceOverride?.trim();
      const codeOv = r.codeOverride?.trim();
      // Same rule for the hidden code — and verbatim here means NOT re-ciphered: an
      // override is a literal the owner already wrote in their own notation, and
      // there would be nowhere to show them what it became. hcOn is the master
      // switch, so turning the feature off can't leave a stray code on a sticker.
      const hcOv = r.hiddenOverride?.trim();
      const code = codeOv || r.item.code;
      for (const s of use) {
        // Built ONCE per source, outside the copy loop below: a 999-quantity row
        // would otherwise re-parse the template, re-read the map and re-run the
        // cipher 999 times on every keystroke in the options panel.
        const hidden = !hcOn ? '' : hcOv || buildHiddenCode(hcTemplate, hcCtxOf(r.item, s, code), hcEnv);
        // The forced symbology lands HERE, on the spec — so the flat barcode, the
        // price tag's side strip and all three renderers read one field and
        // cannot disagree about which format this label is.
        const fmt = bcFormat === 'item' ? s.format : bcFormat;
        for (let i = 0; i < r.qty; i += 1) out.push({ name: s.sub ? `${r.item.name} · ${s.sub}` : r.item.name, code, price: override || s.price, hidden, barcode: s.value, format: fmt });
      }
    }
    return out;
    // hcEnv carries the fetched prices, the markers and the cipher key, so the
    // preview updates the moment they land. optsKey does NOT cover any of that —
    // it only feeds previewHtml, which already deps on `specs`.
  }, [rows, i18n.language, showCurrency, hcOn, hcTemplate, hcEnv, bcFormat]);
  const totalLabels = specs.length;
  /**
   * What a FORCED symbology would do to this batch, counted separately because the two
   * failure modes are not the same problem (see barcodeFit):
   *   `unsupported` — falls back to Code 128. The number is still right.
   *   `renumbered`  — prints a DIFFERENT number. The sticker scans and the till finds
   *                   nothing, and nothing on screen would otherwise say so.
   * Without this the owner picks "EAN-13", gets a roll of Code 128 (or worse, a roll of
   * codes that aren't their products') and only finds out at the counter.
   */
  const bcFit = useMemo(() => {
    if (bcFormat === 'item') return { unsupported: 0, renumbered: 0 };
    let unsupported = 0; let renumbered = 0;
    for (const s of specs) {
      const fit = barcodeFit(s.barcode || s.code, bcFormat);
      if (fit === 'unsupported') unsupported += 1;
      else if (fit === 'renumbered') renumbered += 1;
    }
    return { unsupported, renumbered };
  }, [specs, bcFormat]);
  // What the template WOULD print for a row's first label — the placeholder of the
  // per-product override, mirroring how priceOverride shows srcs[0].price.
  const hcRowCodes = useMemo(() => {
    const m = new Map<string, string>();
    if (!hcOn) return m;
    for (const r of rows) {
      const srcs = sourcesOf(r.item, i18n.language, showCurrency);
      const chosen = srcs.filter((s) => r.selected.includes(s.key));
      const s = (chosen.length ? chosen : srcs)[0]!;
      m.set(r.item.id, buildHiddenCode(hcTemplate, hcCtxOf(r.item, s, r.codeOverride?.trim() || r.item.code), hcEnv));
    }
    return m;
  }, [hcOn, rows, i18n.language, showCurrency, hcTemplate, hcEnv]);
  // The same first label with the cipher OFF, so the panel can show "OMMM = 1000"
  // and the owner can check they can still read the code back.
  const hcSamplePlain = useMemo(() => {
    const r = rows[0];
    if (!hcOn || !hcKeyChars || !r) return '';
    const srcs = sourcesOf(r.item, i18n.language, showCurrency);
    const chosen = srcs.filter((s) => r.selected.includes(s.key));
    const s = (chosen.length ? chosen : srcs)[0]!;
    return r.hiddenOverride?.trim() || buildHiddenCode(hcTemplate, hcCtxOf(r.item, s, r.codeOverride?.trim() || r.item.code), { ...hcEnv, key: null });
  }, [hcOn, hcKeyChars, rows, i18n.language, showCurrency, hcTemplate, hcEnv]);

  // One key for the whole option bag: a hand-written dep list silently stopped the
  // preview from updating whenever someone added an option and forgot to list it.
  const optsKey = JSON.stringify(opts);
  // Deferred, because `custom` is inside optsKey: without this every frame of a drag
  // rebuilds all 48 preview labels — a fresh JsBarcode SVG each — and the box lags the
  // cursor. The DESIGNER's own label (designHtml) stays on the live key, so the thing
  // being dragged still tracks; only the sheet below it is allowed to fall behind.
  const previewKey = useDeferredValue(optsKey);
  const previewHtml = useMemo(
    () => buildSheetHtml(specs.slice(0, PREVIEW_CAP), opts),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [specs, previewKey],
  );

  // ── The custom designer: ONE label, blown up, with a draggable box per element. ──
  /**
   * Only the elements that would actually be on the label get a handle — mirroring
   * labelHtml's own gates, so a handle can never float over nothing (or, worse, be
   * missing for something that IS printing).
   */
  const designEls = useMemo(() => {
    // ANY spec, not just the first: the gates are per label, so a run whose first
    // product happens to carry no SKU still prints one on the rest — and reading
    // specs[0] alone withheld the handle for an element that was going to print,
    // leaving no way to place it.
    const any = (f: (s: LabelSpec) => unknown): boolean => specs.some((s) => Boolean(f(s)));
    return EL_IDS.filter((id) => (
      id === 'logo' ? Boolean(opts.logoUrl)
        : id === 'name' ? showName && any((s) => s.name)
          : id === 'barcode' ? showBarcode
            : id === 'code' ? showCode && any((s) => s.code)
              : id === 'price' ? showPrice && any((s) => s.price)
                : id === 'extra' ? Boolean(extraText.trim())
                  : any((s) => s.hidden)
    ));
  }, [specs, opts.logoUrl, showName, showBarcode, showCode, showPrice, extraText]);
  // The same builder the preview and the print pop-up use — the designer draws NOTHING
  // of its own, it only lays transparent handles over the real thing. That is what
  // keeps it from becoming a fourth renderer to keep in step.
  const designHtml = useMemo(
    () => buildSheetHtml(specs.slice(0, 1), opts),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [specs, optsKey],
  );
  const designWrapRef = useRef<HTMLDivElement>(null);
  const [designW, setDesignW] = useState(460);
  // `totalLabels > 0` is a dep because the designer only mounts inside the non-empty
  // branch of the preview: picking Custom with an empty basket left the ref null, the
  // effect bailed, and designW stayed at its 460 placeholder even after products arrived.
  const hasLabels = totalLabels > 0;
  useEffect(() => {
    const el = designWrapRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => setDesignW(el.clientWidth || 460));
    ro.observe(el);
    return () => ro.disconnect();
  }, [template, hasLabels]);
  // Blow the label up to fill the panel, but never past 14× — beyond that a 0.2mm
  // border is a fat slab and the design reads worse than the print will.
  const designScale = Math.max(1, Math.min(14, designW / Math.max(1, wMm * PX_PER_MM)));
  /**
   * The cut border's thickness, which is also the offset between the two coordinate
   * frames in play: .lp-abs is positioned from .lp-label's PADDING box (inside the
   * border) while the handle layer sits on the unbordered scaled div. labelCanvas calls
   * the same number `bo`.
   */
  const designBo = border ? 0.2 : 0;
  const snapMm = (v: number, fine: boolean): number => {
    const step = fine ? DESIGN_FINE_MM : DESIGN_SNAP_MM;
    return Math.round(v / step) * step;
  };
  /** Every edit path goes through here, which is also where the layout stops being
   *  the auto-fitted default and becomes the owner's to keep. */
  const setBox = (id: ElId, patch: Partial<ElBox>): void => {
    customFor.current = null;
    setCustom((c) => ({ ...c, [id]: clampBox({ ...c[id], ...patch }, wMm, hMm) }));
  };
  /**
   * Shift = the fine step, read off whichever event react-rnd hands back (mouse, touch
   * or pointer — a TouchEvent has no shiftKey, and reading it off would throw under
   * `strict`).
   */
  const isFine = (e: unknown): boolean => (
    typeof e === 'object' && e !== null && 'shiftKey' in e && Boolean((e as { shiftKey?: boolean }).shiftKey)
  );
  /** Alt suspends the guides for one drag — the escape hatch when a box has to sit
   *  just off a line that would otherwise capture it. */
  const isFree = (e: unknown): boolean => (
    typeof e === 'object' && e !== null && 'altKey' in e && Boolean((e as { altKey?: boolean }).altKey)
  );
  /** The alignment lines to draw right now; empty except during a drag. */
  const [guides, setGuides] = useState<Guide[]>([]);
  /** How far the live snap has displaced the box from the true cursor position. */
  const dragOffRef = useRef({ x: 0, y: 0 });
  /**
   * react-rnd reports POSITION AND SIZE in the element's own pixels — it has already
   * divided out the `scale` prop — so the only conversion left is CSS px → millimetres.
   * Both commits snap, then clamp, then store; and both mark the layout as hand-placed
   * on the FIRST event, not at the end, so a drag that is abandoned mid-gesture still
   * counts as the owner's and the size auto-fit stops.
   */
  const commitMove = (id: ElId, xPx: number, yPx: number, fine: boolean, done: boolean, free: boolean): void => {
    customFor.current = null;
    // Undo whatever the last frame's snap added before reading the cursor, because
    // `position` is controlled: react-rnd adds this frame's mouse delta to the value WE
    // handed back, so a snapped value becomes the next frame's origin and the pull is
    // re-applied on top of itself until the box is nowhere near the cursor. Subtracting
    // the offset recovers the true drag position; the box then sticks to a guide while
    // the pointer is near it and comes away cleanly the moment it isn't.
    const off = dragOffRef.current;
    const raw = { ...custom[id], x: xPx / PX_PER_MM - off.x, y: yPx / PX_PER_MM - off.y };
    let b = raw;
    let gs: Guide[] = [];
    if (!free) {
      const others = designEls.filter((e) => e !== id).map((e) => clampBox(custom[e], wMm, hMm));
      const r = alignBox(raw, others, wMm, hMm, GUIDE_SNAP_PX / (PX_PER_MM * designScale));
      b = r.box; gs = r.guides;
    }
    // The grid is the fallback, never an override: an axis already sitting on a guide is
    // left exactly there, or rounding to 0.5mm would walk it straight back off the line
    // it was just aligned to.
    if (done) {
      b = {
        ...b,
        x: gs.some((g) => g.axis === 'x') ? b.x : snapMm(b.x, fine),
        y: gs.some((g) => g.axis === 'y') ? b.y : snapMm(b.y, fine),
      };
    }
    const final = clampBox(b, wMm, hMm);
    dragOffRef.current = done ? { x: 0, y: 0 } : { x: final.x - raw.x, y: final.y - raw.y };
    setGuides(done ? [] : gs);
    setCustom((c) => ({ ...c, [id]: final }));
  };
  /**
   * Resize. The edge cap lives HERE rather than being left to clampBox, which keeps a
   * box inside by pulling `x` back: a grip dragged past the label edge would then slide
   * the whole box sideways under the cursor instead of simply stopping. Only the corner
   * being dragged may move.
   */
  const commitResize = (
    id: ElId,
    ref: { offsetWidth: number; offsetHeight: number },
    pos: { x: number; y: number },
    fine: boolean,
    snap: boolean,
  ): void => {
    customFor.current = null;
    const q = (v: number): number => (snap ? snapMm(v, fine) : v);
    setCustom((c) => {
      let x = q(pos.x / PX_PER_MM);
      let y = q(pos.y / PX_PER_MM);
      let w = q(ref.offsetWidth / PX_PER_MM);
      let h = q(ref.offsetHeight / PX_PER_MM);
      // A left/top grip dragged past the edge: pin the edge and give the width back,
      // so the opposite edge — the one the owner is not holding — stays put.
      if (x < 0) { w += x; x = 0; }
      if (y < 0) { h += y; y = 0; }
      w = Math.max(EL_MIN_MM, Math.min(w, wMm - x));
      h = Math.max(EL_MIN_MM, Math.min(h, hMm - y));
      return { ...c, [id]: clampBox({ x, y, w, h }, wMm, hMm) };
    });
  };
  const onElKey = (e: React.KeyboardEvent, id: ElId): void => {
    const d: Record<string, [number, number]> = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
    const v = d[e.key];
    if (!v) return;
    e.preventDefault();
    const step = e.shiftKey ? DESIGN_FINE_MM : DESIGN_SNAP_MM;
    setBox(id, { x: custom[id].x + v[0] * step, y: custom[id].y + v[1] * step });
  };
  /** An element's name in the panel — the SAME strings its on/off toggle uses. */
  const elName = (id: ElId): string => ({
    logo: t('labels.showLogo', { defaultValue: 'Store logo' }),
    name: t('labels.showName', { defaultValue: 'Product name' }),
    barcode: t('labels.showBarcode', { defaultValue: 'Barcode' }),
    code: t('labels.showCode', { defaultValue: 'SKU / code' }),
    price: t('labels.showPrice', { defaultValue: 'Price' }),
    extra: t('labels.extraText', { defaultValue: 'Extra line' }),
    hidden: t('labels.hidden', { defaultValue: 'Hidden code' }),
  })[id];

  /**
   * The hidden code's last-chance checks, shared by Print and Export PDF: a roll of
   * 800 wrong stickers and a wrong PDF are the same mistake, so neither path may have
   * a guard the other lacks. Returns false having already said why.
   */
  const hcOutputAllowed = (): boolean => {
    // Transient only — hcLoading is false as soon as the fetch settles, however it settled.
    if (hcLoading) { toast.error(t('labels.hiddenLoading', { defaultValue: 'Loading price-list prices…' })); return false; }
    // HARD stop. The owner asked for the numbers to be scrambled and they are not
    // being scrambled — the code prints markers (hcEnv.suppress) and nothing goes out
    // until the key is fixed or scrambling is switched off. Failing closed is the
    // cheap side: a wasted print run is recoverable, 4,000 stickers carrying the
    // purchase cost in plain digits are not.
    if (hcKeyBroken) { toast.error(t('labels.hiddenCipherBad', { defaultValue: 'Enter exactly 10 different characters, one for each digit 0-9.' })); return false; }
    // SOFT stop. Markers where a list's prices should be may well be acceptable — but
    // as a decision, not a surprise. The first click after a NEW failure buys the
    // warning; clicking again is the owner saying "print it anyway".
    const failKey = hcFailed.join(',');
    if (failKey && hcFailAckRef.current !== failKey) {
      hcFailAckRef.current = failKey;
      toast.error(`${t('labels.hiddenError', { defaultValue: 'Price-list prices could not be loaded — the code will print without them.' })} (${hcFailedNames.join(', ')})`);
      return false;
    }
    return true;
  };

  const onPrint = (): void => {
    if (!totalLabels) return;
    // The same contract the PDF's logo guard set, on the print path too: a roll of
    // 800 "xx-xx-" stickers is as unrecoverable as a wrong export.
    if (!hcOutputAllowed()) return;
    const html = buildSheetHtml(specs, opts);
    const win = window.open('', '_blank', 'width=1000,height=760');
    if (!win) { toast.error(t('labels.popupBlocked', { defaultValue: 'Allow pop-ups to open the print view.' })); return; }
    const page = mode === 'roll' ? `@page{size:${wMm}mm ${hMm}mm;margin:0}`
      : mode === 'custom' ? `@page{size:${sheetW}mm ${sheetH}mm;margin:${sheetMargin}mm}`
      : '@page{size:A4;margin:6mm}';
    win.document.write(
      `<!doctype html><html dir="${document.documentElement.dir || 'ltr'}"><head><meta charset="utf-8"><title>${escapeHtml(t('labels.title', { defaultValue: 'Labels' }))}</title>`
      + `<style>${SHEET_CSS}\n${page}\n@media screen{body{margin:0;background:#eceef2;padding:16px;font-family:${FONT}}}\n@media print{body{margin:0;background:#fff}}</style>`
      + `</head><body>${html}<script>window.addEventListener("load",function(){setTimeout(function(){window.print();},250);});window.onafterprint=function(){window.close();};</script></body></html>`,
    );
    win.document.close();
    askMarkPrinted();
  };

  // Draw one label to a high-res canvas (crisp barcode + Arabic/any-font text that
  // jsPDF's built-in fonts can't render — so text goes in as an image too).
  const onExportPdf = async (): Promise<void> => {
    if (!totalLabels) return;
    if (totalLabels > PDF_MAX) {
      toast.error(t('labels.pdfTooMany', { defaultValue: 'Too many labels for one PDF ({{n}}). Narrow the selection or print in batches (max {{max}}).', n: totalLabels, max: PDF_MAX }));
      return;
    }
    // The logo loads asynchronously; exporting before it lands would drop the watermark
    // from the PDF *only* — the preview would still look right, so say so out loud.
    // Only while a load is genuinely in flight: once it has failed the watermark is gone
    // from all three renderers, and blocking the export would just be a dead end.
    if (wmOn && wmSource === 'logo' && logoUrl && !logoImg && !logoFailed) {
      toast.error(t('labels.logoLoading', { defaultValue: 'The store logo is still loading — try again in a moment.' }));
      return;
    }
    // The hidden code's own guards — the same set the print path runs (see onPrint).
    if (!hcOutputAllowed()) return;
    setPdfBusy(true);
    try {
      const { jsPDF } = await import('jspdf');
      const roll = mode === 'roll';
      const custom = mode === 'custom';
      // jsPDF swaps a page's dims when width > height, so pick the orientation from the
      // page's own aspect — that keeps the PDF page equal to the print @page size.
      const pDims: [number, number] = roll ? [wMm, hMm] : [sheetW, sheetH];
      const fmt: [number, number] | 'a4' = mode === 'sheet' ? 'a4' : pDims;
      const orient: 'portrait' | 'landscape' = mode === 'sheet' ? 'portrait' : pDims[0] >= pDims[1] ? 'landscape' : 'portrait';
      const doc = new jsPDF({ unit: 'mm', format: fmt, orientation: orient });
      const margin = roll ? 0 : custom ? sheetMargin : 6;
      // The same two options .lp-sheet's grid-gap uses, so the PDF tiles the labels
      // exactly where the print pop-up does. Roll mode is one label per page — no gap.
      const gapX = roll ? 0 : cellGapXMm; const gapY = roll ? 0 : cellGapYMm;
      const pageW = roll ? wMm : custom ? sheetW : 210; const pageH = roll ? hMm : custom ? sheetH : 297;
      const cols = roll ? 1 : Math.max(1, Math.floor((pageW - 2 * margin + gapX) / (wMm + gapX)));
      const rowsPer = roll ? 1 : Math.max(1, Math.floor((pageH - 2 * margin + gapY) / (hMm + gapY)));
      const perPage = cols * rowsPer;
      for (let i = 0; i < specs.length; i += 1) {
        if (i > 0 && i % perPage === 0) doc.addPage(fmt, orient);
        // Yield every so often so the busy state can paint and canvases get GC'd.
        if (i > 0 && i % 25 === 0) await new Promise((res) => setTimeout(res));
        const idx = i % perPage; const c = idx % cols; const r = Math.floor(idx / cols);
        const x = margin + c * (wMm + gapX); const y = margin + r * (hMm + gapY);
        let png: string;
        // A cross-origin logo taints the canvas and toDataURL() throws — an actionable
        // message beats the generic server error the outer catch would show.
        try { png = labelCanvas(specs[i]!, opts, logoImg).toDataURL('image/png'); } catch {
          toast.error(t('labels.pdfTainted', { defaultValue: 'The store logo blocked the PDF export (cross-origin image). Turn the logo off, or re-upload it in the storefront settings.' }));
          return;
        }
        doc.addImage(png, 'PNG', x, y, wMm, hMm);
      }
      doc.save(`labels-${new Date().toISOString().slice(0, 10)}.pdf`);
      askMarkPrinted();
    } catch { toast.error(tc('errors:server')); } finally { setPdfBusy(false); }
  };

  const chk = 'h-4 w-4 accent-brand';
  const toggleRow = (checked: boolean, onChange: (v: boolean) => void, label: string): JSX.Element => (
    <label className="flex items-center gap-2 text-sm"><input type="checkbox" className={chk} checked={checked} onChange={(e) => onChange(e.target.checked)} />{label}</label>
  );
  const autoLabel = t('labels.auto', { defaultValue: 'Auto' });
  /**
   * A compact −/value/+ stepper for a size in mm, with its own range. `auto: true` adds
   * the AUTO_MM sentinel below the range: stepping down off the bottom lands on it and
   * stepping up leaves it, so "let the template decide" needs no control of its own —
   * and the value itself is a button that puts it back.
   */
  const mmRow = (
    label: string,
    value: number,
    set: (v: number) => void,
    o: { min: number; max: number; step: number; auto?: boolean; bigSteps?: ReadonlyArray<readonly [number, number]> },
  ): JSX.Element => {
    const isAuto = o.auto === true && value < 0;
    /** The step at this size: fine while small, coarser once large (`bigSteps`: [from mm, step]). */
    const stepAt = (v: number, d: number): number => {
      let st = o.step;
      // Going down from exactly a threshold uses the finer step below it.
      for (const [from, s] of o.bigSteps ?? []) if (d > 0 ? v >= from : v > from) st = s;
      return st;
    };
    const commit = (raw: string): void => {
      const v = Number(normalizeAmountTyping(raw));
      if (!Number.isFinite(v) || raw.trim() === '') return;
      set(Math.min(o.max, Math.max(o.min, Math.round(v * 100) / 100)));
    };
    const bump = (d: number): void => {
      if (isAuto) { set(d > 0 ? o.min : AUTO_MM); return; }
      d = Math.sign(d) * stepAt(value, d);
      // Two decimals, not one: a 0.25mm hairline rule is a real setting, and rounding
      // it to 0.3 in the box while storing 0.25 makes the panel lie about the label.
      const next = Math.round((value + d) * 100) / 100;
      if (o.auto && next < o.min) { set(AUTO_MM); return; }
      set(Math.min(o.max, Math.max(o.min, next)));
    };
    const shown = isAuto ? autoLabel : `${Number(value.toFixed(2))}mm`;
    return (
      <div className="flex items-center justify-between gap-2 text-sm">
        <span className="text-fg-muted">{label}</span>
        <span className="inline-flex items-center gap-1">
          <button type="button" aria-label={tc('actions.decrease', { defaultValue: 'Decrease' })} onClick={() => bump(-o.step)} className="grid h-6 w-6 place-items-center rounded border border-border text-fg-muted hover:text-fg"><Minus className="h-3.5 w-3.5" aria-hidden /></button>
          {o.auto
            ? <button type="button" onClick={() => set(AUTO_MM)} title={autoLabel} className="w-14 text-center font-mono text-xs tabular-nums text-fg-muted hover:text-fg">{shown}</button>
            : (
              // Typed straight in (owner, 2026-11-01: "make them bigger") — keyed on
              // the value so the −/+ buttons refresh it; committed on Enter or leaving.
              <span className="inline-flex w-16 items-center justify-center gap-0.5 font-mono text-xs tabular-nums">
                <input
                  key={value}
                  defaultValue={Number(value.toFixed(2))}
                  inputMode="decimal"
                  dir="ltr"
                  aria-label={label}
                  onBlur={(e) => commit(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); (e.target as HTMLInputElement).blur(); } }}
                  className="w-10 rounded border border-transparent bg-transparent px-0.5 text-center hover:border-border focus:border-brand focus:outline-none"
                />
                <span className="text-fg-muted">mm</span>
              </span>
            )}
          <button type="button" aria-label={tc('actions.increase', { defaultValue: 'Increase' })} onClick={() => bump(o.step)} className="grid h-6 w-6 place-items-center rounded border border-border text-fg-muted hover:text-fg"><Plus className="h-3.5 w-3.5" aria-hidden /></button>
        </span>
      </div>
    );
  };
  /**
   * Text sizes all share one range; the strip's own controls need their own.
   * Up to 40mm (was 8mm — owner, 2026-11-01: "make them bigger", for price tags
   * and shelf signs), in fine steps while small and coarser ones once large, so
   * a big size is a few clicks, not forty.
   */
  const sizeRow = (label: string, value: number, set: (v: number) => void): JSX.Element =>
    mmRow(label, value, set, { min: 1.5, max: TEXT_MAX_MM, step: 0.3, bigSteps: [[8, 0.5], [12, 1]] });
  /** Every text on the label bigger or smaller together, 10% a click. */
  const scaleAllText = (f: number): void => {
    const s = (v: number): number => Math.min(TEXT_MAX_MM, Math.max(1.5, Math.round(v * f * 10) / 10));
    setNameMm(s(nameMm)); setPriceMm(s(priceMm)); setCodeMm(s(codeMm)); setExtraMm(s(extraMm)); setLogoMm(s(logoMm));
  };
  /** A slider for the values that aren't millimetres (percent, degrees). */
  const rangeRow = (label: string, value: number, set: (v: number) => void, o: { min: number; max: number; step: number; fmt: (v: number) => string }): JSX.Element => (
    <div className="flex items-center justify-between gap-2 text-sm">
      <span className="whitespace-nowrap text-fg-muted">{label}</span>
      <span className="inline-flex min-w-0 flex-1 items-center gap-2">
        <input type="range" min={o.min} max={o.max} step={o.step} value={value} onChange={(ev) => set(Number(ev.target.value))} aria-label={label} className="h-1.5 min-w-0 flex-1 cursor-pointer accent-brand" />
        <span className="w-12 shrink-0 text-end font-mono text-xs tabular-nums">{o.fmt(value)}</span>
      </span>
    </div>
  );
  /** A pill in a segmented control (template / mode / side switchers). */
  const pill = (active: boolean, onClick: () => void, label: string, disabled = false): JSX.Element => (
    <button type="button" onClick={onClick} aria-pressed={active} disabled={disabled}
      className={`inline-flex h-7 items-center rounded-full px-3 text-xs font-semibold transition disabled:cursor-not-allowed disabled:opacity-40 ${active ? 'bg-brand text-brand-contrast' : 'text-fg-muted hover:text-fg'}`}>
      {label}
    </button>
  );
  /** A collapsed disclosure group — the options column is long enough already. */
  const section = (title: string, badge: string, children: ReactNode): JSX.Element => (
    <details className="rounded-md border border-border px-2.5 py-1.5">
      <summary className="flex cursor-pointer list-none items-center justify-between text-sm font-medium marker:content-['']">
        <span>{title}</span><span className="text-xs text-fg-muted">{badge}</span>
      </summary>
      <div className="mt-2 flex flex-col gap-2.5 border-t border-border pt-2.5">{children}</div>
    </details>
  );
  // The strip's side and the watermark's corner are stored physically, but the panel
  // speaks start/end — resolved in the page's direction (LTR in the admin, even in Arabic).
  const uiRtl = layoutDir === 'rtl';
  const sideOf = (edge: 'start' | 'end'): VbcSide => ((edge === 'start') !== uiRtl ? 'left' : 'right');
  const WM_POS_UI = [
    { k: 'posCenter', d: 'Center', ltr: 'center', rtl: 'center' },
    { k: 'posTopStart', d: 'Top · start', ltr: 'tl', rtl: 'tr' },
    { k: 'posTopEnd', d: 'Top · end', ltr: 'tr', rtl: 'tl' },
    { k: 'posBottomStart', d: 'Bottom · start', ltr: 'bl', rtl: 'br' },
    { k: 'posBottomEnd', d: 'Bottom · end', ltr: 'br', rtl: 'bl' },
  ] as const;
  const offLabel = t('labels.watermarkOff', { defaultValue: 'Off' });
  const wmBadge = !wmOn ? offLabel
    : wmSource === 'logo' ? t('labels.watermarkLogo', { defaultValue: 'Store logo' })
      : wmText.trim() ? `“${wmText.trim()}”` : t('labels.watermarkText', { defaultValue: 'Custom text' });
  // The bars' length is what a scanner's aiming line has to cross — warn before the owner
  // prints 800 stickers that no gun can read. Straight off vbcMetrics, the same maths the
  // three renderers draw from: it applies the clamps AND, now that the strip's quiet zone
  // is on the scan axis only, thickMm is the printed ink length rather than a band that
  // was half margin. A hand-kept copy here is exactly how the warning went blind.
  const vbcBarsMm = vbcMetrics(opts).thickMm;
  // How far the code runs down the label — no longer simply the label height now that
  // the strip's length is a knob, so the "too short for EAN-13" warning reads it too.
  const vbcLenMm = vbcMetrics(opts).lenMm;
  const vbcBadge = !showVertical ? offLabel
    : vbcMode === 'number' ? t('labels.vbcModeNumber', { defaultValue: 'Number only' })
      : `${vbcWidthMm.toFixed(1)}mm`;
  // The RESOLVED padding, so the collapsed summary says what the label actually has
  // rather than repeating the word "Auto" back at whoever set it.
  const geoNow = geometry(opts);
  const spacingBadge = `${Number(geoNow.padX.toFixed(2))} · ${Number(geoNow.padY.toFixed(2))}mm`;
  /** One row of the per-element alignment section: Auto / Start / Center / End. */
  const alignRow = (label: string, value: ElAlign, set: (v: ElAlign) => void): JSX.Element => (
    <div className="flex items-center justify-between gap-2 text-sm">
      <span className="text-fg-muted">{label}</span>
      <select value={value} onChange={(ev) => set(ev.target.value as ElAlign)} className="h-8 max-w-[9rem] rounded-md border border-border bg-bg px-2 text-sm">
        <option value="auto">{autoLabel}</option>
        {/* Physical values behind logical words — `edgeAlign` is the same start/end →
            left/right translation the strip's Side control does. */}
        <option value={edgeAlign('start')}>{t('labels.alignStart', { defaultValue: 'Start' })}</option>
        <option value="center">{t('labels.alignCenter', { defaultValue: 'Center' })}</option>
        <option value={edgeAlign('end')}>{t('labels.alignEnd', { defaultValue: 'End' })}</option>
      </select>
    </div>
  );
  // How many rows are NOT following the global control — the one number worth putting
  // in a collapsed summary, since "all Auto" is the state that needs no attention.
  const perAlignSet = [nameAlign, priceAlign, codeAlign, extraAlign].filter((a) => a !== 'auto').length;
  const perAlignBadge = perAlignSet === 0 ? autoLabel : `${perAlignSet}`;
  // Alignment is stored PHYSICALLY (the PDF canvas has no writing direction) and the
  // panel speaks start/end — the exact same translation the strip's Side control does,
  // so `sideOf` serves both ('left' | 'right' is a subset of Align).
  // Named `edgeAlign`, not `alignOf`: the module already has an alignOf(opts, el) that
  // resolves an element's 'auto', and a shadow of it in here would be a trap.
  const edgeAlign = (edge: 'start' | 'end'): Align => sideOf(edge);
  // ── Hidden-code panel bits. The badge is the live sample: the most useful thing
  //    that could sit in a collapsed section's summary. ──
  const hcSample = specs[0]?.hidden ?? '';
  const hcBadge = !hcOn ? offLabel
    : hcLoading ? '…'
      : hcSample ? (hcSample.length > 14 ? `${hcSample.slice(0, 14)}…` : hcSample)
        // A template with no product to sample it on isn't "not set" — say so.
        : hcTemplate.trim() ? '—' : t('labels.hiddenNotSet', { defaultValue: 'Not set' });
  const chipCls = 'rounded-full border border-border px-2 py-0.5 font-mono text-[11px] font-medium text-fg-muted hover:text-fg';
  const hcInsert = (token: string): void => {
    // Append, not caret-insert: the natural build order is type "xx" → chip →
    // type "xx" → chip, which appending handles exactly. All-or-nothing, because a
    // token cut in half by the length cap would print as literal text.
    setHcTemplate((v) => (v.length + token.length <= HC_TPL_MAX ? v + token : v));
    hcInputRef.current?.focus();
  };
  /** A chip inserts the list's NAME so a saved design stays readable and hand-
   *  editable; it falls back to the id when the name would break the grammar or
   *  two active lists share it (then a name couldn't say which). */
  const hcListToken = (l: PriceList): string => {
    const n = l.name.trim();
    return `{list:${!n || /[{}|]/.test(n) || hcIndex.dupes.has(n.toLowerCase()) ? l.id : n}}`;
  };
  /** The owner's example, straight from their own lists: xx{A}xx{B}xx{C}. */
  const hcStarterTemplate = (): string => hcIndex.active.reduce((acc, l) => {
    const next = `${acc}xx${hcListToken(l)}`;
    return next.length <= HC_TPL_MAX ? next : acc;
  }, '');
  // Repeated key characters stay ALLOWED — encoding is still deterministic and a
  // decoy is a legitimate choice — but only DECODING breaks, and decoding is the
  // human act this feature exists for, so name them.
  const hcDupes = [...new Set(Array.from(hcKey).filter((c, i, a) => a.indexOf(c) !== i))];
  const HC_TOKEN_CHIPS = [
    { tok: '{cost}', k: 'hiddenCost', d: 'Cost' },
    { tok: '{price}', k: 'hiddenPrice', d: 'Sale price' },
    { tok: '{sku}', k: 'hiddenSku', d: 'SKU' },
    { tok: '{barcode}', k: 'hiddenBarcode', d: 'Barcode' },
  ] as const;
  // Ten distinct letters each — a starting point beats a blank box.
  const HC_KEY_SUGGESTIONS = ['MONEYTALKS', 'CHARLESTON', 'BLACKHORSE'] as const;
  const presetId = SIZE_PRESETS.find((p) => p.w === widthMm && p.h === heightMm)?.id ?? 'custom';
  const catCombo: ComboboxOption[] = flattenCategoryOptions(categories).map((o) => ({ value: o.id, label: o.label }));
  const brandCombo: ComboboxOption[] = brands.map((b) => ({ value: b.id, label: b.title }));

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">{t('labels.title', { defaultValue: 'Print labels' })}</h1>
          <p className="mt-1 text-sm text-fg-muted">{t('labels.description', { defaultValue: 'Print product barcodes / price stickers — pick products, set quantities and label size, then print or export a PDF.' })}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => void onExportPdf()} disabled={totalLabels === 0} loading={pdfBusy}>
            <FileDown className="h-4 w-4" aria-hidden />{t('labels.pdf', { defaultValue: 'Export PDF' })}
          </Button>
          <Button onClick={onPrint} disabled={totalLabels === 0}>
            <Printer className="h-4 w-4" aria-hidden />{t('labels.print', { defaultValue: 'Print' })}{totalLabels > 0 ? ` (${totalLabels})` : ''}
          </Button>
        </div>
      </header>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[23rem_1fr]">
        <div className="flex flex-col gap-4">
          <LabelQueue onAdd={addPending} version={queueVersion} categories={catCombo} locale={dataLocale} canMarkAll={canWrite} />
          <Card className="flex flex-col gap-3 p-4">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-fg-muted">{t('labels.products', { defaultValue: 'Products' })}</h2>
            <div className="relative">
              <Search className="pointer-events-none absolute inset-y-0 start-2.5 my-auto h-4 w-4 text-fg-muted" aria-hidden />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('searchPlaceholder')} className="ps-8" />
              {results.length > 0 && (
                <ul className="absolute z-30 mt-1 max-h-64 w-full overflow-auto rounded-md border border-border bg-bg py-1 shadow-lg">
                  {results.map((it) => (
                    <li key={it.id}>
                      <button type="button" onClick={() => addOne(it)} className="flex w-full flex-col items-start px-3 py-1.5 text-start hover:bg-bg-subtle">
                        <span className="text-sm font-medium text-fg">{it.name}</span>
                        <span className="font-mono text-xs text-fg-muted">{it.code}{it.barcode ? ` · ${it.barcode}` : ''}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {/* Bulk add: all products of a category or brand. */}
            <div className="flex flex-col gap-2 rounded-md border border-dashed border-border p-2.5">
              <div className="flex items-center gap-1.5">
                <FolderTree className="h-3.5 w-3.5 text-fg-muted" aria-hidden />
                <div className="min-w-0 flex-1"><Combobox size="sm" options={catCombo} value={catId} onChange={setCatId} placeholder={t('list.allCategories', { defaultValue: 'Category' })} ariaLabel={t('list.allCategories', { defaultValue: 'Category' })} /></div>
                <Button size="sm" variant="secondary" disabled={!catId || bulkBusy} loading={bulkBusy} onClick={() => catId && void addAll({ categoryId: catId }, catCombo.find((o) => o.value === catId)?.label ?? '')}>{t('labels.addAll', { defaultValue: 'Add all' })}</Button>
              </div>
              <div className="flex items-center gap-1.5">
                <Tag className="h-3.5 w-3.5 text-fg-muted" aria-hidden />
                <div className="min-w-0 flex-1"><Combobox size="sm" options={brandCombo} value={brandId} onChange={setBrandId} placeholder={t('list.allBrands', { defaultValue: 'Brand' })} ariaLabel={t('list.allBrands', { defaultValue: 'Brand' })} /></div>
                <Button size="sm" variant="secondary" disabled={!brandId || bulkBusy} loading={bulkBusy} onClick={() => brandId && void addAll({ brandId }, brandCombo.find((o) => o.value === brandId)?.label ?? '')}>{t('labels.addAll', { defaultValue: 'Add all' })}</Button>
              </div>
              <p className="text-[11px] text-fg-subtle">{t('labels.addAllSubtreeHint', { defaultValue: 'A category also adds everything in its subcategories.' })}</p>
            </div>
            {rows.length === 0 ? (
              <p className="text-xs text-fg-muted">{t('labels.empty', { defaultValue: 'Search and add products, or add a whole category / brand.' })}</p>
            ) : (
              <>
                <div className="flex items-center justify-between text-xs text-fg-muted">
                  <span>{t('labels.rowsCount', { defaultValue: '{{n}} product(s)', n: rows.length })}</span>
                  <span className="flex items-center gap-3">
                    <button type="button" onClick={() => void markRowsPrinted()} className="inline-flex items-center gap-1 font-medium text-success underline-offset-4 hover:underline">
                      <Check className="h-3.5 w-3.5" aria-hidden />{t('labels.queue.markRows', { defaultValue: 'Mark printed' })}
                    </button>
                    <button type="button" onClick={() => setRows([])} className="font-medium underline-offset-4 hover:text-fg hover:underline">{t('list.clearFilters', { defaultValue: 'Clear' })}</button>
                  </span>
                </div>
                <ul className="flex max-h-[28rem] flex-col gap-1.5 overflow-auto pe-1">
                  {rows.map((r) => {
                    const srcs = sourcesOf(r.item, i18n.language, showCurrency);
                    return (
                      <li key={r.item.id} className="rounded-md border border-border bg-bg-subtle px-2.5 py-1.5">
                        <div className="flex items-center gap-2">
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-medium">{r.item.name}</span>
                            <span className="block truncate font-mono text-xs text-fg-muted">{r.item.code}</span>
                          </span>
                          <div className="flex items-center gap-1">
                            <button type="button" aria-label={tc('actions.decrease', { defaultValue: 'Decrease' })} onClick={() => setQty(r.item.id, r.qty - 1)} className="grid h-6 w-6 place-items-center rounded border border-border text-fg-muted hover:text-fg"><Minus className="h-3.5 w-3.5" aria-hidden /></button>
                            <Input value={String(r.qty)} onChange={(e) => setQty(r.item.id, Number(e.target.value.replace(/\D/g, '')) || 1)} inputMode="numeric" className="h-6 w-12 px-1 text-center text-xs" />
                            <button type="button" aria-label={tc('actions.increase', { defaultValue: 'Increase' })} onClick={() => setQty(r.item.id, r.qty + 1)} className="grid h-6 w-6 place-items-center rounded border border-border text-fg-muted hover:text-fg"><Plus className="h-3.5 w-3.5" aria-hidden /></button>
                          </div>
                          <button type="button" aria-label={tc('actions.remove', { defaultValue: 'Remove' })} onClick={() => removeRow(r.item.id)} className="text-fg-muted hover:text-danger"><Trash2 className="h-4 w-4" aria-hidden /></button>
                        </div>
                        {/* Why this product is here, from "Needs a label" — on its own line so it
                            never squeezes the name beside the quantity box. */}
                        {(pendingByItem.get(r.item.id) ?? []).some((l) => r.selected.includes(l.key)) && (
                          <div className="mt-1 flex flex-wrap gap-1">
                            {(pendingByItem.get(r.item.id) ?? []).filter((l) => r.selected.includes(l.key)).map((l) => (
                              <span key={l.key} className={`whitespace-nowrap rounded px-1.5 py-0.5 text-[10px] font-semibold ${l.reason === 'new' ? 'bg-brand-subtle text-brand' : 'border border-warning text-warning'}`}>
                                {l.reason === 'new'
                                  ? t('labels.queue.reasonNew', { defaultValue: 'New' })
                                  : l.reason === 'price'
                                    ? t('labels.queue.reasonPrice', { defaultValue: 'Price changed' })
                                    : t('labels.queue.reasonBarcode', { defaultValue: 'Barcode changed' })}
                              </span>
                            ))}
                          </div>
                        )}
                        {showPrice && (
                          <div className="mt-1.5 flex items-center gap-2">
                            <Tag className="h-3.5 w-3.5 flex-none text-fg-subtle" aria-hidden />
                            <Input
                              value={r.priceOverride ?? ''}
                              onChange={(e) => setPriceOverride(r.item.id, e.target.value)}
                              placeholder={srcs[0]?.price || t('labels.customPrice', { defaultValue: 'Custom price' })}
                              aria-label={t('labels.customPrice', { defaultValue: 'Custom price' })}
                              className="h-6 flex-1 px-1.5 text-xs"
                            />
                          </div>
                        )}
                        {showCode && (
                          <div className="mt-1.5 flex items-center gap-2">
                            <span className="flex-none font-mono text-[11px] font-semibold text-fg-subtle">#</span>
                            <Input
                              value={r.codeOverride ?? ''}
                              onChange={(e) => setCodeOverride(r.item.id, e.target.value)}
                              placeholder={r.item.code}
                              /* Retitled: this overrides the printed SKU. The real
                                 hidden code is the separate input right below. */
                              aria-label={t('labels.customCode', { defaultValue: 'Custom code' })}
                              className="h-6 flex-1 px-1.5 font-mono text-xs"
                            />
                          </div>
                        )}
                        {hcOn && (
                          <div className="mt-1.5 flex items-center gap-2">
                            <EyeOff className="h-3.5 w-3.5 flex-none text-fg-subtle" aria-hidden />
                            <Input
                              dir="ltr"
                              value={r.hiddenOverride ?? ''}
                              onChange={(e) => setHiddenOverride(r.item.id, e.target.value)}
                              placeholder={hcRowCodes.get(r.item.id) || t('labels.hiddenPerItem', { defaultValue: 'Hidden code for this product' })}
                              aria-label={t('labels.hiddenPerItem', { defaultValue: 'Hidden code for this product' })}
                              className="h-6 flex-1 px-1.5 font-mono text-xs"
                            />
                          </div>
                        )}
                        {srcs.length > 1 && (
                          <div className="mt-1.5 flex flex-wrap gap-1">
                            {srcs.map((s) => {
                              const on = r.selected.includes(s.key);
                              return (
                                <button key={s.key} type="button" onClick={() => toggleSource(r.item.id, s.key)}
                                  className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${on ? 'border-transparent bg-brand text-brand-contrast' : 'border-border text-fg-muted hover:text-fg'}`}>
                                  {s.sub || t('labels.primaryBarcode', { defaultValue: 'Primary' })}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
          </Card>

          <Card className="flex flex-col gap-3 p-4">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-fg-muted">{t('labels.size', { defaultValue: 'Label size' })}</h2>
            <div className="flex flex-wrap items-center gap-2">
              <select value={presetId} onChange={(e) => { const p = SIZE_PRESETS.find((x) => x.id === e.target.value); if (p) { setWidthMm(p.w); setHeightMm(p.h); } }} className="h-9 rounded-md border border-border bg-bg px-2 text-sm">
                {SIZE_PRESETS.map((p) => <option key={p.id} value={p.id}>{p.w} × {p.h} mm</option>)}
                <option value="custom">{t('labels.custom', { defaultValue: 'Custom' })}</option>
              </select>
              <label className="flex items-center gap-1 text-sm"><span className="text-fg-muted">{t('labels.width', { defaultValue: 'W' })}</span><Input value={String(widthMm)} onChange={(e) => setWidthMm(Math.min(210, Number(e.target.value.replace(/\D/g, '')) || 0))} inputMode="numeric" className="h-9 w-16" /></label>
              <label className="flex items-center gap-1 text-sm"><span className="text-fg-muted">{t('labels.height', { defaultValue: 'H' })}</span><Input value={String(heightMm)} onChange={(e) => setHeightMm(Math.min(297, Number(e.target.value.replace(/\D/g, '')) || 0))} inputMode="numeric" className="h-9 w-16" /></label>
              <span className="text-xs text-fg-muted">mm</span>
            </div>
            <div className="inline-flex w-fit rounded-full border border-border p-0.5">
              {(['sheet', 'roll', 'custom'] as const).map((m) => (
                <button key={m} type="button" onClick={() => setMode(m)} aria-pressed={mode === m} className={`inline-flex h-7 items-center rounded-full px-3 text-xs font-semibold transition ${mode === m ? 'bg-brand text-brand-contrast' : 'text-fg-muted hover:text-fg'}`}>
                  {m === 'sheet' ? t('labels.sheet', { defaultValue: 'A4 sheet' }) : m === 'roll' ? t('labels.roll', { defaultValue: 'Roll / one per page' }) : t('labels.customPage', { defaultValue: 'Custom size' })}
                </button>
              ))}
            </div>
            {mode === 'custom' && (
              <div className="flex flex-wrap items-center gap-2 rounded-md border border-dashed border-border p-2.5">
                <span className="text-xs font-medium text-fg-muted">{t('labels.pageSize', { defaultValue: 'Page' })}</span>
                <label className="flex items-center gap-1 text-sm"><span className="text-fg-muted">{t('labels.width', { defaultValue: 'W' })}</span><Input value={String(sheetW)} onChange={(e) => setSheetW(Math.min(2000, Number(e.target.value.replace(/\D/g, '')) || 0))} inputMode="numeric" className="h-8 w-16" /></label>
                <label className="flex items-center gap-1 text-sm"><span className="text-fg-muted">{t('labels.height', { defaultValue: 'H' })}</span><Input value={String(sheetH)} onChange={(e) => setSheetH(Math.min(2000, Number(e.target.value.replace(/\D/g, '')) || 0))} inputMode="numeric" className="h-8 w-16" /></label>
                <label className="flex items-center gap-1 text-sm"><span className="text-fg-muted">{t('labels.margin', { defaultValue: 'Margin' })}</span><Input value={String(sheetMargin)} onChange={(e) => setSheetMargin(Math.min(50, Number(e.target.value.replace(/\D/g, '')) || 0))} inputMode="numeric" className="h-8 w-14" /></label>
                <span className="text-xs text-fg-muted">mm</span>
              </div>
            )}
          </Card>

          <Card className="flex flex-col gap-2.5 p-4">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-fg-muted">{t('labels.layout', { defaultValue: 'Layout' })}</h2>
            {/* Template — Centered stack vs a shelf price-tag (logo-left, big price,
                name on top, SKU at the bottom, barcode standing up the side). */}
            <div className="inline-flex w-fit rounded-full border border-border p-0.5">
              {(['centered', 'tag', 'custom'] as const).map((tp) => (
                <button key={tp} type="button" onClick={() => setTemplate(tp)} aria-pressed={template === tp}
                  className={`inline-flex h-7 items-center rounded-full px-3 text-xs font-semibold transition ${template === tp ? 'bg-brand text-brand-contrast' : 'text-fg-muted hover:text-fg'}`}>
                  {tp === 'centered' ? t('labels.tplCentered', { defaultValue: 'Centered' })
                    : tp === 'tag' ? t('labels.tplTag', { defaultValue: 'Price tag' })
                      : t('labels.tplCustom', { defaultValue: 'Custom' })}
                </button>
              ))}
            </div>

            {/* Where the text sits. Start/end, not left/right: in an Arabic admin the
                start of the label is its right-hand edge, and alignOf() is what turns
                that into the physical value all three renderers draw from. */}
            <div className="flex items-center justify-between gap-2 text-sm">
              <span className="text-fg-muted">{t('labels.align', { defaultValue: 'Text alignment' })}</span>
              <div className="inline-flex rounded-full border border-border p-0.5">
                {pill(align === edgeAlign('start'), () => setAlign(edgeAlign('start')), t('labels.alignStart', { defaultValue: 'Start' }))}
                {pill(align === 'center', () => setAlign('center'), t('labels.alignCenter', { defaultValue: 'Center' }))}
                {pill(align === edgeAlign('end'), () => setAlign(edgeAlign('end')), t('labels.alignEnd', { defaultValue: 'End' }))}
              </div>
            </div>

            <h2 className="mt-1 text-sm font-semibold uppercase tracking-wide text-fg-muted">{t('labels.content', { defaultValue: 'On the label' })}</h2>
            {logoUrl && !logoFailed && toggleRow(showLogo, setShowLogo, t('labels.showLogo', { defaultValue: 'Store logo' }))}
            {toggleRow(showName, setShowName, t('labels.showName', { defaultValue: 'Product name' }))}
            {/* How many lines a long name may wrap to before it is cut with an ellipsis.
                Auto = what the template always did: two on a centered label, one on a
                price tag, where the name is a header above the rule. */}
            {showName && (
              <div className="flex items-center justify-between gap-2 text-sm">
                <span className="text-fg-muted">{t('labels.nameLines', { defaultValue: 'Name lines' })}</span>
                <select value={nameLines} onChange={(e) => setNameLines(Number(e.target.value))} className="h-8 rounded-md border border-border bg-bg px-2 text-sm">
                  <option value={0}>{autoLabel}</option>
                  {[1, 2, 3].map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
            )}
            {toggleRow(showBarcode, setShowBarcode, t('labels.showBarcode', { defaultValue: 'Scannable barcode' }))}
            {/* The symbology to print in. "Per product" is each item's own setting;
                anything else forces THIS print run without touching the catalogue —
                the products page is where you change the products themselves. */}
            {(showBarcode || (template === 'tag' && showVertical)) && (
              <>
                <div className="flex items-center justify-between gap-2 text-sm">
                  <span className="text-fg-muted">{t('labels.bcFormat', { defaultValue: 'Barcode type' })}</span>
                  <select value={bcFormat} onChange={(e) => setBcFormat(e.target.value as BcFormatOpt)} className="h-8 max-w-[11rem] rounded-md border border-border bg-bg px-2 text-sm">
                    <option value="item">{t('labels.bcFormatItem', { defaultValue: 'Per product' })}</option>
                    {BC_FORMATS.map((f) => <option key={f} value={f}>{t(`barcodeFormat.${f}`)}</option>)}
                  </select>
                </div>
                {/* The dangerous one first: a renumbered label scans perfectly and
                    finds nothing, so it must not read as a footnote to the other. */}
                {bcFit.renumbered > 0 && (
                  <p className="text-[11px] text-danger">
                    {t('labels.bcFormatRenumbered', {
                      defaultValue: '{{n}} of {{total}} labels would print a DIFFERENT number than the product’s barcode (it is one digit short, so a check digit gets added). Those stickers would scan to nothing.',
                      n: bcFit.renumbered,
                      total: totalLabels,
                    })}
                  </p>
                )}
                {bcFit.unsupported > 0 && (
                  <p className="text-[11px] text-warning">
                    {t('labels.bcFormatFallback', {
                      defaultValue: '{{n}} of {{total}} labels can’t be this type — they print as Code 128 instead. Fix the products in bulk from the Products page.',
                      n: bcFit.unsupported,
                      total: totalLabels,
                    })}
                  </p>
                )}
              </>
            )}
            {toggleRow(showCode, setShowCode, t('labels.showCode', { defaultValue: 'SKU / code' }))}
            {toggleRow(showPrice, setShowPrice, t('labels.showPrice', { defaultValue: 'Price' }))}
            {showPrice && toggleRow(showCurrency, setShowCurrency, t('labels.showCurrency', { defaultValue: 'Currency with the price' }))}
            {template === 'tag' && toggleRow(divider, setDivider, t('labels.divider', { defaultValue: 'Divider line' }))}
            {template === 'tag' && divider && (
              <>
                {toggleRow(dividerFull, setDividerFull, t('labels.dividerFull', { defaultValue: 'Run the line the full width' }))}
                {mmRow(t('labels.dividerThickness', { defaultValue: 'Line thickness' }), dividerMm, setDividerMm, { min: 0.05, max: 2, step: 0.05 })}
                {dividerFull && showVertical && (
                  <p className="text-[11px] text-fg-subtle">{t('labels.dividerFullHint', { defaultValue: 'The line stops at the vertical barcode — crossing the bars would stop them scanning.' })}</p>
                )}
              </>
            )}
            {showPrice && toggleRow(priceAccent, setPriceAccent, t('labels.priceAccent', { defaultValue: 'Colour the price' }))}
            {toggleRow(border, setBorder, t('labels.border', { defaultValue: 'Border (cut guide)' }))}

            {/* Accent colour — the number-only strip + optional price. */}
            <div className="flex items-center justify-between gap-2 text-sm">
              <span className="text-fg-muted">{t('labels.accent', { defaultValue: 'Accent colour' })}</span>
              <span className="inline-flex items-center gap-2">
                <input type="color" value={accent} onChange={(e) => setAccent(e.target.value)} aria-label={t('labels.accent', { defaultValue: 'Accent colour' })} className="h-7 w-10 cursor-pointer rounded border border-border bg-bg p-0.5" />
                <button type="button" onClick={() => setAccent(INK)} className="text-xs text-fg-muted underline-offset-4 hover:text-fg hover:underline">{t('labels.accentBlack', { defaultValue: 'Black' })}</button>
              </span>
            </div>

            {/* Per-element alignment. Everything starts on "Auto" = whatever the one
                Text-alignment control above says, so this section only ever needs
                opening when a row wants to disagree with the rest. */}
            {section(t('labels.alignPer', { defaultValue: 'Alignment per element' }), perAlignBadge, (
              <>
                {alignRow(t('labels.showName', { defaultValue: 'Product name' }), nameAlign, setNameAlign)}
                {alignRow(t('labels.showPrice', { defaultValue: 'Price' }), priceAlign, setPriceAlign)}
                {alignRow(t('labels.showCode', { defaultValue: 'SKU / code' }), codeAlign, setCodeAlign)}
                {alignRow(t('labels.extraText', { defaultValue: 'Extra line' }), extraAlign, setExtraAlign)}
                <p className="text-[11px] text-fg-subtle">{t('labels.alignPerHint', { defaultValue: '“Auto” follows the Text alignment above.' })}</p>
              </>
            ))}

            {/* Custom template: the box each element sits in. The designer above the
                preview is the fast way; these are the exact millimetres. */}
            {template === 'custom' && section(t('labels.customLayout', { defaultValue: 'Element boxes' }), elName(selEl), (
              <>
                <p className="text-[11px] text-fg-subtle">{t('labels.designerHint', { defaultValue: 'Drag a box to move it, its corner to resize. Arrow keys nudge; hold Shift for fine steps.' })}</p>
                <div className="flex flex-wrap gap-1">
                  {designEls.map((id) => (
                    <button key={id} type="button" onClick={() => setSelEl(id)}
                      className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${selEl === id ? 'border-transparent bg-brand text-brand-contrast' : 'border-border text-fg-muted hover:text-fg'}`}>
                      {elName(id)}
                    </button>
                  ))}
                </div>
                {designEls.includes(selEl) ? (
                  <>
                    {mmRow(t('labels.boxX', { defaultValue: 'From the left' }), custom[selEl].x, (v) => setBox(selEl, { x: v }), { min: 0, max: Math.max(0, wMm), step: 0.5 })}
                    {mmRow(t('labels.boxY', { defaultValue: 'From the top' }), custom[selEl].y, (v) => setBox(selEl, { y: v }), { min: 0, max: Math.max(0, hMm), step: 0.5 })}
                    {mmRow(t('labels.boxW', { defaultValue: 'Width' }), custom[selEl].w, (v) => setBox(selEl, { w: v }), { min: EL_MIN_MM, max: Math.max(EL_MIN_MM, wMm), step: 0.5 })}
                    {mmRow(t('labels.boxH', { defaultValue: 'Height' }), custom[selEl].h, (v) => setBox(selEl, { h: v }), { min: EL_MIN_MM, max: Math.max(EL_MIN_MM, hMm), step: 0.5 })}
                  </>
                ) : (
                  <p className="text-[11px] text-warning">{t('labels.boxOff', { defaultValue: 'That element isn’t on this label — switch it on above to place it.' })}</p>
                )}
                {/* Reset hands the layout BACK to the auto-fit: the owner has just said
                    they want the default, so resizing the label afterwards should keep
                    re-fitting it rather than freezing this one size's millimetres. */}
                <button type="button" onClick={() => { customFor.current = `${wMm}x${hMm}`; setCustom(defaultLayout(wMm, hMm)); }}
                  className="inline-flex w-fit items-center gap-1 rounded-md border border-border px-2 py-1 text-[11px] font-medium text-fg-muted hover:text-fg">
                  <RefreshCw className="h-3 w-3" aria-hidden />{t('labels.resetLayout', { defaultValue: 'Reset the layout' })}
                </button>
              </>
            ))}

            {/* Spacing — the room around and between everything on the label, plus the
                room between the labels themselves on the sheet. */}
            {section(t('labels.spacing', { defaultValue: 'Spacing' }), spacingBadge, (
              <>
                {/* A custom label has no flow and no padding — every element sits where
                    it was put. Showing these there would be three controls that do
                    nothing, so they are hidden rather than quietly ignored. */}
                {template !== 'custom' ? (
                  <>
                    {mmRow(t('labels.gapMm', { defaultValue: 'Between elements' }), gapMm, setGapMm, { min: 0, max: 8, step: 0.2, auto: true })}
                    {mmRow(t('labels.padX', { defaultValue: 'Padding · left & right' }), padXMm, setPadXMm, { min: 0, max: 15, step: 0.2, auto: true })}
                    {mmRow(t('labels.padY', { defaultValue: 'Padding · top & bottom' }), padYMm, setPadYMm, { min: 0, max: 15, step: 0.2, auto: true })}
                    <p className="text-[11px] text-fg-subtle">{t('labels.spacingHint', { defaultValue: 'Auto keeps this template’s own spacing. Padding is capped so it can never swallow the label.' })}</p>
                  </>
                ) : (
                  <p className="text-[11px] text-fg-subtle">{t('labels.spacingCustom', { defaultValue: 'On a custom label each element carries its own box, so there is no padding or spacing to set.' })}</p>
                )}
                {/* Roll mode is one label per page — there is no cell to space away from. */}
                {mode !== 'roll' && (
                  <>
                    {mmRow(t('labels.cellGapX', { defaultValue: 'Between labels · across' }), cellGapXMm, setCellGapXMm, { min: 0, max: 20, step: 0.5 })}
                    {mmRow(t('labels.cellGapY', { defaultValue: 'Between labels · down' }), cellGapYMm, setCellGapYMm, { min: 0, max: 20, step: 0.5 })}
                    <p className="text-[11px] text-fg-subtle">{t('labels.cellGapHint', { defaultValue: 'The gap between labels on the sheet — 0 butts them together for a guillotine cut.' })}</p>
                  </>
                )}
              </>
            ))}

            {/* Watermark — the store logo (or any text) printed faded behind the label. */}
            {section(t('labels.watermark', { defaultValue: 'Watermark' }), wmBadge, (
              <>
                <div className="inline-flex w-fit rounded-full border border-border p-0.5">
                  {pill(!wmOn, () => setWmOn(false), offLabel)}
                  {/* Choosing the logo as the watermark MOVES it to the background rather
                      than adding a second copy — leaving the inline strip on printed the
                      same logo twice on every label. The inline toggle stays available
                      right above, so anyone who actually wants both can say so. */}
                  {pill(wmOn && wmSource === 'logo', () => { setWmOn(true); setWmSource('logo'); setShowLogo(false); }, t('labels.watermarkLogo', { defaultValue: 'Store logo' }), !logoUrl || logoFailed)}
                  {pill(wmOn && wmSource === 'text', () => { setWmOn(true); setWmSource('text'); }, t('labels.watermarkText', { defaultValue: 'Custom text' }))}
                </div>
                {!logoUrl && <p className="text-[11px] text-fg-subtle">{t('labels.wmNoLogo', { defaultValue: 'No store logo uploaded — use custom text.' })}</p>}
                {/* Say WHY the logo option is dead — printing/exporting still works, it just
                    goes out without the watermark. */}
                {logoUrl && logoFailed && <p className="text-[11px] text-warning">{t('labels.wmLogoFailed', { defaultValue: 'The store logo could not be loaded — re-upload it in the storefront settings, or use custom text.' })}</p>}
                {wmOn && wmSource === 'text' && (
                  <div className="flex items-center gap-2">
                    <Input value={wmText} onChange={(e) => setWmText(e.target.value)} maxLength={40} className="h-8 flex-1 text-xs"
                      placeholder={t('labels.watermarkTextPlaceholder', { defaultValue: 'e.g. SALE — printed faded behind every label' })}
                      aria-label={t('labels.watermarkText', { defaultValue: 'Custom text' })} />
                    <input type="color" value={wmColor} onChange={(e) => setWmColor(e.target.value)} aria-label={t('labels.accent', { defaultValue: 'Accent colour' })} className="h-7 w-10 shrink-0 cursor-pointer rounded border border-border bg-bg p-0.5" />
                  </div>
                )}
                {wmOn && (
                  <>
                    {rangeRow(t('labels.watermarkOpacity', { defaultValue: 'Opacity' }), Math.round(wmOpacity * 100), (v) => setWmOpacity(v / 100), { min: 3, max: 60, step: 1, fmt: (v) => `${v}%` })}
                    {rangeRow(t('labels.watermarkScale', { defaultValue: 'Size' }), wmScale, setWmScale, { min: 10, max: 150, step: 5, fmt: (v) => `${v}%` })}
                    {rangeRow(t('labels.watermarkRotate', { defaultValue: 'Rotation' }), wmRotate, setWmRotate, { min: -90, max: 90, step: 5, fmt: (v) => `${v}°` })}
                    <div className="flex items-center gap-1">
                      {[0, -30, -45, 90].map((deg) => (
                        <button key={deg} type="button" onClick={() => setWmRotate(deg)} className={`rounded border border-border px-1.5 py-0.5 font-mono text-[11px] tabular-nums ${wmRotate === deg ? 'text-fg' : 'text-fg-muted hover:text-fg'}`}>{deg}°</button>
                      ))}
                    </div>
                    <div className="flex items-center justify-between gap-2 text-sm">
                      <span className="text-fg-muted">{t('labels.watermarkPosition', { defaultValue: 'Position' })}</span>
                      <select value={wmPos} onChange={(e) => setWmPos(e.target.value as WmPos)} className="h-8 rounded-md border border-border bg-bg px-2 text-sm">
                        {WM_POS_UI.map((o) => <option key={o.k} value={uiRtl ? o.rtl : o.ltr}>{t(`labels.${o.k}`, { defaultValue: o.d })}</option>)}
                      </select>
                    </div>
                    {toggleRow(wmBehindBarcode, setWmBehindBarcode, t('labels.wmBehindBarcode', { defaultValue: 'Let it show through the barcode' }))}
                    {wmBehindBarcode && <p className="text-[11px] text-warning">{t('labels.wmBehindBarcodeWarn', { defaultValue: 'May stop the barcode scanning.' })}</p>}
                    <p className="text-[11px] text-fg-subtle">{t('labels.watermarkPrintHint', { defaultValue: 'Very low opacity may not come out on every printer.' })}</p>
                  </>
                )}
              </>
            ))}

            {/* The hidden staff code — several price-list prices in ONE short string
                ("xx1000xx2"). Applies to both templates, so it sits above the
                tag-only strip section. */}
            {section(t('labels.hidden', { defaultValue: 'Hidden code' }), hcBadge, (
              <>
                {toggleRow(hcOn, setHcOn, t('labels.hiddenOn', { defaultValue: 'Print a hidden code' }))}
                <p className="text-[11px] text-fg-subtle">{t('labels.hiddenHint', { defaultValue: 'A staff-only code that can carry every price list’s price — you read it off the sticker, the customer can’t.' })}</p>
                {hcOn && (
                  <>
                    {/* dir=ltr on the input as well as the label: what is typed
                        left-to-right is what prints left-to-right. */}
                    <Input
                      ref={hcInputRef}
                      dir="ltr"
                      value={hcTemplate}
                      maxLength={HC_TPL_MAX}
                      onChange={(e) => setHcTemplate(e.target.value)}
                      placeholder={t('labels.hiddenTemplatePlaceholder', { defaultValue: 'e.g. xx1000xx2' })}
                      aria-label={t('labels.hiddenTemplate', { defaultValue: 'Code' })}
                      className="h-8 font-mono text-xs"
                    />
                    <p className="text-[11px] text-fg-subtle">{t('labels.hiddenTemplateHint', { defaultValue: 'Type your own letters between the prices — click a price list below to insert it.' })}</p>
                    {/* One click builds the owner's own example out of their own
                        lists, with no Arabic list name typed by hand. */}
                    {!hcTemplate.trim() && hcIndex.active.length > 0 && (
                      <button type="button" onClick={() => setHcTemplate(hcStarterTemplate())}
                        className="w-fit rounded-md border border-border px-2 py-1 text-[11px] font-medium text-fg-muted hover:text-fg">
                        {t('labels.hiddenStarter', { defaultValue: 'Use my price lists' })}
                      </button>
                    )}
                    <div className="flex flex-wrap items-center gap-1">
                      <span className="me-1 text-[11px] font-medium text-fg-muted">{t('labels.hiddenInsert', { defaultValue: 'Insert' })}</span>
                      {hcIndex.active.map((l) => (
                        <button key={l.id} type="button" onClick={() => hcInsert(hcListToken(l))} className={chipCls} title={t('labels.hiddenLists', { defaultValue: 'Price lists' })}>
                          {l.name}{l.currencyCode ? ` · ${l.currencyCode}` : ''}
                        </button>
                      ))}
                      {HC_TOKEN_CHIPS.map((c) => (
                        <button key={c.tok} type="button" onClick={() => hcInsert(c.tok)} className={chipCls}>
                          {t(`labels.${c.k}`, { defaultValue: c.d })}
                        </button>
                      ))}
                    </div>
                    {/* The live sample — order, formatting and cipher checked at a glance. */}
                    <div className="flex flex-wrap items-baseline gap-2 rounded-md border border-dashed border-border px-2 py-1">
                      <span className="text-[11px] text-fg-muted">{t('labels.hiddenPreviewLabel', { defaultValue: 'Sample code' })}</span>
                      <span dir="ltr" className="font-mono text-xs font-semibold text-fg">{hcSample || '—'}</span>
                      {hcSamplePlain && hcSamplePlain !== hcSample && (
                        <span dir="ltr" className="font-mono text-[11px] text-fg-subtle">= {hcSamplePlain}</span>
                      )}
                    </div>
                    {!pricingOn && <p className="text-[11px] text-warning">{t('labels.hiddenNoPricing', { defaultValue: 'No price lists — turn the pricing module on to put price-list prices in the code.' })}</p>}
                    {pricingOn && hcLists !== null && hcIndex.active.length === 0 && <p className="text-[11px] text-fg-subtle">{t('labels.hiddenNoPricing', { defaultValue: 'No price lists — turn the pricing module on to put price-list prices in the code.' })}</p>}
                    {hcUnknownRefs.length > 0 && <p className="text-[11px] text-warning">{t('labels.hiddenUnknown', { defaultValue: 'No active price list called “{{refs}}” — that slot prints “?”. Re-insert it from the chips above.', refs: hcUnknownRefs.join('”, “') })}</p>}
                    {hcFailedNames.length > 0 && <p className="text-[11px] text-warning">{`${t('labels.hiddenError', { defaultValue: 'Price-list prices could not be loaded — the code will print without them.' })} (${hcFailedNames.join(', ')})`}</p>}
                    {hcLoading && <p className="text-[11px] text-fg-subtle">{t('labels.hiddenLoading', { defaultValue: 'Loading price-list prices…' })}</p>}
                    {pricingOn && hcRefs.length > 0 && (
                      <button type="button" onClick={() => { hcCacheRef.current.clear(); setHcNonce((n) => n + 1); }}
                        className="inline-flex w-fit items-center gap-1 rounded-md border border-border px-2 py-1 text-[11px] font-medium text-fg-muted hover:text-fg">
                        <RefreshCw className="h-3 w-3" aria-hidden />{t('labels.hiddenRefresh', { defaultValue: 'Refresh prices' })}
                      </button>
                    )}
                    <p className="text-[11px] text-fg-subtle">{t('labels.hiddenQtyHint', { defaultValue: 'Prices are the single-unit price (quantity breaks don’t apply), and variants share the product’s list price.' })}</p>

                    {/* The cipher — the classic shop letter substitution. */}
                    {toggleRow(hcCipherOn, setHcCipherOn, t('labels.hiddenCipher', { defaultValue: 'Scramble the numbers' }))}
                    {hcCipherOn && (
                      <>
                        <Input
                          dir="ltr"
                          value={hcKey}
                          maxLength={20}
                          onChange={(e) => setHcKey(e.target.value)}
                          placeholder="MONEYTALKS"
                          aria-label={t('labels.hiddenCipherKey', { defaultValue: 'Code letters' })}
                          className="h-8 font-mono text-xs tracking-[.2em]"
                        />
                        <p className="text-[11px] text-fg-subtle">{t('labels.hiddenCipherHint', { defaultValue: 'Ten different characters, one for each digit 0-9 — with MONEYTRICK, 1000 prints as OMMM.' })}</p>
                        <div className="flex flex-wrap items-center gap-1">
                          {HC_KEY_SUGGESTIONS.map((k) => (
                            <button key={k} type="button" onClick={() => setHcKey(k)} className={chipCls}>{k}</button>
                          ))}
                        </div>
                        {hcKeyChars
                          ? (
                            <div dir="ltr" className="flex flex-wrap gap-x-2 gap-y-0.5 font-mono text-[11px] text-fg-muted">
                              {hcKeyChars.map((c, d) => <span key={`${d}-${c}`}>{d}→{c}</span>)}
                            </div>
                          )
                          : <p className="text-[11px] text-warning">{t('labels.hiddenCipherBad', { defaultValue: 'Enter exactly 10 different characters, one for each digit 0-9.' })}</p>}
                        {hcKeyChars && hcDupes.length > 0 && (
                          <p className="text-[11px] text-warning">{t('labels.hiddenCipherDupe', { defaultValue: 'The same character stands for two digits ({{chars}}) — the code still prints, but you can’t read it back for certain.', chars: hcDupes.join(' ') })}</p>
                        )}
                        <p className="text-[11px] text-fg-subtle">{t('labels.hiddenCipherScope', { defaultValue: 'Only the numbers from the inserted values are replaced — your own letters and marks print as typed.' })}</p>
                      </>
                    )}

                    {/* What a slot prints when the list has no price for the product. */}
                    <div className="flex items-center justify-between gap-2 text-sm">
                      <span className="text-fg-muted">{t('labels.hiddenMissing', { defaultValue: 'Mark for “no price”' })}</span>
                      <Input dir="ltr" value={hcMissing} maxLength={3} onChange={(e) => setHcMissing(e.target.value)}
                        aria-label={t('labels.hiddenMissing', { defaultValue: 'Mark for “no price”' })} className="h-8 w-16 text-center font-mono text-xs" />
                    </div>
                    {toggleRow(hcBaseFallback, setHcBaseFallback, t('labels.hiddenFallback', { defaultValue: 'Use the product’s own price when a list has none' }))}
                    <p className="text-[11px] text-fg-subtle">{t('labels.hiddenFallbackHint', { defaultValue: 'Off, a product that isn’t on the list leaves its slot empty — so a gap always means “not priced on that list”.' })}</p>

                    <div className="flex items-center justify-between gap-2 text-sm">
                      <span className="text-fg-muted">{t('labels.hiddenPos', { defaultValue: 'Position' })}</span>
                      <select value={hcPos} onChange={(e) => setHcPos(e.target.value as HcPos)} className="h-8 max-w-[11rem] rounded-md border border-border bg-bg px-2 text-sm">
                        <option value="top">{t('labels.hiddenPosTop', { defaultValue: 'Top' })}</option>
                        <option value="bottom">{t('labels.hiddenPosBottom', { defaultValue: 'Bottom' })}</option>
                        <option value="corner">{t('labels.hiddenPosCorner', { defaultValue: 'Corner' })}</option>
                      </select>
                    </div>
                    {hcPos === 'corner' && (
                      <>
                        <div className="flex items-center justify-between gap-2 text-sm">
                          <span className="text-fg-muted">{t('labels.hiddenPosCorner', { defaultValue: 'Corner' })}</span>
                          {/* Same start/end → physical mapping as the watermark, minus
                              its centre entry: the canvas has no writing direction. */}
                          <select value={hcCorner} onChange={(e) => setHcCorner(e.target.value as HcCorner)} className="h-8 max-w-[11rem] rounded-md border border-border bg-bg px-2 text-sm">
                            {WM_POS_UI.filter((o) => o.ltr !== 'center').map((o) => <option key={o.k} value={uiRtl ? o.rtl : o.ltr}>{t(`labels.${o.k}`, { defaultValue: o.d })}</option>)}
                          </select>
                        </div>
                        <p className="text-[11px] text-fg-subtle">{t('labels.hiddenCornerHint', { defaultValue: 'A corner code takes no room from the layout, but on a crowded label it can sit over something.' })}</p>
                      </>
                    )}
                    {mmRow(t('labels.hiddenSize', { defaultValue: 'Size' }), hcMm, setHcMm, { min: 1.2, max: 5, step: 0.2 })}
                    <div className="flex items-center justify-between gap-2 text-sm">
                      <span className="text-fg-muted">{t('labels.hiddenColor', { defaultValue: 'Colour' })}</span>
                      <span className="inline-flex items-center gap-2">
                        <input type="color" value={hcColor} onChange={(e) => setHcColor(e.target.value)} aria-label={t('labels.hiddenColor', { defaultValue: 'Colour' })} className="h-7 w-10 cursor-pointer rounded border border-border bg-bg p-0.5" />
                        <button type="button" onClick={() => setHcColor(MUTED)} className="text-xs text-fg-muted underline-offset-4 hover:text-fg hover:underline">{t('labels.hiddenColorMuted', { defaultValue: 'Muted' })}</button>
                      </span>
                    </div>
                  </>
                )}
              </>
            ))}

            {/* The price-tag side strip: a real rotated barcode with its digits. */}
            {template === 'tag' && section(t('labels.vbc', { defaultValue: 'Vertical barcode' }), vbcBadge, (
              <>
                {toggleRow(showVertical, setShowVertical, t('labels.vbcOn', { defaultValue: 'Vertical barcode (scannable)' }))}
                {showVertical && (
                  <>
                    <div className="inline-flex w-fit rounded-full border border-border p-0.5">
                      {pill(vbcMode === 'barcode', () => setVbcMode('barcode'), t('labels.vbcModeBars', { defaultValue: 'Real barcode' }))}
                      {pill(vbcMode === 'number', () => setVbcMode('number'), t('labels.vbcModeNumber', { defaultValue: 'Number only' }))}
                    </div>
                    <div className="flex items-center justify-between gap-2 text-sm">
                      <span className="text-fg-muted">{t('labels.vbcSide', { defaultValue: 'Side' })}</span>
                      <select value={vbcSide} onChange={(e) => setVbcSide(e.target.value as VbcSide)} className="h-8 max-w-[11rem] rounded-md border border-border bg-bg px-2 text-sm">
                        <option value={sideOf('start')}>{t('labels.sideStart', { defaultValue: 'Start' })}</option>
                        <option value={sideOf('end')}>{t('labels.sideEnd', { defaultValue: 'End' })}</option>
                      </select>
                    </div>
                    {/* Ungated: 'Number only' reserves the very same --vbc-w / stripW as the
                        barcode mode does, so hiding this stepper pinned the old-look strip
                        to the 9mm default and squeezed the main column with no way back. */}
                    {mmRow(t('labels.vbcWidth', { defaultValue: 'Strip width' }), vbcWidthMm, setVbcWidthMm, { min: 5, max: 20, step: 0.5 })}
                    {vbcMode === 'barcode' && (
                      <>
                        {toggleRow(vbcDigits, setVbcDigits, t('labels.vbcShowValue', { defaultValue: 'Show the number under the bars' }))}
                        <div className="inline-flex w-fit rounded-full border border-border p-0.5">
                          {pill(vbcRotate === 90, () => setVbcRotate(90), `↓ ${t('labels.vbcDown', { defaultValue: 'Top to bottom' })}`)}
                          {pill(vbcRotate === 270, () => setVbcRotate(270), `↑ ${t('labels.vbcUp', { defaultValue: 'Bottom to top' })}`)}
                        </div>
                        <div className="flex items-center justify-between gap-2 text-sm">
                          <span className="text-fg-muted">{t('labels.vbcColor', { defaultValue: 'Bar colour' })}</span>
                          <span className="inline-flex items-center gap-2">
                            <input type="color" value={vbcColor} onChange={(e) => setVbcColor(e.target.value)} aria-label={t('labels.vbcColor', { defaultValue: 'Bar colour' })} className="h-7 w-10 cursor-pointer rounded border border-border bg-bg p-0.5" />
                            <button type="button" onClick={() => setVbcColor(INK)} className="text-xs text-fg-muted underline-offset-4 hover:text-fg hover:underline">{t('labels.accentBlack', { defaultValue: 'Black' })}</button>
                          </span>
                        </div>
                        {/* How far down the label the code runs — the SCAN axis. Shorter
                            is a real choice on a tall tag, and the warning below reads
                            the resulting millimetres rather than the label height. */}
                        {rangeRow(t('labels.vbcLength', { defaultValue: 'Bar length' }), vbcLenPct, setVbcLenPct, { min: 20, max: 100, step: 5, fmt: (v) => `${v}%` })}
                        {lightOrRed(vbcColor) && <p className="text-[11px] text-warning">{t('labels.vbcColorWarn', { defaultValue: 'Light or coloured bars may not scan — black is safest.' })}</p>}
                        {vbcBarsMm < VBC_MIN_THICK_MM && <p className="text-[11px] text-warning">{t('labels.vbcThinWarn', { defaultValue: 'The bars are under 5mm long — widen the strip or hide the number.' })}</p>}
                        {vbcLenMm < 30 && <p className="text-[11px] text-warning">{t('labels.vbcShortWarn', { defaultValue: 'The code runs {{h}}mm down the label; EAN-13 / UPC need ≈30mm — use CODE128, a taller label, or a longer bar length.', h: Math.round(vbcLenMm) })}</p>}
                      </>
                    )}
                    {(vbcMode === 'number' || vbcDigits) && mmRow(t('labels.showVertical', { defaultValue: 'Barcode number' }), vbcMm, setVbcMm, { min: 1.2, max: 5, step: 0.2 })}
                  </>
                )}
              </>
            ))}

            {/* Font, casing, per-element colours, price affixes + an extra free-text line. */}
            <div className="flex items-center justify-between gap-2 text-sm">
              <span className="text-fg-muted">{t('labels.font', { defaultValue: 'Font' })}</span>
              <select value={fontKey} onChange={(e) => setFontKey(e.target.value as keyof typeof FONTS)} className="h-8 rounded-md border border-border bg-bg px-2 text-sm">
                {Object.keys(FONTS).map((k) => <option key={k} value={k}>{k[0]!.toUpperCase() + k.slice(1)}</option>)}
              </select>
            </div>
            {toggleRow(upper, setUpper, t('labels.upper', { defaultValue: 'UPPERCASE the product name' }))}
            <div className="flex items-center justify-between gap-2 text-sm">
              <span className="text-fg-muted">{t('labels.colors', { defaultValue: 'Colours (name · price · code)' })}</span>
              <span className="inline-flex items-center gap-1.5">
                <input type="color" value={nameColor} onChange={(e) => setNameColor(e.target.value)} aria-label={t('labels.showName', { defaultValue: 'Product name' })} className="h-7 w-8 cursor-pointer rounded border border-border bg-bg p-0.5" />
                <input type="color" value={priceColor} onChange={(e) => setPriceColor(e.target.value)} aria-label={t('labels.showPrice', { defaultValue: 'Price' })} className="h-7 w-8 cursor-pointer rounded border border-border bg-bg p-0.5" />
                <input type="color" value={codeColor} onChange={(e) => setCodeColor(e.target.value)} aria-label={t('labels.showCode', { defaultValue: 'SKU / code' })} className="h-7 w-8 cursor-pointer rounded border border-border bg-bg p-0.5" />
              </span>
            </div>
            {showPrice && (
              <div className="flex items-center gap-2 text-sm">
                <span className="whitespace-nowrap text-fg-muted">{t('labels.priceAffix', { defaultValue: 'Price ± text' })}</span>
                <Input value={pricePrefix} onChange={(e) => setPricePrefix(e.target.value)} placeholder={t('labels.prefix', { defaultValue: 'before' })} className="h-8 flex-1 text-xs" />
                <Input value={priceSuffix} onChange={(e) => setPriceSuffix(e.target.value)} placeholder={t('labels.suffix', { defaultValue: 'after' })} className="h-8 flex-1 text-xs" />
              </div>
            )}
            <div className="flex items-center gap-2 text-sm">
              <span className="whitespace-nowrap text-fg-muted">{t('labels.extraText', { defaultValue: 'Extra line' })}</span>
              <Input value={extraText} onChange={(e) => setExtraText(e.target.value)} placeholder={t('labels.extraPlaceholder', { defaultValue: 'tagline / note (all labels)' })} className="h-8 flex-1 text-xs" />
            </div>

            <h2 className="mt-2 text-sm font-semibold uppercase tracking-wide text-fg-muted">{t('labels.textSizes', { defaultValue: 'Text sizes' })}</h2>
            <div className="flex items-center justify-between gap-2 text-sm">
              <span className="font-medium">{t('labels.allText', { defaultValue: 'All text' })}</span>
              <span className="inline-flex items-center gap-1">
                <button type="button" aria-label={tc('actions.decrease', { defaultValue: 'Decrease' })} onClick={() => scaleAllText(1 / 1.1)} className="grid h-6 w-6 place-items-center rounded border border-border text-fg-muted hover:text-fg"><Minus className="h-3.5 w-3.5" aria-hidden /></button>
                <span className="w-16 text-center text-xs text-fg-muted">±10%</span>
                <button type="button" aria-label={tc('actions.increase', { defaultValue: 'Increase' })} onClick={() => scaleAllText(1.1)} className="grid h-6 w-6 place-items-center rounded border border-border text-fg-muted hover:text-fg"><Plus className="h-3.5 w-3.5" aria-hidden /></button>
              </span>
            </div>
            {showLogo && logoUrl && !logoFailed && sizeRow(t('labels.showLogo', { defaultValue: 'Store logo' }), logoMm, setLogoMm)}
            {showName && sizeRow(t('labels.showName', { defaultValue: 'Product name' }), nameMm, setNameMm)}
            {/* The scannable barcode's own box. Auto height = fill whatever the rest of
                the label leaves over, which is what it has always done. */}
            {/* In custom mode the BOX is the barcode's size, so these two would fight it. */}
            {showBarcode && template !== 'custom' && mmRow(t('labels.bcHeight', { defaultValue: 'Barcode height' }), bcMm, setBcMm, { min: 1, max: 60, step: 0.5, auto: true })}
            {showBarcode && template !== 'custom' && rangeRow(t('labels.bcWidth', { defaultValue: 'Barcode width' }), bcWidthPct, setBcWidthPct, { min: 20, max: 100, step: 5, fmt: (v) => `${v}%` })}
            {/* Which way the bars run. Custom only, because it is only here that the
                barcode has a box of its own to fill either way — the flow templates
                stack it in a row of blocks, where "on its end" has no height to mean. */}
            {showBarcode && template === 'custom' && (
              <div className="flex items-center justify-between gap-2 text-sm">
                <span className="text-fg-muted">{t('labels.bcDirection', { defaultValue: 'Barcode direction' })}</span>
                <span className="inline-flex overflow-hidden rounded-md border border-border">
                  <button
                    type="button"
                    aria-pressed={!bcVertical}
                    onClick={() => setBcVertical(false)}
                    className={`px-2.5 py-1 text-xs ${!bcVertical ? 'bg-brand text-white' : 'bg-bg text-fg-muted'}`}
                  >
                    {t('labels.bcHorizontal', { defaultValue: 'Across' })}
                  </button>
                  <button
                    type="button"
                    aria-pressed={bcVertical}
                    onClick={() => setBcVertical(true)}
                    className={`px-2.5 py-1 text-xs ${bcVertical ? 'bg-brand text-white' : 'bg-bg text-fg-muted'}`}
                  >
                    {t('labels.bcVertical', { defaultValue: 'Standing' })}
                  </button>
                </span>
              </div>
            )}
            {showPrice && sizeRow(t('labels.showPrice', { defaultValue: 'Price' }), priceMm, setPriceMm)}
            {showCode && sizeRow(t('labels.showCode', { defaultValue: 'SKU / code' }), codeMm, setCodeMm)}
            {extraText.trim() && sizeRow(t('labels.extraText', { defaultValue: 'Extra line' }), extraMm, setExtraMm)}
          </Card>

          <Card className="flex flex-col gap-2.5 p-4">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-fg-muted">{t('labels.presets', { defaultValue: 'Saved designs' })}</h2>
            <div className="flex items-center gap-2">
              <Input value={presetName} onChange={(e) => setPresetName(e.target.value)} placeholder={t('labels.presetName', { defaultValue: 'Name this design…' })} className="h-8 flex-1 text-sm" onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); savePreset(); } }} />
              <Button size="sm" variant="secondary" disabled={!presetName.trim()} onClick={savePreset}>{tc('actions.save', { defaultValue: 'Save' })}</Button>
            </div>
            {Object.keys(presets).length === 0 ? (
              <p className="text-xs text-fg-muted">{t('labels.presetsEmpty', { defaultValue: 'Save the current look as a design you can reuse in one click.' })}</p>
            ) : (
              <ul className="flex flex-col gap-1">
                {Object.keys(presets).map((n) => (
                  <li key={n} className="flex items-center gap-2 rounded-md border border-border bg-bg-subtle px-2.5 py-1 text-sm">
                    <button type="button" onClick={() => applyConfig(presets[n]!)} className="flex-1 truncate text-start font-medium hover:text-brand">{n}</button>
                    <button type="button" onClick={() => deletePreset(n)} aria-label={tc('actions.remove', { defaultValue: 'Remove' })} className="text-fg-muted hover:text-danger"><Trash2 className="h-3.5 w-3.5" aria-hidden /></button>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>

        {/* Preview stays pinned while the (taller) options column scrolls past it. */}
        <Card className="p-4 lg:sticky lg:top-4 lg:self-start">
          <div className="mb-3 flex items-center justify-between gap-2">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-fg-muted">{t('labels.preview', { defaultValue: 'Preview' })}</h2>
            <div className="flex items-center gap-3">
              {/* Preview zoom — display only; the printed size never changes. */}
              <span className="inline-flex items-center gap-1">
                <button type="button" aria-label={tc('actions.decrease', { defaultValue: 'Decrease' })} onClick={() => setZoom((z) => Math.max(0.5, Math.round((z - 0.25) * 100) / 100))} className="grid h-6 w-6 place-items-center rounded border border-border text-fg-muted hover:text-fg"><Minus className="h-3.5 w-3.5" aria-hidden /></button>
                <button type="button" onClick={() => setZoom(1)} title={t('labels.zoomReset', { defaultValue: 'Reset zoom' })} className="w-12 text-center font-mono text-xs tabular-nums text-fg-muted hover:text-fg">{Math.round(zoom * 100)}%</button>
                <button type="button" aria-label={tc('actions.increase', { defaultValue: 'Increase' })} onClick={() => setZoom((z) => Math.min(3, Math.round((z + 0.25) * 100) / 100))} className="grid h-6 w-6 place-items-center rounded border border-border text-fg-muted hover:text-fg"><Plus className="h-3.5 w-3.5" aria-hidden /></button>
              </span>
              <span className="text-xs text-fg-muted">{totalLabels > PREVIEW_CAP ? t('labels.previewCap', { defaultValue: 'Showing {{n}} of {{total}}', n: PREVIEW_CAP, total: totalLabels }) : t('labels.previewCount', { defaultValue: '{{total}} label(s)', total: totalLabels })}</span>
            </div>
          </div>
          {totalLabels === 0 ? (
            <div className="grid min-h-64 place-items-center rounded-lg border border-dashed border-border text-sm text-fg-muted">{t('labels.empty', { defaultValue: 'Search and add products, or add a whole category / brand.' })}</div>
          ) : (
            <div className="overflow-auto rounded-lg bg-[#eceef2] p-4 lg:max-h-[calc(var(--vh100)-13rem)]">
              <style>{SHEET_CSS}</style>
              {/* ── The designer. One real label, blown up, with a transparent handle
                  over each element. It renders the SAME buildSheetHtml output the
                  preview and the print pop-up use — the handles are the only thing
                  drawn here, so this can never become a fourth renderer to keep in
                  step with the other three. ── */}
              {template === 'custom' && (
                <div className="mb-4 rounded-lg border border-border bg-white p-3 shadow">
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <span className="text-xs font-semibold uppercase tracking-wide text-fg-muted">{t('labels.designer', { defaultValue: 'Design' })}</span>
                    <span className="text-[11px] text-fg-subtle">{t('labels.designerHint', { defaultValue: 'Drag a box to move it, its corner to resize. Arrow keys nudge; hold Shift for fine steps.' })}</span>
                  </div>
                  {/* dir=ltr because this is a physical-millimetre canvas, the same rule
                      .lp-tag follows: under an Arabic shell the mm-sized inner div was
                      right-aligned inside its pre-sized parent and then scaled further
                      right from transformOrigin top-left, leaving ~18% of the label on
                      screen and NO scrollable overflow to reach the rest. */}
                  <div ref={designWrapRef} dir="ltr" className="overflow-auto">
                    {/* The scaled label needs a parent of its POST-scale size, or the
                        surrounding layout still reserves the 1× box and everything below
                        overlaps it (transform never affects layout). Auto margins centre
                        it the way the preview sheet below is centred — an over-constrained
                        block box otherwise resolves the leftover width into one margin and
                        the designer sat hard against the panel edge. */}
                    <div style={{ width: wMm * PX_PER_MM * designScale, height: hMm * PX_PER_MM * designScale, margin: '0 auto' }}>
                      <div className="relative" style={{ width: `${wMm}mm`, height: `${hMm}mm`, transform: `scale(${designScale})`, transformOrigin: 'top left' }}>
                        {/* pointer-events:none is THE fix for "I can't move them".
                            SHEET_CSS gives every .lp-abs z-index 1 while .lp-label is only
                            position:relative (z-index auto ⇒ no stacking context), so the
                            injected boxes paint in the SAME stacking context as the handles
                            and above them — and each one covers its handle exactly, since
                            both are the same clampBox rect. Every pointerdown landed on the
                            product name instead of the handle, so no drag could ever start;
                            without preventDefault running, it just selected the text.
                            user-select:none stops that highlight for good measure. */}
                        <div
                          style={{ pointerEvents: 'none', userSelect: 'none' }}
                          dangerouslySetInnerHTML={{ __html: designHtml }}
                        />
                        {/* The handle layer is offset by the cut border because .lp-abs is
                            positioned from .lp-label's PADDING box (inside the 0.2mm
                            border) while these are children of the unbordered scaled div —
                            which is exactly the offset labelCanvas calls `bo`. Left
                            uncorrected the handles sat 0.2mm up and left of the ink. */}
                        <div
                          className="absolute"
                          style={{ left: `${designBo}mm`, top: `${designBo}mm`, right: 0, bottom: 0, zIndex: 10 }}
                        >
                          {/* The alignment lines, drawn only while a box is being dragged.
                              pointer-events:none so a line can never eat the drag that is
                              drawing it, and hairline-thin through the same /designScale
                              divisor the handles use. */}
                          {guides.map((g) => (
                            <div
                              key={`${g.axis}${g.at}`}
                              style={{
                                position: 'absolute', pointerEvents: 'none', background: 'var(--color-accent, #f59e0b)',
                                ...(g.axis === 'x'
                                  ? { left: `${g.at}mm`, top: 0, bottom: 0, width: 1 / designScale }
                                  : { top: `${g.at}mm`, left: 0, right: 0, height: 1 / designScale }),
                              }}
                            />
                          ))}
                          {designEls.map((id) => {
                            const b = clampBox(custom[id], wMm, hMm);
                            const on = selEl === id;
                            // Widths are divided by the scale so the outline and the grips
                            // stay hairline-thin on screen: the transform magnifies them
                            // too, and at 10× a 1px border swallows a 2mm box.
                            const px = (n: number): number => n / designScale;
                            const grip = (extra: React.CSSProperties): React.CSSProperties => ({
                              width: px(9), height: px(9), background: 'var(--color-brand, #6366f1)', ...extra,
                            });
                            /** An edge strip: invisible, just a grab zone, scaled like the corners. */
                            const edge = (extra: React.CSSProperties): React.CSSProperties => ({ background: 'transparent', ...extra });
                            return (
                              <Rnd
                                key={id}
                                // react-rnd divides every pointer delta by this before it
                                // reaches us, which is the whole reason it was chosen: the
                                // designer lives under transform: scale(1..14) and the old
                                // hand-rolled maths had to undo that itself.
                                scale={designScale}
                                position={{ x: b.x * PX_PER_MM, y: b.y * PX_PER_MM }}
                                size={{ width: b.w * PX_PER_MM, height: b.h * PX_PER_MM }}
                                minWidth={EL_MIN_MM * PX_PER_MM}
                                minHeight={EL_MIN_MM * PX_PER_MM}
                                enableResizing={on}
                                // EVERY handle is re-sized through px(), including the four
                                // edges: react-rnd's defaults are a flat 10px, and at 14×
                                // that is a 140-screen-pixel grab strip hanging 70px outside
                                // the box, swallowing the drags of whatever sits next to it.
                                resizeHandleStyles={{
                                  bottomRight: grip({ right: px(-4), bottom: px(-4) }),
                                  bottomLeft: grip({ left: px(-4), bottom: px(-4) }),
                                  topRight: grip({ right: px(-4), top: px(-4) }),
                                  topLeft: grip({ left: px(-4), top: px(-4) }),
                                  top: edge({ top: px(-3), left: 0, width: '100%', height: px(6) }),
                                  bottom: edge({ bottom: px(-3), left: 0, width: '100%', height: px(6) }),
                                  left: edge({ left: px(-3), top: 0, height: '100%', width: px(6) }),
                                  right: edge({ right: px(-3), top: 0, height: '100%', width: px(6) }),
                                }}
                                // Snap on RELEASE only. `position` is controlled, so a value
                                // snapped mid-gesture is handed straight back to
                                // react-draggable as its new origin and the rounding
                                // compounds every frame: a 3mm drag off a 21.3mm box walked
                                // 21.0 → 20.5 → … → 17.5 instead of landing on 18.5. During
                                // the drag we store exactly what the cursor says (still
                                // clamped, so the edge still pins) and quantise once at the end.
                                onDragStart={() => { setSelEl(id); dragOffRef.current = { x: 0, y: 0 }; }}
                                onDrag={(e, d) => commitMove(id, d.x, d.y, isFine(e), false, isFree(e))}
                                onDragStop={(e, d) => commitMove(id, d.x, d.y, isFine(e), true, isFree(e))}
                                onResizeStart={() => setSelEl(id)}
                                onResize={(e, _dir, ref, _delta, pos) => commitResize(id, ref, pos, isFine(e), false)}
                                onResizeStop={(e, _dir, ref, _delta, pos) => commitResize(id, ref, pos, isFine(e), true)}
                                style={{
                                  border: `${px(on ? 2 : 1)}px ${on ? 'solid' : 'dashed'} var(--color-brand, #6366f1)`,
                                  background: on ? 'color-mix(in srgb, var(--color-brand, #6366f1) 12%, transparent)' : 'transparent',
                                  cursor: 'move',
                                  // react-draggable does not set this for you, and without it
                                  // the surrounding overflow-auto panel steals every touch drag.
                                  touchAction: 'none',
                                  outline: 'none',
                                }}
                                role="button"
                                tabIndex={0}
                                aria-label={elName(id)}
                                title={elName(id)}
                                onKeyDown={(e: React.KeyboardEvent) => onElKey(e, id)}
                                onFocus={() => setSelEl(id)}
                              />
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div style={{ transform: `scale(${zoom})`, transformOrigin: 'top center', width: zoom < 1 ? undefined : 'fit-content', margin: '0 auto' }}>
                {mode === 'sheet' ? (
                  <div className="mx-auto bg-white shadow" style={{ width: '210mm', padding: '6mm' }} dangerouslySetInnerHTML={{ __html: previewHtml }} />
                ) : mode === 'custom' ? (
                  <div className="mx-auto bg-white shadow" style={{ width: `${sheetW}mm`, minHeight: `${sheetH}mm`, padding: `${sheetMargin}mm` }} dangerouslySetInnerHTML={{ __html: previewHtml }} />
                ) : (
                  <div className="mx-auto w-fit bg-white p-3 shadow" dangerouslySetInnerHTML={{ __html: previewHtml }} />
                )}
              </div>
            </div>
          )}
        </Card>
      </div>

      {!canWrite && <p className="text-xs text-fg-muted">{t('labels.readonlyNote', { defaultValue: 'Note: you have read-only access; you can still print labels.' })}</p>}
    </div>
  );
}
